import React, { useState, useEffect, useCallback } from 'react';
import ItemDetailModal from './ItemDetailModal';
import ManualDepositModal from './ManualDepositModal';
import WithdrawalModal from './WithdrawalModal';
import { hatsAPI, tradesAPI, balanceAPI, disputesAPI, reviewsAPI, profileAPI, usersAPI, paymentsAPI, authAPI, adminAPI, shippingAPI } from '../services/api';
import PasswordChangeModal from './PasswordChangeModal';
import TwoFactorModal from './TwoFactorModal';
import ImageLightbox from './ImageLightbox';

const DashboardModal = ({ isOpen, onClose, onLogout, currentUser, initialActiveTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  
  // Date utility functions for trade date validation
  const getNowET = () => {
    const now = new Date();
    const etOffset = -5; // Eastern Time is UTC-5
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    return new Date(utc + (etOffset * 3600000));
  };
  
  const computeFirstAvailableET = useCallback(() => {
    const d = getNowET();
    d.setDate(d.getDate() + 2); // 2 days minimum
    return d;
  }, []);
  
  const computeMaxEndDate = useCallback((startDate) => {
    if (!startDate) return null;
    const start = new Date(startDate);
    const maxEnd = new Date(start);
    maxEnd.setMonth(maxEnd.getMonth() + 1); // 1 month maximum
    return maxEnd;
  }, []);
  
  const ymd = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  
  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return { isValid: true, error: '' };
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (end <= start) {
      return { isValid: false, error: 'End date must be after start date' };
    }
    
    const diffTime = end - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 3) {
      return { isValid: false, error: 'Trade duration must be at least 3 days' };
    }
    
    if (diffDays > 31) {
      return { isValid: false, error: 'Trade duration cannot exceed 1 month (31 days)' };
    }
    
    return { isValid: true, error: '' };
  };
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  
  // Data states
  const [userBalance, setUserBalance] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(false);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [loadingMoreTransactions, setLoadingMoreTransactions] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');

  const [showManualDeposit, setShowManualDeposit] = useState(false);
  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [myHats, setMyHats] = useState([]);
  const [pendingTrades, setPendingTrades] = useState([]); // Pending trades (awaiting acceptance)
  const [activeTrades, setActiveTrades] = useState([]); // Active/accepted trades
  
  // Debug: Monitor activeTrades changes
  useEffect(() => {
  }, [activeTrades]);
  
  const [tradeHistory, setTradeHistory] = useState([]);
  const [myDisputes, setMyDisputes] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [receivedReviews, setReceivedReviews] = useState([]);
  const [manualDeposits, setManualDeposits] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [userMessages, setUserMessages] = useState([]); // User-to-admin conversation thread (+ merged legacy admin notices)
  const [newUserMessage, setNewUserMessage] = useState(''); // Input for sending message to admin
  
  // UI state for item detail and modify modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [modifyTrade, setModifyTrade] = useState(null);
  const [modifyMode, setModifyMode] = useState('date'); // 'date' or 'item'
  const [modifyDates, setModifyDates] = useState({ start_date: '', end_date: '' });
  const [dateValidationError, setDateValidationError] = useState('');
  const [closetItems, setClosetItems] = useState([]);
  
  // Sync embedded messages to tradeMessages state when trades are loaded
  useEffect(() => {
    const syncMessages = () => {
      const allTrades = [...pendingTrades, ...activeTrades, ...tradeHistory];
      const newTradeMessages = {};
      
      allTrades.forEach(trade => {
        if (trade.messages && trade.messages.length > 0) {
          // Convert embedded messages to the format expected by tradeMessages state
          newTradeMessages[trade._id] = trade.messages.map(msg => ({
            id: String(msg.user_id) || '',
            user_name: msg.user_name || 'Unknown',
            message: msg.text || '',
            timestamp: msg.created_at || new Date().toISOString()
          }));
        }
      });
      
      // Only update if there are messages to sync
      if (Object.keys(newTradeMessages).length > 0) {
        setTradeMessages(prev => ({ ...prev, ...newTradeMessages }));
      }
    };
    
    syncMessages();
  }, [pendingTrades, activeTrades, tradeHistory]);
  
  // Date change handlers with validation
  const handleStartDateChange = (newStartDate) => {
    setModifyDates(prev => ({ ...prev, start_date: newStartDate }));
    
    // Clear validation error when user changes dates
    setDateValidationError('');
    
    // If end date exists, validate the range
    if (modifyDates.end_date) {
      const validation = validateDateRange(newStartDate, modifyDates.end_date);
      if (!validation.isValid) {
        setDateValidationError(validation.error);
      }
    }
  };
  
  const handleEndDateChange = (newEndDate) => {
    setModifyDates(prev => ({ ...prev, end_date: newEndDate }));
    
    // Clear validation error when user changes dates
    setDateValidationError('');
    
    // If start date exists, validate the range
    if (modifyDates.start_date) {
      const validation = validateDateRange(modifyDates.start_date, newEndDate);
      if (!validation.isValid) {
        setDateValidationError(validation.error);
      }
    }
  };
  const [closetLoading, setClosetLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  
  // Trade messaging state
  const [tradeMessages, setTradeMessages] = useState({});
  const [newMessages, setNewMessages] = useState({});
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Item detail popup for modify screen
  const [showItemDetailPopup, setShowItemDetailPopup] = useState(false);
  const [selectedItemForDetail, setSelectedItemForDetail] = useState(null);
  
  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Full closet modal state
  const [showFullClosetModal, setShowFullClosetModal] = useState(false);
  const [fullClosetItems, setFullClosetItems] = useState([]);
  const [fullClosetLoading, setFullClosetLoading] = useState(false);
  // Edit item modal state
  const [editHat, setEditHat] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    value: '',
    condition: '',
    brand: ''
  });

  // Photo management modal state
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [currentPhotos, setCurrentPhotos] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [newPhotos, setNewPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  
  // Trade history sorting state
  const [tradeHistoryFilter, setTradeHistoryFilter] = useState('all');
  
  // Profile tab state
  const [profileFormData, setProfileFormData] = useState({
    name: '',
    closet_name: '',
    phone: '',
    street: '',
    apartment: '',
    city: '',
    state: '',
    zip: '',
    created_at: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  
  // Photo gallery state
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Photo gallery handlers
  const openPhotoGallery = (images, startIndex = 0) => {
    if (images && images.length > 0) {
      setGalleryImages(images);
      setCurrentImageIndex(startIndex);
      setPhotoGalleryOpen(true);
    }
  };
  
  const closePhotoGallery = () => {
    setPhotoGalleryOpen(false);
    setGalleryImages([]);
    setCurrentImageIndex(0);
  };
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  // Check if an item is part of any active trades
  const isItemInActiveTrade = (hatId) => {
    const allTrades = [...pendingTrades, ...activeTrades];
    return allTrades.some(trade => 
      trade.offered_hat?._id === hatId || 
      trade.requested_hat?._id === hatId ||
      trade.offered_item?._id === hatId || 
      trade.requested_item?._id === hatId
    );
  };

  // Filter trade history based on selected status
  const getFilteredTradeHistory = () => {
    if (!tradeHistory || tradeHistory.length === 0) return [];
    
    if (tradeHistoryFilter === 'all') {
      return tradeHistory;
    }
    
    return tradeHistory.filter(trade => trade.status === tradeHistoryFilter);
  };

  // Format date/time for Eastern Time
  const formatDateTimeET = (dateString) => {
    try {
      const date = new Date(dateString);
      // Subtract 5 hours to convert from UTC to Eastern Time (adjusted for DST)
      const easternTime = new Date(date.getTime() - (5 * 60 * 60 * 1000));
      return easternTime.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch { return ''; }
  };

  // Smart timestamp formatter that automatically detects timezone source
  const formatSmartTimestamp = (dateString) => {
    try {
      const timestamp = new Date(dateString);
      const now = new Date();
      const timeDiff = Math.abs(now.getTime() - timestamp.getTime());
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      // If timestamp is within 2 hours of current time, assume it's already in Eastern Time
      if (hoursDiff <= 2) {
        return timestamp.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      } else {
        // If timestamp is more than 2 hours off, assume it's UTC and convert to Eastern Time (adjusted for DST)
        const easternTime = new Date(timestamp.getTime() - (5 * 60 * 60 * 1000));
        return easternTime.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      }
    } catch (error) {
      console.error('Error formatting smart timestamp:', error);
      return dateString;
    }
  };
  
  // Trade details modal state
  const [tradeDetailsOpen, setTradeDetailsOpen] = useState(false);
  const [tradeDetails, setTradeDetails] = useState(null);
  const [acknowledgingReceived, setAcknowledgingReceived] = useState(false);
  const [acknowledgingReturned, setAcknowledgingReturned] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingTrade, setRatingTrade] = useState(null);
  const [ratingData, setRatingData] = useState({ rating: 5, comment: '' });
  
  // Enhanced tracking state
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);

  // Define tabs with icons
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'fas fa-home' },
    { id: 'hats', label: 'My Bags', icon: 'fas fa-shopping-bag' },
    { id: 'upload', label: 'Upload Item', icon: 'fas fa-plus' },
    { id: 'trades', label: 'Trades', icon: 'fas fa-exchange-alt' },
    { id: 'active-trades', label: 'Active', icon: 'fas fa-play' },
    { id: 'trade-history', label: 'Trade History', icon: 'fas fa-history' },
    { id: 'disputes', label: 'Disputes', icon: 'fas fa-exclamation-triangle' },
    { id: 'reviews', label: 'Reviews', icon: 'fas fa-star' },
    { id: 'messages', label: 'Messages', icon: 'fas fa-envelope' },
    { id: 'balance', label: 'Balance', icon: 'fas fa-wallet' },
    { id: 'profile', label: 'Profile', icon: 'fas fa-user' },
    { id: 'how-to', label: 'How To', icon: 'fas fa-video' }
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const sendUserMessage = async () => {
    if (!newUserMessage.trim()) {
      setError('Please enter a message');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await hatsAPI.sendUserMessage(newUserMessage.trim());
      setNewUserMessage('');
      setSuccess('Message sent successfully!');
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to send message:', error);
      setError(error.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced tracking function
  const openTrackingModal = async (tradeId) => {
    try {
      setLoadingTracking(true);
      setTrackingModalOpen(true);
      
      const response = await shippingAPI.getTradeTracking(tradeId);
      if (response.success) {
        setTrackingData(response.tracking);
      } else {
        setTrackingData(null);
      }
    } catch (error) {
      console.error('Failed to load tracking data:', error);
      setTrackingData(null);
    } finally {
      setLoadingTracking(false);
    }
  };

  const handleAcknowledgeReceived = async (tradeId) => {
    try {
      setAcknowledgingReceived(true);
      await tradesAPI.acknowledgeReceived(tradeId);
      
      // Set confirmation details for modal
      const now = new Date();
      setConfirmationDetails({
        tradeId: tradeId,
        timestamp: now.toLocaleString(),
        tradeRef: String(tradeId).slice(-6),
        itemName: tradeDetails?.trade?.requested_hat?.name || 'Item'
      });
      setShowConfirmationModal(true);
      
      // Update tradeDetails immediately to reflect the acknowledgment
      if (tradeDetails && tradeDetails.trade) {
        const me = (currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id);
        const isUser1 = tradeDetails.trade.user1_id === me;
        const updatedTrade = {
          ...tradeDetails.trade,
          [isUser1 ? 'user1_received_at' : 'user2_received_at']: now.toISOString()
        };
        setTradeDetails({
          ...tradeDetails,
          trade: updatedTrade
        });
      }
      
      setSuccess('Item received acknowledged successfully!');
      // Refresh trade data
      loadDashboardData();
    } catch (error) {
      setError(error.message || 'Failed to acknowledge receipt');
    } finally {
      setAcknowledgingReceived(false);
    }
  };

  const handleAcknowledgeReturned = async (tradeId) => {
    try {
      setAcknowledgingReturned(true);
      await tradesAPI.acknowledgeReturned(tradeId);
      
      // Set confirmation details for modal
      const now = new Date();
      setConfirmationDetails({
        tradeId: tradeId,
        timestamp: now.toLocaleString(),
        tradeRef: String(tradeId).slice(-6),
        itemName: tradeDetails?.trade?.offered_hat?.name || 'Item'
      });
      setShowConfirmationModal(true);
      
      // Update tradeDetails immediately to reflect the acknowledgment
      if (tradeDetails && tradeDetails.trade) {
        const me = (currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id);
        const isUser1 = tradeDetails.trade.user1_id === me;
        const updatedTrade = {
          ...tradeDetails.trade,
          [isUser1 ? 'user1_returned_at' : 'user2_returned_at']: now.toISOString()
        };
        setTradeDetails({
          ...tradeDetails,
          trade: updatedTrade
        });
      }
      
      setSuccess('Item returned acknowledged successfully!');
      // Refresh trade data
      loadDashboardData();
    } catch (error) {
      setError(error.message || 'Failed to acknowledge return');
    } finally {
      setAcknowledgingReturned(false);
    }
  };

  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [selectedTradeForDispute, setSelectedTradeForDispute] = useState(null);
  const [disputeForm, setDisputeForm] = useState({
    reason: '',
    description: '',
    evidence: []
  });
  const [disputeSubmitted, setDisputeSubmitted] = useState(false);
  
  // Dispute messaging state
  const [showDisputeMessagingModal, setShowDisputeMessagingModal] = useState(false);
  const [showDisputeHistoryModal, setShowDisputeHistoryModal] = useState(false);
  const [selectedDisputeForMessaging, setSelectedDisputeForMessaging] = useState(null);
  const [, setSelectedDisputeForHistory] = useState(null);
  const [disputeMessages, setDisputeMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showClosedDisputes, setShowClosedDisputes] = useState(false);

  const handleCreateDispute = async (tradeId) => {
    console.log('Creating dispute for trade ID:', tradeId);
    setSelectedTradeForDispute(String(tradeId));
    setDisputeSubmitted(false); // Reset submitted state for new dispute
    setShowDisputeModal(true);
    // Close the Trade Details modal to show the dispute modal
    setTradeDetailsOpen(false);
  };

  const handleSubmitDispute = async () => {
    try {
      setIsLoading(true);
      
      if (!selectedTradeForDispute) {
        setError('No trade selected for dispute');
        return;
      }
      
      if (!disputeForm.reason) {
        setError('Please select a reason for the dispute');
        return;
      }
      
      if (!disputeForm.description || disputeForm.description.trim() === '') {
        setError('Please provide a description of the issue');
        return;
      }
      
      const disputeData = {
        trade_id: selectedTradeForDispute,
        reason: disputeForm.reason,
        description: disputeForm.description.trim(),
        evidence: disputeForm.evidence
      };
      
      console.log('Submitting dispute with data:', disputeData);
      console.log('Selected trade for dispute:', selectedTradeForDispute);
      
      await disputesAPI.create(disputeData);
      setSuccess('Dispute created successfully! Our team will review it within 24-48 hours.');
      setDisputeSubmitted(true);
      
      // Refresh disputes data to enable communication buttons
      try {
        const disputes = await disputesAPI.getMyDisputes();
        setMyDisputes(disputes);
      } catch (error) {
        console.error('Failed to refresh disputes:', error);
      }
      
      // Don't close the modal immediately - let users use communication features
      // setShowDisputeModal(false);
      setDisputeForm({ reason: '', description: '', evidence: [] });
      // Keep selectedTradeForDispute so communication buttons work
      // setSelectedTradeForDispute(null);
      loadDashboardData();
    } catch (error) {
      console.error('Error creating dispute:', error);
      if (error.message && error.message.includes('already exists')) {
        setError('A dispute has already been created for this trade');
      } else {
        setError(error.message || 'Failed to create dispute');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Dispute messaging functions
  const loadDisputeMessages = async (disputeId) => {
    try {
      const response = await disputesAPI.getDisputeMessages(disputeId);
      setDisputeMessages(response.messages || []);
    } catch (error) {
      console.error('Error loading dispute messages:', error);
      setError('Failed to load messages');
      setDisputeMessages([]);
    }
  };

  const sendDisputeMessage = async () => {
    if (!newMessage.trim() || !selectedDisputeForMessaging) return;
    
    try {
      setIsLoading(true);
      await disputesAPI.sendDisputeMessage(selectedDisputeForMessaging, newMessage.trim());
      setNewMessage('');
      await loadDisputeMessages(selectedDisputeForMessaging);
      setSuccess('Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRatingModal = async (tradeId) => {
    try {
      const canReview = await reviewsAPI.canReviewTrade(tradeId);
      if (canReview.can_review) {
        setRatingTrade({ id: tradeId, reviewed_user_id: canReview.reviewed_user_id });
        setRatingData({ rating: 5, comment: '' });
        setShowRatingModal(true);
      } else {
        setError(canReview.reason || 'Cannot review this trade');
      }
    } catch (error) {
      setError(error.message || 'Failed to check review eligibility');
    }
  };

  const handleSubmitRating = async () => {
    try {
      setIsLoading(true);
      console.log('Submitting rating for trade:', ratingTrade.id);
      const reviewData = {
        trade_id: ratingTrade.id,
        rating: ratingData.rating,
        comment: ratingData.comment
      };
      
      console.log('Review data:', reviewData);
      const result = await reviewsAPI.create(reviewData);
      console.log('Review submission result:', result);
      
      setSuccess('Review submitted successfully!');
      setShowRatingModal(false);
      setRatingTrade(null);
      setRatingData({ rating: 5, comment: '' });
      loadDashboardData();
    } catch (error) {
      console.error('Error submitting review:', error);
      setError(error.message || 'Failed to submit review');
      // Close modal even on error so user isn't stuck
      setShowRatingModal(false);
      setRatingTrade(null);
      setRatingData({ rating: 5, comment: '' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadDashboardData = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Load data individually to handle failures gracefully
      const loadData = async (apiCall, defaultValue = null, callName = 'unknown') => {
        try {
          const result = await apiCall();
          return result;
        } catch (err) {
          return defaultValue;
        }
      };

      // Load all data in parallel but handle individual failures
            const [balanceRes, hatsRes, tradesRes, pendingRes, historyRes, disputesRes, reviewsRes, receivedReviewsRes, userMessagesRes, profileRes, transactionsRes, manualDepositsRes, withdrawalsRes] = await Promise.allSettled([
        loadData(() => balanceAPI.getBalance(), { balance: 0, available_balance: 0 }, 'getBalance'),
        loadData(() => hatsAPI.getMyHats(), [], 'getMyHats'),
        loadData(() => tradesAPI.getMyTrades(), [], 'getMyTrades'),
        loadData(() => tradesAPI.getPendingTrades(), [], 'getPendingTrades'),
        loadData(() => tradesAPI.getTradeHistory(), [], 'getTradeHistory'),
        loadData(() => disputesAPI.getMyDisputes(), [], 'getMyDisputes'),
        loadData(() => reviewsAPI.getMyReviews(), [], 'getMyReviews'),
        loadData(() => reviewsAPI.getReceivedReviews(), [], 'getReceivedReviews'),
        loadData(() => hatsAPI.getUserMessages(), [], 'getUserMessages'),
        loadData(() => profileAPI.get(), {}, 'getProfile'),
        loadData(() => balanceAPI.getTransactions({ page: 1, per_page: 10 }), { transactions: [], total: 0 }, 'getTransactions'),
        loadData(() => adminAPI.getUserManualDeposits(), [], 'getUserManualDeposits'),
        loadData(() => adminAPI.getUserWithdrawals(), [], 'getUserWithdrawals')
      ]);


      // Extract results (Promise.allSettled returns {status, value} objects)
      const balance = balanceRes.status === 'fulfilled' ? balanceRes.value : { balance: 0, available_balance: 0 };
      const hats = hatsRes.status === 'fulfilled' ? hatsRes.value : [];
      const trades = tradesRes.status === 'fulfilled' ? tradesRes.value : [];
      const pending = pendingRes.status === 'fulfilled' ? pendingRes.value : [];
      const history = historyRes.status === 'fulfilled' ? historyRes.value : [];
      const disputes = disputesRes.status === 'fulfilled' ? disputesRes.value : [];
      const reviews = reviewsRes.status === 'fulfilled' ? reviewsRes.value : [];
      const receivedReviews = receivedReviewsRes.status === 'fulfilled' ? receivedReviewsRes.value : [];
      const transactionsData = transactionsRes.status === 'fulfilled' ? transactionsRes.value : { transactions: [], total: 0 };
      const transactions = transactionsData.transactions || [];
      const transactionsTotal = transactionsData.total || 0;
      const manualDeposits = manualDepositsRes.status === 'fulfilled' ? manualDepositsRes.value : [];
      const withdrawals = withdrawalsRes.status === 'fulfilled' ? withdrawalsRes.value : [];


      setUserBalance(balance.balance || 0);
      setAvailableBalance(balance.available_balance || 0);
      setMyHats(hats);
      

      setPendingTrades(pending); // Pending trades from API

      // Filter active trades from all trades (status = 'accepted')
      const activeTradesFiltered = trades.filter(trade =>
        String(trade?.status || '').toLowerCase() === 'accepted'
      );
      setActiveTrades(activeTradesFiltered);
      
      setTradeHistory(history);
      setMyDisputes(disputes);
      setMyReviews(reviews);
      setReceivedReviews(receivedReviews);
      const userMessagesData = userMessagesRes.status === 'fulfilled' ? userMessagesRes.value : [];
      setUserMessages(userMessagesData);
      setTransactions(transactions);
      setTransactionsPage(1);
      setTransactionsTotal(transactionsTotal);
      setHasMoreTransactions(transactions.length < transactionsTotal);
      setManualDeposits(manualDeposits);
      setPendingWithdrawals((withdrawals.withdrawals || []).filter(w => w.status === 'pending'));
      
      // Check if this is an authentication issue
      const authErrors = [balanceRes, hatsRes, tradesRes, pendingRes, historyRes, disputesRes, reviewsRes, receivedReviewsRes, profileRes, transactionsRes]
        .filter(result => result.status === 'rejected' && 
          (result.reason?.message?.includes('Invalid token') || 
           result.reason?.message?.includes('No token provided') ||
           result.reason?.message?.includes('401')));
      
      if (authErrors.length >= 5) {
        setError('Your session has expired. Please sign in again');
      } else {
        const failedCalls = [balanceRes, hatsRes, tradesRes, pendingRes, historyRes, disputesRes, reviewsRes, receivedReviewsRes, profileRes, transactionsRes]
          .filter(result => result.status === 'rejected').length;
        
        if (failedCalls === 10) {
          setError('Failed to load dashboard data');
        } else if (failedCalls > 0) {
        } else {
        }
      }
      
    } catch (err) {
      console.error('Unexpected error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  const loadMoreTransactions = async () => {
    if (loadingMoreTransactions || !hasMoreTransactions) return;
    
    setLoadingMoreTransactions(true);
    try {
      const nextPage = transactionsPage + 1;
      const response = await balanceAPI.getTransactions({ page: nextPage, per_page: 10 });
      
      const newTransactions = response?.transactions || [];
      
      if (newTransactions.length === 0) {
        setHasMoreTransactions(false);
      } else {
        setTransactions(prev => {
          const updated = [...prev, ...newTransactions];
          // Check if we've reached the end
          if (updated.length >= (response?.total || transactionsTotal)) {
            setHasMoreTransactions(false);
          }
          return updated;
        });
        setTransactionsPage(nextPage);
      }
    } catch (error) {
      console.error('Error loading more transactions:', error);
      setError('Failed to load more transactions');
    } finally {
      setLoadingMoreTransactions(false);
    }
  };

  useEffect(() => {
    if (isOpen && currentUser) {
      // Check for Stripe payment success
      const urlParams = new URLSearchParams(window.location.search);
      const addFunds = urlParams.get('add_funds');
      const sessionId = urlParams.get('session_id');
      
      if (addFunds === 'success' && sessionId) {
        // Verify the payment session
        paymentsAPI.verifySession(sessionId)
          .then(() => {
            setSuccess('Payment successful! Your balance has been updated.');
            setDepositAmount(''); // Clear the deposit input
            // Clean up URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);
          })
          .catch(err => {
            console.error('Payment verification failed:', err);
            if (err.error_type === 'credit_card_rejected') {
              setError('Credit cards are not accepted. Please use a debit card.');
            } else {
              setError('Payment verification failed. Please contact support if you were charged.');
            }
          });
      } else if (addFunds === 'cancel') {
        setError('Payment was cancelled.');
        // Clean up URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      loadDashboardData();
    }
  }, [isOpen, currentUser, loadDashboardData]);

  // Auto-mark admin replies as read when Messages tab is opened
  useEffect(() => {
    if (isOpen && activeTab === 'messages') {
      const markAdminRepliesAsRead = async () => {
        try {
          // Mark all unread admin replies as read (backend will handle the check)
          await hatsAPI.markAdminRepliesRead();
          // Refresh messages to update the read status and badge count
          loadDashboardData();
        } catch (error) {
          console.error('Failed to mark admin replies as read:', error);
        }
      };
      // Small delay to ensure messages are loaded first
      const timeoutId = setTimeout(markAdminRepliesAsRead, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, activeTab, loadDashboardData]); // Run when tab becomes active

  // Fetch fresh profile data when modal opens
  useEffect(() => {
    if (isOpen && localStorage.getItem('authToken')) {
      const fetchFreshProfile = async () => {
        try {
          const profileData = await profileAPI.get();
          if (profileData) {
            setProfileFormData({
              name: profileData.name || '',
              closet_name: profileData.closet_name || '',
              phone: profileData.phone || '',
              street: profileData.address || '', // Backend uses 'address' field
              apartment: profileData.apartment || '',
              city: profileData.city || '',
              state: profileData.state || '',
              zip: profileData.zip_code || '', // Backend uses 'zip_code' field
              created_at: profileData.created_at || ''
            });
            
            if (profileData.profile_image_url) {
              setProfileImagePreview(profileData.profile_image_url);
            }
          }
        } catch (error) {
          console.error('Error fetching fresh profile data:', error);
          // Fallback to currentUser data if API fails
          if (currentUser) {
            setProfileFormData({
              name: currentUser.name || '',
              closet_name: currentUser.closet_name || '',
              phone: currentUser.phone || '',
              street: currentUser.address || '',
              apartment: currentUser.apartment || '',
              city: currentUser.city || '',
              state: currentUser.state || '',
              zip: currentUser.zip_code || '',
            });
            
            if (currentUser.profile_image_url) {
              setProfileImagePreview(currentUser.profile_image_url);
            }
          }
        }
      };
      
      fetchFreshProfile();
    }
  }, [isOpen, currentUser]);

  // Initialize profile form data when currentUser changes (fallback)
  useEffect(() => {
    if (currentUser && !isOpen) {
      setProfileFormData({
        name: currentUser.name || '',
        closet_name: currentUser.closet_name || '',
        phone: currentUser.phone || '',
        street: currentUser.address || '', // Backend uses 'address' field
        apartment: currentUser.apartment || '',
        city: currentUser.city || '',
        state: currentUser.state || '',
        zip: currentUser.zip_code || '', // Backend uses 'zip_code' field
      });
      
      if (currentUser.profile_image_url) {
        setProfileImagePreview(currentUser.profile_image_url);
      }
    }
  }, [currentUser, isOpen]);

  const handleTradeAction = async (tradeId, action, payload = {}) => {
    setIsLoading(true);
    setError('');
    
    try {
      if (action === 'accept') {
        // Check if this is a shipping trade for custom loading message
        const trade = pendingTrades.find(t => t._id === tradeId) || 
                     activeTrades.find(t => t._id === tradeId) ||
                     tradeHistory.find(t => t._id === tradeId);
        
        if (trade && trade.delivery_method === 'shipping') {
          setLoadingMessage('Purchasing shipping labels...');
        }
        
        await tradesAPI.accept(tradeId);
        
        // Optimized refresh for accept action - only update what changed
        setTradeDetails(null);
        
        // Refresh only the data that changes when accepting a trade
        const [balanceRes, pendingRes, tradesRes] = await Promise.allSettled([
          balanceAPI.getBalance(),
          tradesAPI.getPendingTrades(),
          tradesAPI.getMyTrades()
        ]);
        
        // Update balance
        if (balanceRes.status === 'fulfilled') {
          setUserBalance(balanceRes.value.balance || 0);
          setAvailableBalance(balanceRes.value.available_balance || 0);
        }
        
        // Update pending trades (remove the accepted trade)
        if (pendingRes.status === 'fulfilled') {
          setPendingTrades(pendingRes.value);
        }
        
        // Update active trades (add the accepted trade)
        if (tradesRes.status === 'fulfilled') {
          const activeTradesFiltered = tradesRes.value.filter(trade =>
            String(trade?.status || '').toLowerCase() === 'accepted'
          );
          setActiveTrades(activeTradesFiltered);
        }
        
        setSuccess('Trade accepted successfully');
        setLoadingMessage('Loading...'); // Reset to default
      } else if (action === 'reject') {
        await tradesAPI.reject(tradeId);
        setTradeDetails(null);
        await loadDashboardData();
        setSuccess('Trade rejected successfully');
      } else if (action === 'cancel') {
        await tradesAPI.cancel(tradeId);
        setTradeDetails(null);
        await loadDashboardData();
        setSuccess('Trade cancelled successfully');
      } else if (action === 'modify') {
        await tradesAPI.modify(tradeId, payload);
        setTradeDetails(null);
        await loadDashboardData();
        setSuccess('Trade modified successfully! The other user will be notified.');
      } else {
        throw new Error(`Unknown action: ${action}`);
      }
      
    } catch (err) {
      console.error('Error updating trade:', err);
      setError(`Failed to ${action} trade: ${err.message}`);
      setLoadingMessage('Loading...'); // Reset to default on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeposit = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const amount = parseFloat(depositAmount);
      if (isNaN(amount) || amount < 5) {
        setError('Please enter a valid amount of at least $5');
        return;
      }
      
      // Create Stripe checkout session
      const session = await paymentsAPI.createCheckoutSession(amount, window.location.origin);
      
      if (session.url) {
        // Redirect to Stripe checkout
        window.location.href = session.url;
      } else {
        setError('Failed to create payment session');
      }
    } catch (err) {
      console.error('Error creating deposit:', err);
      setError(`Failed to create deposit: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWithdrawSuccess = async () => {
    await loadDashboardData();
  };

  const handleViewTradeDetails = async (trade) => {
    try {
      setIsLoading(true);
      
      // If trade only has _id, fetch full trade details from backend
      let fullTrade = trade;
      if (trade._id && !trade.offered_hat && !trade.requested_hat) {
        console.log('Fetching full trade details for:', trade._id);
        const response = await tradesAPI.get(trade._id);
        if (response && response.trade) {
          fullTrade = response.trade;
          // Map hat1/hat2 to offered_hat/requested_hat for consistency
          if (response.hat1) {
            fullTrade.offered_hat = {
              ...response.hat1,
              brand: response.hat1.brand || 'N/A',
              size: response.hat1.size || 'N/A',
              condition: response.hat1.condition || 'N/A'
            };
          }
          if (response.hat2) {
            fullTrade.requested_hat = {
              ...response.hat2,
              brand: response.hat2.brand || 'N/A',
              size: response.hat2.size || 'N/A',
              condition: response.hat2.condition || 'N/A'
            };
          }
          // Map user data
          if (response.user1) {
            fullTrade.from_user = response.user1;
          }
          if (response.user2) {
            fullTrade.to_user = response.user2;
          }
        }
      }
      
      // Use the backend's dual financial breakdown system
      const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
      const initiatorId = fullTrade.hold_user_id;
      const youInitiated = initiatorId === currentUserId;
      
      // Get the correct financial breakdown based on user perspective
      let userFinancialBreakdown;
      
      if (youInitiated) {
        // User 1 (initiator) - use financial_breakdown
        if (fullTrade.financial_breakdown) {
          userFinancialBreakdown = fullTrade.financial_breakdown;
        } else if (fullTrade.financial_breakdown_initiator) {
          userFinancialBreakdown = fullTrade.financial_breakdown_initiator;
        } else {
          // Fallback for initiator
          const yourBorrowedValue = Number(fullTrade.requested_hat?.value || 0);
          const siteFee = Number(fullTrade.financial_breakdown?.site_fee || 0);
          const shippingFees = Number(fullTrade.financial_breakdown?.delivery_fees || 0);
          const totalFees = siteFee + shippingFees;
          const originalBalance = Number(fullTrade.financial_breakdown?.original_balance || 0);
          
          userFinancialBreakdown = {
            site_fee: siteFee,
            delivery_fees: shippingFees,
            other_item_value: yourBorrowedValue,
            total_charge: totalFees + yourBorrowedValue,
            original_balance: originalBalance,
            ending_balance: originalBalance - totalFees - yourBorrowedValue
          };
        }
      } else {
        // User 2 (receiver) - use financials
        if (fullTrade.financials) {
          // Convert financials to financial_breakdown format
          // Use receiver's actual fees from financials (not initiator's fees)
          const receiverSiteFee = Number(fullTrade.financials.site_fee || 0);
          const receiverDeliveryFees = Number(fullTrade.financials.delivery_fees || 0);
          const yourBorrowedValue = Number(fullTrade.offered_hat?.value || 0);
          const originalBalance = Number(fullTrade.financials.original_balance || 0);
          const endingBalance = Number(fullTrade.financials.ending_balance || 0);
          
          userFinancialBreakdown = {
            site_fee: receiverSiteFee,
            delivery_fees: receiverDeliveryFees,
            other_item_value: yourBorrowedValue,
            total_charge: receiverSiteFee + receiverDeliveryFees + yourBorrowedValue,
            original_balance: originalBalance,
            ending_balance: endingBalance
          };
        } else if (fullTrade.financial_breakdown_receiver) {
          userFinancialBreakdown = fullTrade.financial_breakdown_receiver;
        } else {
          // Fallback for receiver
          const yourBorrowedValue = Number(fullTrade.offered_hat?.value || 0);
          const siteFee = Number(fullTrade.financial_breakdown?.site_fee || 0);
          const shippingFees = Number(fullTrade.financial_breakdown?.delivery_fees || 0);
          const totalFees = siteFee + shippingFees;
          const originalBalance = Number(fullTrade.financial_breakdown?.original_balance || 0);
          
          userFinancialBreakdown = {
            site_fee: siteFee,
            delivery_fees: shippingFees,
            other_item_value: yourBorrowedValue,
            total_charge: totalFees + yourBorrowedValue,
            original_balance: originalBalance,
            ending_balance: originalBalance - totalFees - yourBorrowedValue
          };
        }
      }
      
      // Create trade object with user-specific financial data
      const userTradeData = {
        ...fullTrade,
        financial_breakdown: userFinancialBreakdown
      };
      
      console.log('Setting trade details:', userTradeData);
      setTradeDetails({ trade: userTradeData });
      setTradeDetailsOpen(true);
    } catch (error) {
      console.error('Error loading trade details:', error);
      setError('Failed to load trade details');
    } finally {
      setIsLoading(false);
    }
  };

  // Modify trade functions
  const openModifyModal = async (trade) => {
    setModifyTrade(trade);
    setModifyMode('date');
    // Pre-fill dates if available
    setModifyDates({ 
      start_date: (trade.start_date || '').slice(0,10), 
      end_date: (trade.end_date || '').slice(0,10) 
    });
    setClosetItems([]);
    setModifyOpen(true);
  };

  const loadOtherUserCloset = async (trade) => {
    if (!trade) return;
    try {
      setClosetLoading(true);
      // Determine other user's id relative to current user
      const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
      const otherUserId = trade.user1_id === currentUserId ? trade.user2_id : trade.user1_id;
      const closet = await usersAPI.getCloset(otherUserId, { page: 1, perPage: 50 });
      setClosetItems((closet && closet.items) || []);
    } catch (e) {
      setClosetItems([]);
    } finally {
      setClosetLoading(false);
    }
  };

  const submitModify = async () => {
    if (!modifyTrade) return;
    
    // Validate dates if modifying dates
    if (modifyMode === 'date' && modifyDates.start_date && modifyDates.end_date) {
      const validation = validateDateRange(modifyDates.start_date, modifyDates.end_date);
      if (!validation.isValid) {
        setDateValidationError(validation.error);
        return;
      }
    }
    
    const updates = {};
    
    // Always allow both dates and item change together
    if (modifyDates.start_date) updates.start_date = modifyDates.start_date;
    if (modifyDates.end_date) updates.end_date = modifyDates.end_date;
    
    if (selectedItemId) {
      const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
      const isInitiator = modifyTrade.hold_user_id === currentUserId;
      if (isInitiator) {
        updates.hat2_id = selectedItemId; // Initiator changing requested item
      } else {
        updates.hat1_id = selectedItemId; // Receiver changing offered item
      }
    }
    
    const modMsg = prompt('Add a short message about your changes (optional):', '');
    if (modMsg && modMsg.trim()) {
      updates.mod_message = modMsg.trim();
    }
    
    if (Object.keys(updates).length === 0) return;
    
    await handleTradeAction(modifyTrade._id, 'modify', updates);
    setModifyOpen(false);
    setSelectedItemId('');
  };

  // Trade messaging functions
  const sendTradeMessage = async (tradeId, message) => {
    if (!message.trim() || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      // Send message via API
      const response = await tradesAPI.sendMessage(tradeId, message.trim());
      
      if (response && response.message_data) {
        const newMessage = {
          id: response.message_data.id,
          user_name: response.message_data.user_name,
          message: response.message_data.message,
          timestamp: response.message_data.timestamp
        };
        
        setTradeMessages(prev => ({
          ...prev,
          [tradeId]: [...(prev[tradeId] || []), newMessage]
        }));
        
        setNewMessages(prev => ({
          ...prev,
          [tradeId]: ''
        }));
        
        setSuccess('Message sent successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSendingMessage(false);
    }
  };

  // TODO: implement loadTradeMessages functionality
  // const loadTradeMessages = async (tradeId) => {
  //   try {
  //     // Load all messages for this trade from backend
  //     const response = await tradesAPI.getMessages(tradeId);
  //     if (response && response.messages) {
  //       setTradeMessages(prev => ({
  //         ...prev,
  //         [tradeId]: response.messages
  //       }));
  //     }
  //   } catch (error) {
  //     console.error('Error loading trade messages:', error);
  //     // Fallback to existing trade messages if API fails
  //     const trade = pendingTrades.find(t => t._id === tradeId) || 
  //                  activeTrades.find(t => t._id === tradeId) ||
  //                  tradeHistory.find(t => t._id === tradeId);
  //     
  //     if (trade && trade.messages) {
  //       setTradeMessages(prev => ({
  //         ...prev,
  //         [tradeId]: trade.messages
  //       }));
  //     }
  //   }
  // };

  // Timeline tracking functions
  const createTimelineEntry = (trade) => {
    const timeline = [];
    
    // Add creation entry
    timeline.push({
      id: `create-${trade._id}`,
      type: 'created',
      user_id: trade.hold_user_id,
      user_name: trade.from_user?.name || 'Unknown',
      timestamp: trade.created_at,
      description: 'initiated this trade',
      icon: '🚀'
    });
    
    // Add modification entries if any
    if (trade.status === 'modified' && trade.last_modified_by) {
      timeline.push({
        id: `modify-${trade._id}`,
        type: 'modified',
        user_id: trade.last_modified_by,
        user_name: trade.last_modified_by === trade.hold_user_id ? trade.from_user?.name : trade.to_user?.name || 'Unknown',
        timestamp: trade.updated_at,
        description: 'modified this trade',
        icon: '✏️',
        message: trade.mod_message
      });
    }
    
    // Add messages if any - use tradeMessages from API instead of trade.messages
    const messages = tradeMessages[trade._id] || [];
    if (messages && messages.length > 0) {
      messages.forEach((msg, index) => {
        timeline.push({
          id: `msg-${trade._id}-${index}`,
          type: 'message',
          user_id: msg.id,
          user_name: msg.user_name || 'Unknown',
          timestamp: msg.timestamp,
          description: 'sent a message',
          icon: '💬',
          message: msg.message
        });
      });
    }
    
    return timeline.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  // Item detail functions for modify screen
  const showItemDetails = (item) => {
    setSelectedItemForDetail(item);
    setShowItemDetailPopup(true);
  };

  const closeItemDetailPopup = () => {
    setShowItemDetailPopup(false);
    setSelectedItemForDetail(null);
  };

  const openImageLightbox = (item, imageIndex = 0) => {
    const allImages = [];
    if (item.image_url) {
      allImages.push(item.image_url);
    }
    if (item.additional_images && item.additional_images.length > 0) {
      allImages.push(...item.additional_images);
    }
    
    if (allImages.length > 0) {
      setLightboxImages(allImages);
      setLightboxIndex(imageIndex);
      setLightboxOpen(true);
    }
  };

  const loadFullClosetModal = async (trade) => {
    setFullClosetLoading(true);
    setShowFullClosetModal(true);
    
    try {
      const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
      const otherUserId = trade.user1_id === currentUserId ? trade.user2_id : trade.user1_id;
      
      console.log('Loading full closet for user:', otherUserId);
      const response = await usersAPI.getCloset(otherUserId);
      console.log('Full closet response:', response);
      
      if (response && response.items) {
        setFullClosetItems(response.items);
        console.log('Set full closet items:', response.items.length);
      } else {
        console.log('No items found in response:', response);
        setError('No items found in closet');
      }
    } catch (error) {
      console.error('Error loading full closet:', error);
      setError('Failed to load closet items');
    } finally {
      setFullClosetLoading(false);
    }
  };

  // Custom transaction amount formatter with rule-based logic and proper refund handling
  const formatTransactionAmount = (transaction) => {
    const amount = Math.abs(transaction.amount);
    const description = (transaction.description || '').toLowerCase();
    const transactionType = (transaction.transaction_type || '').toLowerCase();
    
    // Determine charge type and formatting using rule-based priority system
    let chargeType = '';
    let sign = '';
    let color = '';
    
    // PRIORITY 1: Exact refund transaction types (highest priority)
    if (transactionType === 'site_fee_refund') {
      chargeType = 'Site Fee Refund';
      sign = '+';
      color = '#28a745';
    } else if (transactionType === 'shipping_fee_refund') {
      // Determine if this is pickup or shipping based on trade delivery method
      const deliveryMethod = transaction.delivery_method;
      chargeType = deliveryMethod === 'pickup' ? 'Pickup Fee Refund' : 
                   deliveryMethod === 'shipping' ? 'Shipping Fee Refund' : 
                   'Delivery Fee Refund';
      sign = '+';
      color = '#28a745';
    } else if (transactionType === 'hold_refund') {
      chargeType = 'Hold Return';
      sign = '+';
      color = '#28a745';
    } 
    // PRIORITY 2: Exact charge transaction types
    else if (transactionType === 'site_fee') {
      chargeType = 'Site Fee';
      sign = '-';
      color = '#dc3545';
    } else if (transactionType === 'shipping_fee') {
      // Determine if this is pickup or shipping based on trade delivery method
      const deliveryMethod = transaction.delivery_method;
      chargeType = deliveryMethod === 'pickup' ? 'Pickup Fee' : 
                   deliveryMethod === 'shipping' ? 'Shipping Fee' : 
                   'Delivery Fee';
      sign = '-';
      color = '#dc3545';
    } else if (transactionType === 'hold') {
      chargeType = 'Hold Reserved';
      sign = '-';
      color = '#dc3545';
    }
    // PRIORITY 3: Description-based refund detection (before generic charges)
    else if (description.includes('refund') && (description.includes('site fee') || description.includes('site_fee'))) {
      chargeType = 'Site Fee Refund';
      sign = '+';
      color = '#28a745';
    } else if (description.includes('refund') && (description.includes('shipping fee') || description.includes('pickup fee'))) {
      // Determine if this is pickup or shipping based on trade delivery method
      const deliveryMethod = transaction.delivery_method;
      chargeType = deliveryMethod === 'pickup' ? 'Pickup Fee Refund' : 
                   deliveryMethod === 'shipping' ? 'Shipping Fee Refund' : 
                   'Delivery Fee Refund';
      sign = '+';
      color = '#28a745';
    } else if (description.includes('refund') && (description.includes('item value') || description.includes('hold'))) {
      chargeType = 'Hold Return';
      sign = '+';
      color = '#28a745';
    }
    // PRIORITY 4: Generic refund detection
    else if (description.includes('refund') || transactionType.includes('refund')) {
      chargeType = 'Trade Refund';
      sign = '+';
      color = '#28a745';
    }
    // PRIORITY 5: Description-based charge detection (after refunds)
    else if (description.includes('site fee') || transactionType.includes('site_fee')) {
      chargeType = 'Site Fee';
      sign = '-';
      color = '#dc3545';
    } else if (description.includes('shipping fee') || description.includes('pickup fee') || transactionType.includes('shipping_fee') || transactionType.includes('pickup_fee')) {
      // Determine if this is pickup or shipping based on trade delivery method
      const deliveryMethod = transaction.delivery_method;
      chargeType = deliveryMethod === 'pickup' ? 'Pickup Fee' : 
                   deliveryMethod === 'shipping' ? 'Shipping Fee' : 
                   'Delivery Fee';
      sign = '-';
      color = '#dc3545';
    } else if (description.includes('item value') || description.includes('hold') || transactionType.includes('hold')) {
      chargeType = 'Hold Reserved';
      sign = '-';
      color = '#dc3545';
    } else if (description.includes('trade creation') || transactionType.includes('trade_creation')) {
      chargeType = 'Trade Creation Fee';
      sign = '-';
      color = '#dc3545';
    } else if (description.includes('trade acceptance') || transactionType.includes('trade_acceptance')) {
      chargeType = 'Trade Acceptance Fee';
      sign = '-';
      color = '#dc3545';
    } else if (description.includes('deposit') || transactionType.includes('deposit')) {
      // Check if this is a manual deposit with reference number
      const referenceMatch = description.match(/Reference:\s*([A-Z0-9]+)/);
      if (referenceMatch) {
        chargeType = `Deposit ${referenceMatch[1]}`;
      } else {
        chargeType = 'Deposit';
      }
      sign = '+';
      color = '#28a745';
    } else if (description.includes('withdrawal') || transactionType.includes('withdrawal')) {
      // Check if this is a failed withdrawal (withdrawal refund)
      if (transactionType === 'withdraw_refund' || description.includes('withdrawal failed') || description.includes('funds returned')) {
        chargeType = 'Withdrawal Returned';
        sign = '+';
        color = '#28a745';
      } else {
        chargeType = 'Withdrawal';
        sign = '-';
        color = '#dc3545';
      }
    } 
    // PRIORITY 6: Fallback to direction-based formatting (lowest priority)
    else {
      chargeType = transaction.direction === 'credit' ? 'Credit' : 'Debit';
      sign = transaction.direction === 'credit' ? '+' : '-';
      color = transaction.direction === 'credit' ? '#28a745' : '#dc3545';
    }
    
    // Include trade number if available
    const tradeNumber = transaction.trade_id ? `#${transaction.trade_id.slice(-6)}` : '';
    
    return {
      text: `${sign}$${amount.toFixed(2)}`,
      color: color,
      tradeNumber: tradeNumber,
      chargeType: chargeType,
      direction: transaction.direction
    };
  };

  if (!isOpen) return null;

  return (
    <div className="modal dashboard-modal">
      <div className="modal-content dashboard-content" style={{maxHeight:'90vh', overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <span className="close" onClick={onClose}>&times;</span>
        
        <div className="dashboard-header">
          <h2>My Dashboard</h2>
          <div className="user-info">
            <span>Welcome, {currentUser?.name || currentUser?.username || 'User'}!</span>
            <button className="btn btn-secondary" onClick={onLogout}>Logout</button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
            {error.includes('Please log in') && (
              <div style={{marginTop: '10px'}}>
                <button 
                  className="btn" 
                  onClick={() => {
                    // Close dashboard and trigger login
                    onClose();
                    // You might want to trigger a login modal here
                    window.location.reload(); // Simple solution for now
                  }}
                  style={{background: '#007bff', color: 'white'}}
                >
                  Go to Login
                </button>
              </div>
            )}
          </div>
        )}

        {success && (
          <div className="success-message">
            <p>{success}</p>
          </div>
        )}
        
        <div className="dashboard-tabs">
          {tabs.map(tab => (
            <button 
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
              style={{position: 'relative'}}
            >
              <i className={tab.icon}></i>
              {tab.label}
              {/* Badge for Trades tab */}
              {tab.id === 'trades' && pendingTrades.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#dc3545',
                  color: 'white',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {pendingTrades.length}
                </span>
              )}
              {/* Badge for Disputes tab */}
              {tab.id === 'disputes' && myDisputes.filter(d => d.status === 'open').length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#ffc107',
                  color: '#000',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {myDisputes.filter(d => d.status === 'open').length}
                </span>
              )}
              {/* Badge for Messages tab */}
              {tab.id === 'messages' && (() => {
                const totalUnread = userMessages ? userMessages.filter(msg => msg.sender === 'admin' && !msg.read).length : 0;
                return totalUnread > 0 ? (
                  <span style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    background: '#dc3545',
                    color: 'white',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>
                    {totalUnread}
                  </span>
                ) : null;
              })()}
              {/* Badge for Active tab */}
              {tab.id === 'active-trades' && activeTrades.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#28a745',
                  color: 'white',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {activeTrades.length}
                </span>
              )}
            </button>
          ))}
        </div>
        
        <div className="dashboard-content">
          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner"></div>
              <p>{loadingMessage}</p>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tab-content active">
              {/* Welcome Section */}
              <div style={{background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color:'#fff', padding:20, borderRadius:12, marginBottom:20}}>
                <h2 style={{margin:'0 0 8px 0'}}>Welcome back, {currentUser?.name || 'Trader'}!</h2>
                <p style={{margin:0, opacity:0.9}}>Here's your trading dashboard overview</p>
              </div>

              {/* Quick Stats Grid */}
              <div className="overview-stats" style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:16, marginBottom:20}}>
                <div className="stat-card" style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:8, padding:16, textAlign:'center', boxShadow:'0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div style={{fontSize:32, marginBottom:8}}>💰</div>
                  <h4 style={{margin:'0 0 8px 0', color:'#495057'}}>Available Balance</h4>
                  <p className="stat-value" style={{fontSize:24, fontWeight:'bold', color:'#28a745', margin:0}}>${availableBalance.toFixed(2)}</p>
                  <p style={{fontSize:12, color:'#666', margin:'4px 0 0 0'}}>Ready to trade</p>
                </div>
                <div className="stat-card" style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:8, padding:16, textAlign:'center', boxShadow:'0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div style={{fontSize:32, marginBottom:8}}>🔄</div>
                  <h4 style={{margin:'0 0 8px 0', color:'#495057'}}>Active Trades</h4>
                  <p className="stat-value" style={{fontSize:24, fontWeight:'bold', color:'#007bff', margin:0}}>{activeTrades.length}</p>
                  <p style={{fontSize:12, color:'#666', margin:'4px 0 0 0'}}>In progress</p>
                </div>
                <div className="stat-card" style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:8, padding:16, textAlign:'center', boxShadow:'0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div style={{fontSize:32, marginBottom:8}}>⏳</div>
                  <h4 style={{margin:'0 0 8px 0', color:'#495057'}}>Pending Trades</h4>
                  <p className="stat-value" style={{fontSize:24, fontWeight:'bold', color:'#ffc107', margin:0}}>{pendingTrades.length}</p>
                  <p style={{fontSize:12, color:'#666', margin:'4px 0 0 0'}}>Awaiting response</p>
                </div>
                <div className="stat-card" style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:8, padding:16, textAlign:'center', boxShadow:'0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div style={{fontSize:32, marginBottom:8}}>👜</div>
                  <h4 style={{margin:'0 0 8px 0', color:'#495057'}}>My Items</h4>
                  <p className="stat-value" style={{fontSize:24, fontWeight:'bold', color:'#6f42c1', margin:0}}>{myHats.length}</p>
                  <p style={{fontSize:12, color:'#666', margin:'4px 0 0 0'}}>Available to trade</p>
                </div>
                <div className="stat-card" style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:8, padding:16, textAlign:'center', boxShadow:'0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div style={{fontSize:32, marginBottom:8}}>📊</div>
                  <h4 style={{margin:'0 0 8px 0', color:'#495057'}}>Trade History</h4>
                  <p className="stat-value" style={{fontSize:24, fontWeight:'bold', color:'#17a2b8', margin:0}}>{tradeHistory.length}</p>
                  <p style={{fontSize:12, color:'#666', margin:'4px 0 0 0'}}>Completed trades</p>
                </div>
                <div className="stat-card" style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:8, padding:16, textAlign:'center', boxShadow:'0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div style={{fontSize:32, marginBottom:8}}>⭐</div>
                  <h4 style={{margin:'0 0 8px 0', color:'#495057'}}>Reviews Given</h4>
                  <p className="stat-value" style={{fontSize:24, fontWeight:'bold', color:'#fd7e14', margin:0}}>{myReviews.length}</p>
                  <p style={{fontSize:12, color:'#666', margin:'4px 0 0 0'}}>Ratings submitted</p>
                </div>
                <div className="stat-card" style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:8, padding:16, textAlign:'center', boxShadow:'0 2px 4px rgba(0,0,0,0.1)'}}>
                  <div style={{fontSize:32, marginBottom:8}}>🏆</div>
                  <h4 style={{margin:'0 0 8px 0', color:'#495057'}}>My Rating</h4>
                  <p className="stat-value" style={{fontSize:24, fontWeight:'bold', color:'#28a745', margin:0}}>
                    {receivedReviews.length > 0 ? 
                      (receivedReviews.reduce((sum, r) => sum + r.rating, 0) / receivedReviews.length).toFixed(1) : 
                      '0.0'
                    } ⭐
                  </p>
                  <p style={{fontSize:12, color:'#666', margin:'4px 0 0 0'}}>({receivedReviews.length} reviews)</p>
                </div>
              </div>

              {/* Current Active Trade */}
              {(() => {
                try {
                  if (activeTrades.length > 0) {
                    const current = activeTrades.slice().sort((a,b)=> new Date(b.created_at||b.start_date||0) - new Date(a.created_at||a.start_date||0))[0];
                    return (
                      <div className="current-trade-card" style={{background:'#fff', border:'1px solid #28a745', borderRadius:12, padding:20, marginBottom:20, boxShadow:'0 4px 6px rgba(0,0,0,0.1)'}}>
                        <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:16}}>
                          <div style={{fontSize:24}}>🔄</div>
                          <h3 style={{margin:0, color:'#28a745'}}>Current Active Trade</h3>
                        </div>
                        <div style={{display:'flex', gap:16, alignItems:'center', marginBottom:16}}>
                          <div style={{width:80, height:80, background:'#e9ecef', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#6c757d', overflow:'hidden'}}>
                            {current?.offered_hat?.image_url ? (
                              <img src={current.offered_hat.image_url} alt={current.offered_hat.name} style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:8}} />
                            ) : (
                              <span>No Image</span>
                            )}
                          </div>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:'bold', fontSize:18, marginBottom:4}}>{current?.offered_hat?.name || 'Unknown Item'}</div>
                            <div style={{fontSize:14, color:'#6c757d', marginBottom:4}}>Trading for {current?.requested_hat?.name || 'Unknown Item'}</div>
                            <div style={{fontSize:12, color:'#6c757d'}}>Ref: #{String(current?._id||'').slice(-6)}</div>
                          </div>
                          <button className="btn" onClick={() => handleViewTradeDetails(current)} style={{background:'#28a745', color:'#fff'}}>
                            View Details
                          </button>
                        </div>
                        
                        {/* Progress Bar */}
                        <div style={{background:'#f8f9fa', padding:12, borderRadius:8}}>
                          <div style={{fontSize:14, fontWeight:'bold', marginBottom:8}}>Trade Progress</div>
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                            <span style={{fontSize:12, color: current.user1_received_at ? '#28a745' : '#6c757d'}}>
                              {current.user1_received_at ? '✅' : '⏳'} You Received
                            </span>
                            <span style={{fontSize:12, color: current.user2_received_at ? '#28a745' : '#6c757d'}}>
                              {current.user2_received_at ? '✅' : '⏳'} Sister Received
                            </span>
                            <span style={{fontSize:12, color: current.user1_returned_at ? '#28a745' : '#6c757d'}}>
                              {current.user1_returned_at ? '✅' : '⏳'} You Returned
                            </span>
                            <span style={{fontSize:12, color: current.user2_returned_at ? '#28a745' : '#6c757d'}}>
                              {current.user2_returned_at ? '✅' : '⏳'} Sister Returned
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="no-active-trades" style={{background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:12, padding:20, marginBottom:20, textAlign:'center'}}>
                        <div style={{fontSize:48, marginBottom:16, opacity:0.3}}>🔄</div>
                        <h3 style={{margin:'0 0 8px 0'}}>No Active Trades</h3>
                        <p style={{color:'#6c757d', margin:0}}>You don't have any active trades at the moment.</p>
                      </div>
                    );
                  }
                } catch { 
                  return (
                    <div className="error-state" style={{background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:12, padding:20, marginBottom:20, textAlign:'center'}}>
                      <div style={{fontSize:48, marginBottom:16, opacity:0.3}}>⚠️</div>
                      <h3 style={{margin:'0 0 8px 0'}}>Unable to Load Trade Data</h3>
                      <p style={{color:'#6c757d', margin:0}}>There was an error loading your trade information.</p>
                    </div>
                  );
                }
              })()}

              {/* Quick Actions */}
              <div style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:12, padding:20, marginBottom:20}}>
                <h3 style={{margin:'0 0 16px 0'}}>Quick Actions</h3>
                <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:12}}>
                  <button className="btn" onClick={() => setActiveTab('upload')} style={{background:'#28a745', color:'#fff', padding:'12px 16px'}}>
                    ➕ Upload Item
                  </button>
                  <button className="btn" onClick={() => setActiveTab('trades')} style={{background:'#6f42c1', color:'#fff', padding:'12px 16px'}}>
                    🔍 View Trades
                  </button>
                  <button className="btn" onClick={() => setActiveTab('balance')} style={{background:'#ffc107', color:'#000', padding:'12px 16px'}}>
                    💰 Check Balance
                  </button>
                  <button className="btn" onClick={() => setActiveTab('active-trades')} style={{background:'#007bff', color:'#fff', padding:'12px 16px'}}>
                    🔄 Active Trades
                  </button>
                </div>
              </div>

              {/* Recent Activity */}
              <div style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:12, padding:20, marginBottom:20}}>
                <h3 style={{margin:'0 0 16px 0'}}>Recent Activity</h3>
                <div style={{display:'flex', flexDirection:'column', gap:8}}>
                  {transactions.slice(0, 5).map((transaction, index) => (
                    <div key={transaction._id} style={{display:'flex', alignItems:'center', gap:12, padding:8, background:'#f8f9fa', borderRadius:6}}>
                      <div style={{fontSize:20}}>
                        {transaction.type === 'credit' ? '💰' : transaction.type === 'debit' ? '💸' : '📊'}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:14, fontWeight:'bold'}}>
                          {formatTransactionAmount(transaction).chargeType}
                        </div>
                        <div style={{fontSize:12, color:'#666'}}>
                          {formatTransactionAmount(transaction).tradeNumber && `${formatTransactionAmount(transaction).tradeNumber} `}
                          {transaction.description || 'Transaction processed'}
                        </div>
                      </div>
                      <div style={{fontSize:12, color:'#666', textAlign:'right'}}>
                        <div style={{fontWeight:'bold', color: formatTransactionAmount(transaction).color}}>
                          {formatTransactionAmount(transaction).text}
                        </div>
                        <div>{new Date(transaction.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  ))}
                  {transactions.length === 0 && (
                    <div style={{textAlign:'center', padding:20, color:'#666'}}>
                      <div style={{fontSize:32, marginBottom:8, opacity:0.3}}>📊</div>
                      <p style={{margin:0}}>No recent transactions</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Notifications (recent admin side of merged Messages thread) */}
              <div style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:12, padding:20}}>
                <h3 style={{margin:'0 0 16px 0'}}>Notifications</h3>
                <div style={{display:'flex', flexDirection:'column', gap:8}}>
                  {(() => {
                    const recentAdmin = (userMessages || [])
                      .filter((m) => m.sender === 'admin')
                      .sort((a, b) => new Date(b.timestamp || b.created_at) - new Date(a.timestamp || a.created_at))
                      .slice(0, 2);
                    return recentAdmin.length > 0 ? (
                    recentAdmin.map((message) => {
                      const text = (message.message || message.content || '').trim();
                      return (
                      <div key={message._id} style={{display:'flex', alignItems:'center', gap:12, padding:8, background:'#f8f9fa', borderRadius:6}}>
                        <div style={{fontSize:20}}>📢</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:14, fontWeight:'bold'}}>
                            Message from admin
                          </div>
                          <div style={{fontSize:12, color:'#666'}}>
                            {text.substring(0, 50) + (text.length > 50 ? '...' : '')}
                          </div>
                        </div>
                        <div style={{fontSize:12, color:'#666'}}>
                          {new Date(message.timestamp || message.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    );
                    })
                  ) : (
                    <div style={{textAlign:'center', padding:20, color:'#666'}}>
                      <div style={{fontSize:32, marginBottom:8, opacity:0.3}}>🔔</div>
                      <p style={{margin:0}}>No notifications</p>
                    </div>
                  );
                  })()}
                </div>
              </div>
            </div>
          )}


          {/* My Bags Tab */}
          {activeTab === 'hats' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>My Bags</h3>
                <button className="btn" onClick={loadDashboardData} disabled={isLoading}>Refresh</button>
              </div>
              <div className="hats-grid" style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:16}}>
                {myHats && myHats.length > 0 ? (
                  myHats.map((hat) => (
                    <div key={hat._id} className="hat-item" style={{border: '1px solid #ddd', borderRadius: '8px', background:'#fff', display:'flex', flexDirection:'column', height:'100%', position:'relative'}}>
                      {/* Status Badge */}
                      {hat.status && hat.status !== 'active' && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          zIndex: 10,
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: '#fff',
                          backgroundColor: hat.status === 'flagged' ? '#dc3545' : hat.status === 'pending' ? '#ffc107' : '#28a745',
                          textTransform: 'uppercase',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          {hat.status === 'flagged' ? 'FLAGGED' : hat.status === 'pending' ? 'PENDING' : 'APPROVED'}
                        </div>
                      )}
                      {hat.image_url && (
                        <img src={hat.image_url} alt={hat.name} style={{width: '100%', height: '150px', objectFit: 'contain', borderRadius: '8px 8px 0 0'}} />
                      )}
                      <div style={{padding: '15px', flex:1, display:'flex', flexDirection:'column'}}>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px'}}>
                          <h4 style={{margin:'0', fontSize:'16px', fontWeight:'bold'}}>{hat.name}</h4>
                          {isItemInActiveTrade(hat._id) && (
                            <span style={{
                              background:'#ffc107', 
                              color:'#000', 
                              padding:'2px 6px', 
                              borderRadius:'4px', 
                              fontSize:'10px', 
                              fontWeight:'bold'
                            }}>
                              IN TRADE
                            </span>
                          )}
                        </div>
                        <p style={{fontSize:14, color:'#666', margin:'0 0 12px 0', flex:1}}>{hat.description}</p>
                        
                        <div style={{marginBottom:'12px'}}>
                          <div style={{display:'flex', justifyContent:'space-between', marginBottom:'4px'}}>
                            <span style={{fontSize:14, color:'#333'}}>Value:</span>
                            <span style={{fontSize:14, fontWeight:'bold', color:'#28a745'}}>${hat.value.toFixed(2)}</span>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between', marginBottom:'4px'}}>
                            <span style={{fontSize:14, color:'#333'}}>Condition:</span>
                            <span style={{fontSize:14, color:'#333'}}>{hat.condition || 'Not specified'}</span>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between', marginBottom:'4px'}}>
                            <span style={{fontSize:14, color:'#333'}}>Brand:</span>
                            <span style={{fontSize:14, color:'#333'}}>{hat.brand || 'Not specified'}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div style={{background:'#f8f9fa', padding:'12px', borderRadius:'0 0 8px 8px', display:'flex', gap:'8px'}}>
                        <button 
                          className="btn" 
                          onClick={() => {
                            if (isItemInActiveTrade(hat._id)) {
                              setError('This item is part of an active trade and cannot be edited');
                              return;
                            }
                            setEditHat(hat);
                            setEditFormData({
                              name: hat.name || '',
                              description: hat.description || '',
                              value: hat.value || '',
                              condition: hat.condition || '',
                              brand: hat.brand || ''
                            });
                            setEditOpen(true);
                          }}
                          style={{
                            background: isItemInActiveTrade(hat._id) ? '#6c757d' : '#007bff', 
                            color:'#fff', 
                            padding:'8px 12px', 
                            borderRadius:'4px', 
                            border:'none', 
                            fontSize:'12px',
                            flex:1,
                            display:'flex',
                            alignItems:'center',
                            justifyContent:'center',
                            gap:'4px',
                            cursor: isItemInActiveTrade(hat._id) ? 'not-allowed' : 'pointer'
                          }}
                          disabled={isItemInActiveTrade(hat._id)}
                          title={isItemInActiveTrade(hat._id) ? 'This item is part of an active trade and cannot be edited' : 'Edit item'}
                        >
                          ✎ Edit
                        </button>
                        <button 
                          className="btn" 
                          onClick={async () => {
                            if (isItemInActiveTrade(hat._id)) {
                              setError('This item is part of an active trade and cannot be deleted');
                              return;
                            }
                            
                            if (window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
                              setIsLoading(true);
                              setError('');
                              setSuccess('');
                              
                              try {
                                await hatsAPI.delete(hat._id);
                                setSuccess('Item deleted successfully!');
                                await loadDashboardData();
                              } catch (error) {
                                console.error('Delete error:', error);
                                setError('Failed to delete item. Please try again.');
                              } finally {
                                setIsLoading(false);
                              }
                            }
                          }}
                          disabled={isItemInActiveTrade(hat._id)}
                          style={{
                            background: isItemInActiveTrade(hat._id) ? '#6c757d' : '#dc3545', 
                            color:'#fff', 
                            padding:'8px 12px', 
                            borderRadius:'4px', 
                            border:'none', 
                            fontSize:'12px',
                            flex:1,
                            display:'flex',
                            alignItems:'center',
                            justifyContent:'center',
                            gap:'4px',
                            cursor: isItemInActiveTrade(hat._id) ? 'not-allowed' : 'pointer'
                          }}
                          title={isItemInActiveTrade(hat._id) ? 'This item is part of an active trade and cannot be deleted' : 'Delete item'}
                        >
                          🗑 Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No purses found.</p>
                )}
              </div>
            </div>
          )}

          {/* Upload Item Tab */}
          {activeTab === 'upload' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Upload New Item</h3>
              </div>
              
              <div className="upload-form" style={{background:'#f8f9fa', padding:'20px', borderRadius:'8px', border:'1px solid #dee2e6'}}>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setIsLoading(true);
                  setError('');
                  setSuccess('');
                  
                  try {
                    const formData = new FormData(e.target);
                    const hatData = {
                      name: formData.get('name'),
                      description: formData.get('description'),
                      value: parseFloat(formData.get('value')),
                      condition: formData.get('condition'),
                      brand: formData.get('brand'),
                      size: formData.get('size')
                    };
                    
                    // Validate required fields
                    if (!hatData.name || !hatData.description || !hatData.value || !hatData.condition || !hatData.brand || !hatData.size) {
                      throw new Error('Please fill in all required fields');
                    }
                    
                    if (hatData.value <= 0) {
                      throw new Error('Value must be greater than 0');
                    }
                    
                    // Handle multiple image uploads
                    const imageFiles = formData.getAll('images');
                    if (!imageFiles || imageFiles.length === 0) {
                      throw new Error('Please select at least one image');
                    }
                    
                    if (imageFiles.length > 5) {
                      throw new Error('Maximum 5 images allowed');
                    }
                    
                    // Convert images to base64 for upload
                    const imagePromises = imageFiles.map(file => {
                      if (file.size === 0) {
                        throw new Error('One or more selected files are empty');
                      }
                      return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                      });
                    });
                    
                    const imageBase64s = await Promise.all(imagePromises);
                    
                    // Set primary image and additional images
                    hatData.image = imageBase64s[0]; // First image as primary
                    if (imageBase64s.length > 1) {
                      hatData.additional_images = imageBase64s.slice(1); // Additional images
                    }
                    
                    // Create the hat
                    console.log('Uploading hat with data:', hatData);
                    const response = await hatsAPI.create(hatData);
                    console.log('Upload response:', response);
                    
                    if (response && response.hat && response.hat._id) {
                      setSuccess('Item uploaded successfully!');
                      // Reset form
                      e.target.reset();
                      // Switch back to overview tab
                      setActiveTab('overview');
                      // Reload dashboard data to show the new item
                      await loadDashboardData();
                    } else {
                      throw new Error('Failed to upload item');
                    }
                  } catch (error) {
                    console.error('Upload error:', error);
                    setError(error.message || 'Failed to upload item');
                  } finally {
                    setIsLoading(false);
                  }
                }}>
                  <div style={{marginBottom:'16px'}}>
                    <label style={{display:'block', marginBottom:'8px', fontWeight:'bold'}}>Item Name:</label>
                    <input 
                      type="text" 
                      name="name"
                      placeholder="Enter item name"
                      style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                      required
                    />
                  </div>
                  
                  <div style={{marginBottom:'16px'}}>
                    <label style={{display:'block', marginBottom:'8px', fontWeight:'bold'}}>Description:</label>
                    <textarea 
                      name="description"
                      placeholder="Describe your item"
                      rows="3"
                      style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                      required
                    />
                  </div>
                  
                  <div style={{marginBottom:'16px'}}>
                    <label style={{display:'block', marginBottom:'8px', fontWeight:'bold'}}>Value ($):</label>
                    <input 
                      type="number" 
                      name="value"
                      step="0.01"
                      placeholder="0.00"
                      style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                      required
                    />
                  </div>
                  
                  <div style={{marginBottom:'16px'}}>
                    <label style={{display:'block', marginBottom:'8px', fontWeight:'bold'}}>Condition:</label>
                    <select name="condition" style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}} required>
                      <option value="">Select condition</option>
                      <option value="New">New</option>
                      <option value="Like New">Like New</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                    </select>
                  </div>
                  
                  <div style={{marginBottom:'16px'}}>
                    <label style={{display:'block', marginBottom:'8px', fontWeight:'bold'}}>Brand:</label>
                    <select name="brand" style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}} required>
                      <option value="">Select brand</option>
                      <option value="Aldo">Aldo</option>
                      <option value="Charles & Keith">Charles & Keith</option>
                      <option value="Zara">Zara</option>
                      <option value="H&M">H&M</option>
                      <option value="Forever 21">Forever 21</option>
                      <option value="JW Pei">JW Pei</option>
                      <option value="Brandon Blackwood">Brandon Blackwood</option>
                      <option value="Telfar">Telfar</option>
                      <option value="Tory Burch">Tory Burch</option>
                      <option value="Michael Kors">Michael Kors</option>
                      <option value="Coach">Coach</option>
                      <option value="Kate Spade">Kate Spade</option>
                      <option value="Marc Jacobs">Marc Jacobs</option>
                      <option value="Rebecca Minkoff">Rebecca Minkoff</option>
                      <option value="Mansur Gavriel">Mansur Gavriel</option>
                      <option value="Polène">Polène</option>
                      <option value="Strathberry">Strathberry</option>
                      <option value="Aspinal of London">Aspinal of London</option>
                      <option value="Hermès">Hermès</option>
                      <option value="True Religion">True Religion</option>
                      <option value="Juicy Couture">Juicy Couture</option>
                      <option value="Steve Madden">Steve Madden</option>
                      <option value="Chanel">Chanel</option>
                      <option value="Louis Vuitton">Louis Vuitton</option>
                      <option value="Gucci">Gucci</option>
                      <option value="Dior">Dior</option>
                      <option value="Prada">Prada</option>
                      <option value="Fendi">Fendi</option>
                      <option value="Celine">Celine</option>
                      <option value="Balenciaga">Balenciaga</option>
                      <option value="Bottega Veneta">Bottega Veneta</option>
                      <option value="Givenchy">Givenchy</option>
                      <option value="Saint Laurent (YSL)">Saint Laurent (YSL)</option>
                      <option value="Loewe">Loewe</option>
                      <option value="Valentino">Valentino</option>
                      <option value="Burberry">Burberry</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div style={{marginBottom:'16px'}}>
                    <label style={{display:'block', marginBottom:'8px', fontWeight:'bold'}}>Size:</label>
                    <select name="size" style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}} required>
                      <option value="">Select size</option>
                      <option value="Mini/Clutch">Mini/Clutch</option>
                      <option value="Small/Crossbody">Small/Crossbody</option>
                      <option value="Medium/Shoulder">Medium/Shoulder</option>
                      <option value="Large/Tote">Large/Tote</option>
                      <option value="Extra Large/Weekend">Extra Large/Weekend</option>
                    </select>
                  </div>
                  
                  <div style={{marginBottom:'16px'}}>
                    <label style={{display:'block', marginBottom:'8px', fontWeight:'bold'}}>Images (max 5):</label>
                    <input 
                      type="file" 
                      name="images"
                      accept="image/*"
                      multiple
                      style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                      required
                    />
                    <small style={{color:'#666', fontSize:'12px'}}>You can select up to 5 images at once</small>
                  </div>
                  
                  <button 
                    type="submit" 
                    className="btn" 
                    style={{background:'#28a745', color:'white', padding:'12px 24px', fontSize:'16px'}}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Uploading...' : 'Upload Item'}
                  </button>
                </form>
              </div>
              
              <div style={{marginTop:'20px', padding:'15px', background:'#e9ecef', borderRadius:'8px'}}>
                <h4 style={{margin:'0 0 10px 0', color:'#495057'}}>Upload Guidelines:</h4>
                <ul style={{margin:'0', paddingLeft:'20px', color:'#6c757d'}}>
                  <li>Provide clear, high-quality photos of your item</li>
                  <li>Be honest about the condition and any flaws</li>
                  <li>Set a fair market value for your item</li>
                  <li>Include detailed descriptions to help potential traders</li>
                </ul>
              </div>
            </div>
          )}

          {/* Trades Tab (Pending Trades) */}
          {activeTab === 'trades' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Pending Trades</h3>
                <button className="btn" onClick={loadDashboardData} disabled={isLoading}>Refresh</button>
              </div>
              <div className="trades-list">
                {pendingTrades && pendingTrades.length > 0 ? (
                  <div>
                    {pendingTrades.map((trade, index) => (
                      <div key={trade._id} className="trade-item" style={{border: '1px solid #ddd', padding: '15px', margin: '10px 0', borderRadius: '8px', background:'#fff'}}>
                        <div style={{
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                          gap: window.innerWidth <= 768 ? '12px' : '0'
                        }}>
                          <div style={{flex:1, minWidth: 0}}>
                            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
                              <h4 style={{margin:0}}>Trade #{index + 1}</h4>
                              <span style={{
                                padding:'4px 8px',
                                borderRadius:4,
                                fontSize:12,
                                fontWeight:'bold',
                                color:'#fff',
                                backgroundColor: trade.status === 'pending' ? '#ffc107' : 
                                                trade.status === 'expired' ? '#dc3545' : '#17a2b8'
                              }}>
                                {trade.status?.toUpperCase()}
                              </span>
                              {trade.expires_at && (
                                <span style={{fontSize:12, color:'#666'}}>
                                  Expires: {new Date(trade.expires_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            
                            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12, marginBottom:12}}>
                              <div>
                                <p style={{margin:'4px 0'}}><strong>Offered:</strong> {trade.offered_hat?.name || 'Unknown'}</p>
                                <p style={{margin:'4px 0'}}><strong>Requested:</strong> {trade.requested_hat?.name || 'Unknown'}</p>
                                <p style={{margin:'4px 0'}}><strong>Sister:</strong> {trade.from_user?.name || trade.to_user?.name || 'Unknown'}</p>
                                <p style={{margin:'4px 0'}}><strong>Start Date:</strong> {trade.start_date ? new Date(trade.start_date).toLocaleDateString() : 'Not set'}</p>
                                <p style={{margin:'4px 0'}}><strong>End Date:</strong> {trade.end_date ? new Date(trade.end_date).toLocaleDateString() : 'Not set'}</p>
                              </div>
                              <div>
                                <p style={{margin:'4px 0'}}><strong>Created:</strong> {new Date(trade.created_at).toLocaleDateString()}</p>
                                <p style={{margin:'4px 0'}}><strong>Delivery:</strong> {trade.delivery_method === 'pickup' ? '🚗 Pickup - View email for details' : '📦 Shipping - View email for details'}</p>
                                {trade.financial_breakdown && (
                                  <p style={{margin:'4px 0'}}><strong>Total Cost:</strong> ${(() => {
                                    // Calculate user-specific total cost
                                    const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
                                    const initiatorId = trade.hold_user_id;
                                    const youInitiated = initiatorId === currentUserId;
                                    
                                    // Determine which item the current user is borrowing
                                    const offered = trade.offered_hat;
                                    const requested = trade.requested_hat;
                                    const yourBorrowedValue = youInitiated ? Number(requested?.value || 0) : Number(offered?.value || 0);
                                    
                                    // Get fees from actual trade data
                                    const siteFee = Number(trade.financial_breakdown?.site_fee || 0);
                                    const shippingFees = Number(trade.financial_breakdown?.delivery_fees || 0);
                                    const totalFees = siteFee + shippingFees;
                                    
                                    // Return user-specific total (fees + hold amount)
                                    return (totalFees + yourBorrowedValue).toFixed(2);
                                  })()}</p>
                                )}
                              </div>
                            </div>

                            {/* Timeline */}
                            <div style={{marginBottom:12}}>
                              <h5 style={{fontSize:14, marginBottom:8, color:'#333'}}>Timeline</h5>
                              <div style={{maxHeight:'150px', overflowY:'auto', border:'1px solid #eee', borderRadius:4, padding:8}}>
                                {createTimelineEntry(trade).map((entry) => (
                                  <div key={entry.id} style={{marginBottom:8, paddingBottom:8, borderBottom:'1px solid #f0f0f0'}}>
                                    <div style={{display:'flex', alignItems:'center', gap:8}}>
                                      <span style={{fontSize:16}}>{entry.icon}</span>
                                      <div style={{flex:1}}>
                                        <div style={{fontSize:12, fontWeight:'bold'}}>
                                          {entry.user_name} {entry.description}
                                        </div>
                                        <div style={{fontSize:11, color:'#666'}}>
                                          {formatSmartTimestamp(entry.timestamp)}
                                        </div>
                                        {entry.message && (
                                          <div style={{fontSize:11, color:'#555', marginTop:4, fontStyle:'italic'}}>
                                            "{entry.message}"
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Action Required Indicator */}
                            <div style={{
                              background: trade.status === 'pending' ? '#fff3cd' : 
                                          trade.status === 'expired' ? '#f8d7da' : '#d1ecf1',
                              border: `1px solid ${trade.status === 'pending' ? '#ffeaa7' : 
                                                  trade.status === 'expired' ? '#f5c6cb' : '#bee5eb'}`,
                              padding:8,
                              borderRadius:4,
                              marginBottom:12
                            }}>
                              <div style={{fontSize:14, fontWeight:'bold', marginBottom:4}}>
                                {(() => {
                                  const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
                                  const isInitiator = trade.hold_user_id === currentUserId;
                                  const isModifier = trade.last_modified_by === currentUserId;
                                  
                                  if (trade.status === 'pending') {
                                    return isInitiator ? '⏳ Trade is waiting for response' : '⏳ Trade is waiting on your response';
                                  } else if (trade.status === 'expired') {
                                    return isInitiator ? '⏰ Trade expired - Cancel to get refund' : '⏰ Trade expired - You can reject';
                                  } else {
                                    return isModifier ? '📝 Modification sent, waiting for response' : '📝 Modification Required';
                                  }
                                })()}
                              </div>
                              <div style={{fontSize:12, color:'#666'}}>
                                {(() => {
                                  const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
                                  const isInitiator = trade.hold_user_id === currentUserId;
                                  const isModifier = trade.last_modified_by === currentUserId;
                                  
                                  if (trade.status === 'pending') {
                                    return isInitiator ? 'Waiting for the other party to respond' : 'Please accept or reject this trade offer';
                                  } else if (trade.status === 'expired') {
                                    return isInitiator ? 'Trade has expired - cancel to get your refund' : 'Trade has expired - you can reject this offer';
                                  } else {
                                    return isModifier ? 'Your modification is waiting for the other party\'s response' : 'This trade has been modified and requires your response';
                                  }
                                })()}
                              </div>
                            </div>
                          </div>
                          <div style={{
                            display:'flex', 
                            flexDirection: window.innerWidth <= 768 ? 'row' : 'column', 
                            gap:8, 
                            alignItems: window.innerWidth <= 768 ? 'stretch' : 'flex-end',
                            flexWrap: 'wrap'
                          }}>
                            <button className="btn" onClick={() => handleViewTradeDetails(trade)} style={{
                              fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                              padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                              flex: window.innerWidth <= 768 ? '1' : 'none',
                              minWidth: window.innerWidth <= 768 ? '0' : 'auto',
                              background: '#007bff',
                              color: '#fff'
                            }}>
                              📋 View Details
                            </button>
                            {(trade.status === 'pending' || trade.status === 'modified' || trade.status === 'expired') && (
                              <div style={{
                                display:'flex', 
                                gap:4, 
                                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                                flex: window.innerWidth <= 768 ? '1' : 'none'
                              }}>
                                {(() => {
                                  const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
                                  const isInitiator = trade.hold_user_id === currentUserId || trade.from_user_id === currentUserId;
                                  const isModifier = trade.last_modified_by === currentUserId;
                                  
                                  // User A (receiver) - show Accept/Reject for modified trades
                                  if (!isModifier && trade.status === 'modified') {
                                    return (
                                      <>
                                        <button 
                                          className="btn" 
                                          onClick={() => handleTradeAction(trade._id, 'accept')}
                                          disabled={isLoading}
                                          style={{
                                            background:'#28a745', 
                                            color:'#fff', 
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                            padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                                            flex: window.innerWidth <= 768 ? '1' : 'none'
                                          }}
                                        >
                                          ✓ Approve Modified Trade
                                        </button>
                                        <button 
                                          className="btn" 
                                          onClick={() => handleTradeAction(trade._id, 'reject')}
                                          disabled={isLoading}
                                          style={{
                                            background:'#dc3545', 
                                            color:'#fff', 
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                            padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                                            flex: window.innerWidth <= 768 ? '1' : 'none'
                                          }}
                                        >
                                          ✗ Deny Modified Trade
                                        </button>
                                      </>
                                    );
                                  }
                                  
                                  // User B (modifier) - show Cancel after modification
                                  if (isModifier && trade.status === 'modified') {
                                    return (
                                      <>
                                        <div style={{
                                          padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px', 
                                          background:'#fff3cd', 
                                          borderRadius:4, 
                                          fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                          color:'#856404', 
                                          display:'flex', 
                                          alignItems:'center', 
                                          gap:4,
                                          flex: window.innerWidth <= 768 ? '1' : 'none'
                                        }}>
                                          📝 Modification sent, waiting for response...
                                        </div>
                                        <button 
                                          className="btn" 
                                          onClick={() => handleTradeAction(trade._id, 'cancel')}
                                          disabled={isLoading}
                                          style={{
                                            background:'#ffc107', 
                                            color:'#000', 
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                            padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                                            flex: window.innerWidth <= 768 ? '1' : 'none'
                                          }}
                                        >
                                          ⚠ Cancel Modified Trade
                                        </button>
                                      </>
                                    );
                                  }
                                  
                                  // Original initiator waiting for response
                                  if (isInitiator && trade.status === 'pending') {
                                    return (
                                      <>
                                        <div style={{
                                          padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px', 
                                          background:'#e9ecef', 
                                          borderRadius:4, 
                                          fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                          color:'#6c757d', 
                                          display:'flex', 
                                          alignItems:'center', 
                                          gap:4,
                                          flex: window.innerWidth <= 768 ? '1' : 'none'
                                        }}>
                                          ⏳ Waiting for response...
                                        </div>
                                        <button 
                                          className="btn" 
                                          onClick={() => handleTradeAction(trade._id, 'cancel')}
                                          disabled={isLoading}
                                          style={{
                                            background:'#ffc107', 
                                            color:'#000', 
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                            padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                                            flex: window.innerWidth <= 768 ? '1' : 'none'
                                          }}
                                        >
                                          ⚠ Cancel
                                        </button>
                                      </>
                                    );
                                  }
                                  
                                  // Expired trade - initiator can cancel
                                  if (isInitiator && trade.status === 'expired') {
                                    return (
                                      <>
                                        <div style={{
                                          padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px', 
                                          background:'#f8d7da', 
                                          borderRadius:4, 
                                          fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                          color:'#721c24', 
                                          display:'flex', 
                                          alignItems:'center', 
                                          gap:4,
                                          flex: window.innerWidth <= 768 ? '1' : 'none'
                                        }}>
                                          ⏰ Trade expired - Cancel to get refund
                                        </div>
                                        <button 
                                          className="btn" 
                                          onClick={() => handleTradeAction(trade._id, 'cancel')}
                                          disabled={isLoading}
                                          style={{
                                            background:'#ffc107', 
                                            color:'#000', 
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                            padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                                            flex: window.innerWidth <= 768 ? '1' : 'none'
                                          }}
                                        >
                                          ⚠ Cancel
                                        </button>
                                      </>
                                    );
                                  }
                                  
                                  // Original receiver - show Accept/Reject/Modify
                                  if (!isInitiator && trade.status === 'pending') {
                                    return (
                                      <>
                                        <button 
                                          className="btn" 
                                          onClick={() => handleTradeAction(trade._id, 'accept')}
                                          disabled={isLoading}
                                          style={{
                                            background:'#28a745', 
                                            color:'#fff', 
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                            padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                                            flex: window.innerWidth <= 768 ? '1' : 'none'
                                          }}
                                        >
                                          ✓ Accept
                                        </button>
                                        <button 
                                          className="btn" 
                                          onClick={() => handleTradeAction(trade._id, 'reject')}
                                          disabled={isLoading}
                                          style={{
                                            background:'#dc3545', 
                                            color:'#fff', 
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                            padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                                            flex: window.innerWidth <= 768 ? '1' : 'none'
                                          }}
                                        >
                                          ✗ Reject
                                        </button>
                                        <button 
                                          className="btn" 
                                          onClick={() => openModifyModal(trade)}
                                          disabled={isLoading}
                                          style={{
                                            background:'#6f42c1', 
                                            color:'#fff', 
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                            padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                                            flex: window.innerWidth <= 768 ? '1' : 'none'
                                          }}
                                        >
                                          ✏️ Modify
                                        </button>
                                      </>
                                    );
                                  }
                                  
                                  // Expired trade - receiver can only reject
                                  if (!isInitiator && trade.status === 'expired') {
                                    return (
                                      <>
                                        <button 
                                          className="btn" 
                                          onClick={() => handleTradeAction(trade._id, 'reject')}
                                          disabled={isLoading}
                                          style={{
                                            background:'#dc3545', 
                                            color:'#fff', 
                                            fontSize: window.innerWidth <= 768 ? '11px' : '12px', 
                                            padding: window.innerWidth <= 768 ? '8px 10px' : '6px 12px',
                                            flex: window.innerWidth <= 768 ? '1' : 'none'
                                          }}
                                        >
                                          ✗ Reject
                                        </button>
                                      </>
                                    );
                                  }
                                  
                                  return null;
                                })()}
                              </div>
                            )}
                            
                            {/* Trade Messaging */}
                            <div style={{marginTop:12, padding:8, background:'#f8f9fa', borderRadius:4}}>
                              <div style={{fontSize:12, fontWeight:'bold', marginBottom:6}}>💬 Messages</div>
                              <div style={{maxHeight:'100px', overflowY:'auto', marginBottom:8}}>
                                {(tradeMessages[trade._id] || []).map((msg, index) => (
                                  <div key={`${trade._id}-${index}`} style={{fontSize:11, marginBottom:4, padding:4, background:'white', borderRadius:3}}>
                                    <strong>{msg.user_name}:</strong> {msg.message}
                                    <div style={{fontSize:10, color:'#666'}}>
                                      {formatSmartTimestamp(msg.timestamp)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div style={{display:'flex', gap:4}}>
                                <input 
                                  type="text" 
                                  placeholder="Type a message..."
                                  value={newMessages[trade._id] || ''}
                                  onChange={(e) => setNewMessages(prev => ({...prev, [trade._id]: e.target.value}))}
                                  style={{flex:1, padding:4, fontSize:11, border:'1px solid #ccc', borderRadius:3}}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      sendTradeMessage(trade._id, newMessages[trade._id] || '');
                                    }
                                  }}
                                />
                                <button 
                                  className="btn" 
                                  onClick={() => sendTradeMessage(trade._id, newMessages[trade._id] || '')}
                                  disabled={sendingMessage || !(newMessages[trade._id] || '').trim()}
                                  style={{fontSize:11, padding:'4px 8px'}}
                                >
                                  Send
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign:'center', padding:'40px 20px', color:'#666'}}>
                    <i className="fas fa-clock" style={{fontSize:'48px', marginBottom:'16px', opacity:0.3}}></i>
                    <p style={{fontSize:'18px', marginBottom:'8px'}}>No Pending Trades</p>
                    <p style={{fontSize:'14px'}}>Trades awaiting your response will appear here</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Active Trades Tab */}
          {activeTab === 'active-trades' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Active Trades</h3>
                <button className="btn" onClick={loadDashboardData} disabled={isLoading}>Refresh</button>
              </div>
              <div className="trades-list">
                {activeTrades && activeTrades.length > 0 ? (
                  <div>
                    <div style={{background:'#e8f5e8', padding:12, borderRadius:8, marginBottom:20, border:'1px solid #28a745'}}>
                      <h4 style={{color:'#28a745', margin:'0 0 8px 0'}}>✅ {activeTrades.length} Active Trade{activeTrades.length !== 1 ? 's' : ''}</h4>
                      <p style={{margin:0, fontSize:14, color:'#155724'}}>These trades are in progress and require your attention</p>
                    </div>
                    {activeTrades.map((trade, index) => (
                      <div key={trade._id} style={{border: '1px solid #28a745', padding: '15px', margin: '10px 0', borderRadius: '8px', background:'#f8fff8'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                          <div style={{flex:1}}>
                            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
                              <h5 style={{margin:0, color:'#28a745'}}>Active Trade #{index + 1}</h5>
                              <span style={{
                                padding:'4px 8px',
                                borderRadius:4,
                                fontSize:12,
                                fontWeight:'bold',
                                color:'#fff',
                                backgroundColor: '#28a745'
                              }}>
                                {trade.status?.toUpperCase()}
                              </span>
                            </div>
                            
                            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12, marginBottom:12}}>
                              <div>
                                <p style={{margin:'4px 0'}}><strong>Offered:</strong> {trade.offered_hat?.name || 'Unknown'}</p>
                                <p style={{margin:'4px 0'}}><strong>Requested:</strong> {trade.requested_hat?.name || 'Unknown'}</p>
                                <p style={{margin:'4px 0'}}><strong>Sister:</strong> {trade.from_user?.name || trade.to_user?.name || 'Unknown'}</p>
                                <p style={{margin:'4px 0'}}><strong>Start Date:</strong> {trade.start_date ? new Date(trade.start_date).toLocaleDateString() : 'Not set'}</p>
                                <p style={{margin:'4px 0'}}><strong>End Date:</strong> {trade.end_date ? new Date(trade.end_date).toLocaleDateString() : 'Not set'}</p>
                              </div>
                              <div>
                                <p style={{margin:'4px 0'}}><strong>Created:</strong> {new Date(trade.created_at).toLocaleDateString()}</p>
                                <p style={{margin:'4px 0'}}><strong>Accepted:</strong> {trade.accepted_at ? new Date(trade.accepted_at).toLocaleDateString() : 'N/A'}</p>
                                <p style={{margin:'4px 0'}}><strong>Delivery:</strong> {trade.delivery_method === 'pickup' ? '🚗 Pickup - View email for details' : '📦 Shipping - View email for details'}</p>
                              </div>
                            </div>

                            {/* Progress Indicators */}
                            <div style={{background:'#fff', padding:12, borderRadius:6, marginBottom:12, border:'1px solid #e9ecef'}}>
                              <div style={{fontSize:14, fontWeight:'bold', marginBottom:8}}>Trade Progress</div>
                              {(() => {
                                const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
                                const isUser1 = trade.user1_id === currentUserId;
                                const userReceived = isUser1 ? trade.user1_received_at : trade.user2_received_at;
                                const sisterReceived = isUser1 ? trade.user2_received_at : trade.user1_received_at;
                                
                                return (
                                  <>
                                    <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
                                      <span style={{color: userReceived ? '#28a745' : '#6c757d'}}>
                                        {userReceived ? '✅' : '⏳'} You Received
                                      </span>
                                      {userReceived && (
                                        <span style={{fontSize:12, color:'#666'}}>
                                          {new Date(userReceived).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
                                      <span style={{color: sisterReceived ? '#28a745' : '#6c757d'}}>
                                        {sisterReceived ? '✅' : '⏳'} Sister Received
                                      </span>
                                      {sisterReceived && (
                                        <span style={{fontSize:12, color:'#666'}}>
                                          {new Date(sisterReceived).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                    {/* Admin Override Notice */}
                                    {trade.admin_override && (
                                      <div style={{background:'#fff3cd', border:'1px solid #ffeaa7', borderRadius:4, padding:8, marginTop:8, fontSize:12}}>
                                        <div style={{color:'#856404', fontWeight:'bold', marginBottom:4}}>⚠️ Admin Override</div>
                                        <div style={{color:'#856404'}}>
                                          Admin has overridden tracking confirmation for this trade.
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                              {(() => {
                                const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
                                const isUser1 = trade.user1_id === currentUserId;
                                const userReturned = isUser1 ? trade.user1_returned_at : trade.user2_returned_at;
                                const sisterReturned = isUser1 ? trade.user2_returned_at : trade.user1_returned_at;
                                
                                return (
                                  <>
                                    <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
                                      <span style={{color: userReturned ? '#28a745' : '#6c757d'}}>
                                        {userReturned ? '✅' : '⏳'} You Returned
                                      </span>
                                      {userReturned && (
                                        <span style={{fontSize:12, color:'#666'}}>
                                          {new Date(userReturned).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{display:'flex', alignItems:'center', gap:8}}>
                                      <span style={{color: sisterReturned ? '#28a745' : '#6c757d'}}>
                                        {sisterReturned ? '✅' : '⏳'} Sister Returned
                                      </span>
                                      {sisterReturned && (
                                        <span style={{fontSize:12, color:'#666'}}>
                                          {new Date(sisterReturned).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>

                            {/* Financial Summary */}
                            {trade.financials && (
                              <div style={{background:'#f0f8ff', padding:8, borderRadius:4, marginBottom:12}}>
                                <div style={{fontSize:12, fontWeight:'bold', marginBottom:4}}>Financial Summary</div>
                                <div style={{fontSize:12, color:'#666'}}>
                                  Total Fees: ${trade.financials.total_fee_due_on_accept?.toFixed(2) || '0.00'} | 
                                  Current Balance: ${trade.financials.ending_balance?.toFixed(2) || '0.00'}
                                </div>
                              </div>
                            )}

                            {/* Shipping Labels - Only show for shipping trades */}
                            {trade.delivery_method === 'shipping' && trade.status === 'accepted' && (
                              <div style={{background:'#fff3cd', padding:12, borderRadius:6, marginBottom:12, border:'1px solid #ffeaa7'}}>
                                <div style={{fontSize:14, fontWeight:'bold', marginBottom:8, color:'#856404'}}>📦 Shipping Labels</div>
                                <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                                  <button
                                    className="btn"
                                    style={{backgroundColor: '#007bff', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px'}}
                                    onClick={async () => {
                                      try {
                                        const res = await tradesAPI.getOutboundLabel(trade._id);
                                        if (res && res.label_url) {
                                          window.open(res.label_url, '_blank');
                                          setSuccess('Outbound label opened for printing');
                                        } else {
                                          setError('Outbound label not available yet');
                                        }
                                      } catch (e) {
                                        setError(e.message || 'Failed to retrieve outbound label');
                                      }
                                    }}
                                    title="Print outbound label"
                                  >
                                    📦 Print Outbound Label
                                  </button>
                                  <button
                                    className="btn"
                                    style={{backgroundColor: '#28a745', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px'}}
                                    onClick={async () => {
                                      try {
                                        const res = await tradesAPI.getReturnLabel(trade._id);
                                        if (res && res.label_url) {
                                          window.open(res.label_url, '_blank');
                                          setSuccess('Return label opened for printing');
                                        } else {
                                          setError('Return label not available yet');
                                        }
                                      } catch (e) {
                                        setError(e.message || 'Failed to retrieve return label');
                                      }
                                    }}
                                    title="Print return label"
                                  >
                                    📦 Print Return Label
                                  </button>
                                  <button
                                    className="btn"
                                    style={{backgroundColor: '#6f42c1', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px'}}
                                    onClick={() => openTrackingModal(trade._id)}
                                    title="View all tracking information for this trade"
                                  >
                                    📍 Track Packages
                                  </button>
                                </div>
                                <div style={{fontSize:11, color:'#856404', marginTop:4}}>
                                  Labels have been purchased and are ready for printing. Check your email for direct links.
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end'}}>
                            <button className="btn" onClick={() => handleViewTradeDetails(trade)} style={{fontSize:12, padding:'6px 12px', background:'#28a745', color:'#fff'}}>
                              📋 View Details
                            </button>
                            {(() => {
                              const existingDispute = myDisputes.find(d => String(d.trade_id) === String(trade._id));
                              if (existingDispute) {
                                return (
                                  <button 
                                    className="btn" 
                                    disabled
                                    style={{background:'#6c757d', color:'#fff', fontSize:12, padding:'6px 12px'}}
                                  >
                                    ⚠️ Disputed
                                  </button>
                                );
                              }
                              return (
                                <button 
                                  className="btn" 
                                  onClick={() => handleCreateDispute(trade._id)}
                                  disabled={isLoading}
                                  style={{background:'#ffc107', color:'#000', fontSize:12, padding:'6px 12px'}}
                                >
                                  ⚠️ Dispute
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign:'center', padding:'40px 20px', color:'#666'}}>
                    <i className="fas fa-play" style={{fontSize:'48px', marginBottom:'16px', opacity:0.3}}></i>
                    <p style={{fontSize:'18px', marginBottom:'8px'}}>No Active Trades</p>
                    <p style={{fontSize:'14px'}}>Accepted trades in progress will appear here</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Trade History Tab */}
          {activeTab === 'trade-history' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Trade History</h3>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <label style={{fontSize:'14px', fontWeight:'bold'}}>Filter by:</label>
                    <select 
                      value={tradeHistoryFilter}
                      onChange={(e) => setTradeHistoryFilter(e.target.value)}
                      style={{padding:'4px 8px', border:'1px solid #ccc', borderRadius:'4px', fontSize:'12px'}}
                    >
                      <option value="all">All</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="rejected">Rejected</option>
                      <option value="disputed">Disputed</option>
                    </select>
                  </div>
                <button className="btn" onClick={loadDashboardData} disabled={isLoading}>Refresh</button>
                </div>
              </div>
              <div className="trades-list">
                {tradeHistory && tradeHistory.length > 0 ? (
                  <div>
                    {getFilteredTradeHistory().map((trade, index) => (
                      <div key={trade._id} className="trade-item" style={{border: '1px solid #ddd', padding: '15px', margin: '10px 0', borderRadius: '8px', background:'#fff'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                          <div style={{flex:1}}>
                            <h4 style={{margin:'0 0 8px 0'}}>Trade #{index + 1}</h4>
                            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12, marginBottom:12}}>
                              <div>
                                <p style={{margin:'4px 0'}}><strong>Status:</strong> 
                                  <span style={{
                                    color: trade.status === 'completed' ? '#28a745' : 
                                           trade.status === 'rejected' ? '#dc3545' : '#ffc107',
                                    fontWeight:'bold',
                                    marginLeft:4
                                  }}>
                                    {trade.status?.toUpperCase()}
                                  </span>
                                </p>
                                <p style={{margin:'4px 0'}}><strong>Offered:</strong> {trade.offered_hat?.name || 'Unknown'}</p>
                                <p style={{margin:'4px 0'}}><strong>Requested:</strong> {trade.requested_hat?.name || 'Unknown'}</p>
                              </div>
                              <div>
                                <p style={{margin:'4px 0'}}><strong>Sister:</strong> {trade.from_user?.name || trade.to_user?.name || 'Unknown'}</p>
                                <p style={{margin:'4px 0'}}><strong>Created:</strong> {new Date(trade.created_at).toLocaleDateString()}</p>
                                {trade.completed_at && (
                                  <p style={{margin:'4px 0'}}><strong>Completed:</strong> {new Date(trade.completed_at).toLocaleDateString()}</p>
                                )}
                              </div>
                            </div>
                            
                            {/* Financial Summary */}
                            {trade.financials && (
                              <div style={{background:'#f8f9fa', padding:8, borderRadius:4, marginBottom:12}}>
                                <div style={{fontSize:12, fontWeight:'bold', marginBottom:4}}>Financial Summary</div>
                                <div style={{fontSize:12, color:'#666'}}>
                                  Total Fees: ${trade.financials.total_fee_due_on_accept?.toFixed(2) || '0.00'} | 
                                  Balance Impact: ${trade.financials.ending_balance?.toFixed(2) || '0.00'}
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end'}}>
                            <button className="btn" onClick={() => handleViewTradeDetails(trade)} style={{fontSize:12, padding:'6px 12px'}}>
                              📋 View Details
                            </button>
                            {trade.status === 'completed' && (
                              <button 
                                className="btn" 
                                onClick={() => handleOpenRatingModal(trade._id)}
                                style={{background:'#ffc107', color:'#000', fontSize:12, padding:'6px 12px'}}
                              >
                                ⭐ Rate Trade
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign:'center', padding:'40px 20px', color:'#666'}}>
                    <i className="fas fa-history" style={{fontSize:'48px', marginBottom:'16px', opacity:0.3}}></i>
                    <p style={{fontSize:'18px', marginBottom:'8px'}}>No Trade History</p>
                    <p style={{fontSize:'14px'}}>Your completed trades will appear here</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Disputes Tab */}
          {activeTab === 'disputes' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Disputes & Issues</h3>
                <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
                  <button 
                    className={`btn btn-small ${showClosedDisputes ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={() => setShowClosedDisputes(!showClosedDisputes)}
                  >
                    {showClosedDisputes ? 'Hide Closed' : 'Show Closed'}
                  </button>
                  <button className="btn" onClick={loadDashboardData} disabled={isLoading}>Refresh</button>
                </div>
              </div>
              <div className="disputes-list">
                {myDisputes && myDisputes.length > 0 ? (
                  <div>
                    {myDisputes.filter(dispute => {
                      return showClosedDisputes || !['resolved', 'closed'].includes(dispute.status?.toLowerCase());
                    }).sort((a, b) => {
                      // Sort by status: open/new first, then resolved
                      const statusOrder = { 'open': 0, 'new': 0, 'pending': 1, 'resolved': 2, 'closed': 2 };
                      const aOrder = statusOrder[a.status?.toLowerCase()] ?? 3;
                      const bOrder = statusOrder[b.status?.toLowerCase()] ?? 3;
                      return aOrder - bOrder;
                    }).map((dispute, index) => (
                      <div key={dispute._id} className="dispute-item" style={{border: '1px solid #dc3545', padding: '15px', margin: '10px 0', borderRadius: '8px', background:'#fff5f5'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                          <div style={{flex:1}}>
                            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
                              <h4 style={{margin:0, color:'#dc3545'}}>Dispute #{index + 1}</h4>
                              <span style={{
                                padding:'4px 8px',
                                borderRadius:4,
                                fontSize:12,
                                fontWeight:'bold',
                                color:'#fff',
                                backgroundColor: dispute.status === 'resolved' ? '#28a745' : 
                                               dispute.status === 'closed' ? '#6c757d' : '#dc3545'
                              }}>
                                {dispute.status?.toUpperCase() || 'OPEN'}
                              </span>
                            </div>
                            
                            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12, marginBottom:12}}>
                              <div>
                                <p style={{margin:'4px 0'}}><strong>Trade ID:</strong> {dispute.trade_id}</p>
                                <p style={{margin:'4px 0'}}><strong>Reason:</strong> {dispute.reason}</p>
                                <p style={{margin:'4px 0'}}><strong>Status:</strong> {dispute.status}</p>
                              </div>
                              <div>
                                <p style={{margin:'4px 0'}}><strong>Created:</strong> {new Date(dispute.created_at).toLocaleDateString()}</p>
                                {dispute.resolved_at && (
                                  <p style={{margin:'4px 0'}}><strong>Resolved:</strong> {new Date(dispute.resolved_at).toLocaleDateString()}</p>
                                )}
                              </div>
                            </div>

                            {dispute.description && (
                              <div style={{background:'#f8f9fa', padding:8, borderRadius:4, marginBottom:12}}>
                                <div style={{fontSize:12, fontWeight:'bold', marginBottom:4}}>Description</div>
                                <p style={{margin:0, fontSize:14}}>{dispute.description}</p>
                              </div>
                            )}

                            {dispute.admin_response && (
                              <div style={{background:'#e8f5e8', padding:8, borderRadius:4, marginBottom:12}}>
                                <div style={{fontSize:12, fontWeight:'bold', marginBottom:4}}>Admin Response</div>
                                <p style={{margin:0, fontSize:14}}>{dispute.admin_response}</p>
                              </div>
                            )}
                          </div>
                          <div style={{display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end'}}>
                            <button className="btn" onClick={() => handleViewTradeDetails({_id: dispute.trade_id})} style={{fontSize:12, padding:'6px 12px'}}>
                              📋 View Trade
                            </button>
                            {dispute.status === 'open' && (
                              <button 
                                className="btn" 
                                onClick={() => {
                                  // Open messaging modal for dispute communication
                                  setSelectedDisputeForMessaging(dispute._id);
                                  setShowDisputeMessagingModal(true);
                                  loadDisputeMessages(dispute._id);
                                }} 
                                style={{background:'#17a2b8', color:'#fff', fontSize:12, padding:'6px 12px'}}
                              >
                                💬 Message
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign:'center', padding:'40px 20px', color:'#666'}}>
                    <i className="fas fa-exclamation-triangle" style={{fontSize:'48px', marginBottom:'16px', opacity:0.3}}></i>
                    <p style={{fontSize:'18px', marginBottom:'8px'}}>No Disputes</p>
                    <p style={{fontSize:'14px'}}>You haven't created any disputes</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Reviews & Ratings</h3>
                <button className="btn" onClick={loadDashboardData} disabled={isLoading}>Refresh</button>
              </div>
              
              {/* User's Overall Rating Summary */}
              <div style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:12, padding:20, marginBottom:20, boxShadow:'0 2px 4px rgba(0,0,0,0.1)'}}>
                <div style={{display:'flex', alignItems:'center', gap:16}}>
                  <div style={{fontSize:32}}>🏆</div>
                  <div>
                    <h4 style={{margin:'0 0 8px 0', color:'#333'}}>Your Overall Rating</h4>
                    <div style={{display:'flex', alignItems:'center', gap:12}}>
                      <span style={{fontSize:24, fontWeight:'bold', color:'#28a745'}}>
                        {receivedReviews.length > 0 ? 
                          (receivedReviews.reduce((sum, r) => sum + r.rating, 0) / receivedReviews.length).toFixed(2) : 
                          '0.00'
                        } ⭐
                      </span>
                      <span style={{fontSize:16, color:'#666'}}>
                        ({receivedReviews.length} reviews)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Reviews Given */}
              <div style={{marginBottom:30}}>
                <h4 style={{margin:'0 0 16px 0', color:'#333'}}>Reviews You've Given</h4>
                <div className="reviews-list">
                  {myReviews && myReviews.length > 0 ? (
                    <div>
                      {myReviews.map((review, index) => (
                        <div key={review._id} className="review-item" style={{border: '1px solid #ddd', padding: '15px', margin: '10px 0', borderRadius: '8px', background:'#fff'}}>
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                            <div style={{flex:1}}>
                              <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
                                <h5 style={{margin:0}}>Review #{index + 1}</h5>
                                <div style={{display:'flex', gap:2}}>
                                  {[1,2,3,4,5].map(star => (
                                    <span key={star} style={{color: star <= review.rating ? '#ffc107' : '#ddd', fontSize:'16px'}}>
                                      ★
                                    </span>
                                  ))}
                                </div>
                                <span style={{fontSize:14, fontWeight:'bold', color:'#28a745'}}>
                                  {review.rating}/5
                                </span>
                              </div>
                              <p style={{margin:'4px 0', fontSize:14}}><strong>For:</strong> {review.reviewed_user?.name || review.reviewed_user?.username || 'Unknown User'}</p>
                              {review.comment && (
                                <div style={{background:'#f8f9fa', padding:8, borderRadius:4, marginTop:8}}>
                                  <p style={{margin:0, fontSize:14, fontStyle:'italic'}}>"{review.comment}"</p>
                                </div>
                              )}
                              <p style={{margin:'8px 0 0 0', fontSize:12, color:'#666'}}>
                                <strong>Created:</strong> {new Date(review.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{textAlign:'center', padding:'20px', color:'#666', background:'#f8f9fa', borderRadius:'8px'}}>
                      <i className="fas fa-star" style={{fontSize:'32px', marginBottom:'8px', opacity:0.3}}></i>
                      <p style={{margin:0}}>No reviews given yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Reviews Received */}
              <div>
                <h4 style={{margin:'0 0 16px 0', color:'#333'}}>Reviews You've Received</h4>
                <div className="reviews-list">
                  {receivedReviews && receivedReviews.length > 0 ? (
                    <div>
                      {receivedReviews.map((review, index) => (
                        <div key={review._id} className="review-item" style={{border: '1px solid #ddd', padding: '15px', margin: '10px 0', borderRadius: '8px', background:'#fff'}}>
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                            <div style={{flex:1}}>
                              <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
                                <h5 style={{margin:0}}>Review #{index + 1}</h5>
                                <div style={{display:'flex', gap:2}}>
                                  {[1,2,3,4,5].map(star => (
                                    <span key={star} style={{color: star <= review.rating ? '#ffc107' : '#ddd', fontSize:'16px'}}>
                                      ★
                                    </span>
                                  ))}
                                </div>
                                <span style={{fontSize:14, fontWeight:'bold', color:'#28a745'}}>
                                  {review.rating}/5
                                </span>
                              </div>
                              <p style={{margin:'4px 0', fontSize:14}}><strong>From:</strong> {review.reviewer?.name || 'Unknown User'}</p>
                              {review.comment && (
                                <div style={{background:'#f8f9fa', padding:8, borderRadius:4, marginTop:8}}>
                                  <p style={{margin:0, fontSize:14, fontStyle:'italic'}}>"{review.comment}"</p>
                                </div>
                              )}
                              <p style={{margin:'4px 0', fontSize:12, color:'#666'}}>
                                <strong>Created:</strong> {new Date(review.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{textAlign:'center', padding:'20px', color:'#666', background:'#f8f9fa', borderRadius:'8px'}}>
                      <i className="fas fa-star" style={{fontSize:'32px', marginBottom:'8px', opacity:0.3}}></i>
                      <p style={{margin:0}}>No reviews received yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Messages</h3>
                <button className="btn" onClick={loadDashboardData} disabled={isLoading}>Refresh</button>
              </div>
              
              {/* Send Message to Admin Form */}
              <div style={{marginBottom: '20px', padding: '20px', background: '#fff', borderRadius: '8px', border: '1px solid #dee2e6'}}>
                <h4 style={{margin:'0 0 15px 0'}}>Message Admin</h4>
                <textarea
                  value={newUserMessage}
                  onChange={(e) => setNewUserMessage(e.target.value)}
                  placeholder="Type your message here. Ask a question or send a message to support."
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    marginBottom: '10px'
                  }}
                  disabled={isLoading}
                />
                <button
                  onClick={sendUserMessage}
                  disabled={isLoading || !newUserMessage.trim()}
                  className="btn"
                  style={{background: '#007bff', color: '#fff'}}
                >
                  {isLoading ? 'Sending...' : 'Send Message'}
                </button>
              </div>

              {/* Conversation thread (you ↔ admin; includes older one-way admin notices) */}
              {userMessages && userMessages.length > 0 && (
                <div style={{marginBottom: '20px'}}>
                  <h4 style={{margin:'0 0 15px 0'}}>Conversation Thread</h4>
                  <div style={{maxHeight: '500px', overflowY: 'auto', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
                    {[...userMessages].sort((a, b) => new Date(b.timestamp || b.created_at || 0) - new Date(a.timestamp || a.created_at || 0)).map((msg, index) => (
                      <div
                        key={msg._id || index}
                        style={{
                          marginBottom: '15px',
                          padding: '12px',
                          borderRadius: '8px',
                          background: msg.sender === 'user' ? '#e3f2fd' : '#fff3cd',
                          borderLeft: `4px solid ${msg.sender === 'user' ? '#2196f3' : '#ffc107'}`,
                          alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '5px'}}>
                          <strong style={{color: msg.sender === 'user' ? '#1976d2' : '#f57c00'}}>
                            {msg.sender === 'user' ? 'You' : 'Admin'}
                          </strong>
                          <span style={{fontSize: '12px', color: '#666'}}>
                            {new Date(msg.timestamp || msg.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p style={{margin: 0, whiteSpace: 'pre-wrap'}}>{msg.message || msg.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Facebook Link */}
              <div style={{marginBottom: '20px', padding: '15px', background: '#f0f8ff', borderRadius: '8px', border: '1px solid #d4e6f1'}}>
                <a 
                  href="https://www.facebook.com/groups/ooowap" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#1877f2',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}
                >
                  <span>💬</span>
                  <span>Chat with other sisters</span>
                </a>
              </div>
            </div>
          )}

          {/* Balance Tab */}
          {activeTab === 'balance' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Balance & Transactions</h3>
                <div className="balance-controls" style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                  <div style={{marginBottom: '12px', padding: '8px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px'}}>
                    <div style={{fontSize: '12px', color: '#856404', fontWeight: 'bold', marginBottom: '4px'}}>
                      💳 Payment Method Notice
                    </div>
                    <div style={{fontSize: '11px', color: '#856404'}}>
                      Only debit cards are accepted. Credit cards will be automatically rejected.
                    </div>
                  </div>
                  <div className="deposit-section" style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
                    <input 
                      type="number" 
                      placeholder="Amount" 
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      style={{padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '100px'}}
                      min="5"
                      step="0.01"
                    />
                    <button className="btn" onClick={handleDeposit} disabled={isLoading} style={{backgroundColor: '#28a745', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px'}}>
                      + Stripe Deposit
                    </button>
                    <button className="btn" onClick={() => setShowManualDeposit(true)} style={{backgroundColor: '#17a2b8', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', marginLeft: '4px'}}>
                      Zelle or CashApp Deposit
                    </button>
                  </div>
                  <div className="withdraw-section" style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
                    <button className="btn" onClick={() => setShowWithdrawal(true)} style={{backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px'}}>
                      Zelle or Cashapp Withdraw
                    </button>
                  </div>
                  <button className="btn" onClick={loadDashboardData} disabled={isLoading}>Refresh</button>
                </div>
              </div>
              <div className="balance-info">
                {/* Fund Summary */}
                <div style={{marginBottom: '24px'}}>
                  <div style={{background: '#28a745', color: 'white', padding: '20px', borderRadius: '8px', textAlign: 'center', width: '100%'}}>
                    <div style={{fontSize: '24px', fontWeight: 'bold', marginBottom: '4px'}}>${userBalance.toFixed(2)}</div>
                    <div style={{fontSize: '14px', opacity: 0.9}}>All funds</div>
                  </div>
                </div>

                {/* Pending Deposits Section */}
                {manualDeposits && manualDeposits.filter(deposit => deposit.status === 'pending').length > 0 && (
                  <div style={{marginBottom: '24px'}}>
                    <h4 style={{margin: '0 0 16px 0', color: '#333'}}>Pending Deposits</h4>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                      {manualDeposits.filter(deposit => deposit.status === 'pending').map((deposit) => (
                        <div key={deposit._id} style={{border: '1px solid #f59e0b', padding: '16px', borderRadius: '8px', background: '#fef3c7'}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                            <div style={{flex: 1}}>
                              <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px'}}>
                                <h5 style={{margin: 0, color: '#92400e'}}>Manual Deposit</h5>
                                <span style={{padding: '4px 8px', backgroundColor: '#f59e0b', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold'}}>
                                  {deposit.status.toUpperCase()}
                                </span>
                              </div>
                              <div style={{color: '#92400e', fontSize: '14px'}}>
                                <p style={{margin: '4px 0'}}><strong>Amount:</strong> ${deposit.amount}</p>
                                <p style={{margin: '4px 0'}}><strong>Method:</strong> {deposit.payment_method === 'cashapp' ? 'Cash App' : 'Zelle'}</p>
                                <p style={{margin: '4px 0'}}><strong>Reference:</strong> #{deposit.reference_number}</p>
                                {deposit.reference_note && (
                                  <p style={{margin: '4px 0'}}><strong>Note:</strong> {deposit.reference_note}</p>
                                )}
                                <p style={{margin: '4px 0', fontSize: '12px', fontStyle: 'italic'}}>
                                  <strong>Submitted:</strong> {new Date(deposit.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Withdrawals Section */}
                {pendingWithdrawals && pendingWithdrawals.length > 0 && (
                  <div style={{marginBottom: '24px'}}>
                    <h4 style={{margin: '0 0 16px 0', color: '#333'}}>Pending Withdrawals</h4>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                      {pendingWithdrawals.map((withdrawal) => (
                        <div key={withdrawal._id} style={{border: '1px solid #dc3545', padding: '16px', borderRadius: '8px', background: '#f8d7da'}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                            <div style={{flex: 1}}>
                              <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px'}}>
                                <h5 style={{margin: 0, color: '#721c24'}}>Withdrawal Request</h5>
                                <span style={{padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold'}}>
                                  {withdrawal.status.toUpperCase()}
                                </span>
                              </div>
                              <div style={{color: '#721c24', fontSize: '14px'}}>
                                <p style={{margin: '4px 0'}}><strong>Amount:</strong> ${withdrawal.amount.toFixed(2)}</p>
                                <p style={{margin: '4px 0'}}><strong>Method:</strong> {withdrawal.method === 'cashapp' ? 'Cash App' : withdrawal.method === 'zelle' ? 'Zelle' : withdrawal.method}</p>
                                <p style={{margin: '4px 0'}}><strong>Requested:</strong> {formatDateTimeET(withdrawal.created_at)}</p>
                                {withdrawal.note && <p style={{margin: '4px 0'}}><strong>Note:</strong> {withdrawal.note}</p>}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="transactions-list">
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                    <h4 style={{margin:0}}>Transaction History</h4>
                    <div style={{display: 'flex', gap: '8px'}}>
                      <select className="filter-select" style={{padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px'}}>
                        <option>All Types</option>
                        <option>Deposits</option>
                        <option>Withdrawals</option>
                        <option>Fees</option>
                      </select>
                      <select className="filter-select" style={{padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px'}}>
                        <option>All Time</option>
                        <option>Last 7 days</option>
                        <option>Last 30 days</option>
                        <option>Last 3 months</option>
                      </select>
                    </div>
                  </div>
                  {transactions && transactions.length > 0 ? (
                    <div>
                      {transactions.map((transaction) => (
                        <div key={transaction._id} className="transaction-item" style={{border: '1px solid #eee', padding: '16px', margin: '8px 0', borderRadius: '8px', background:'#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                            <div style={{color: formatTransactionAmount(transaction).color, fontSize: '20px'}}>
                              {formatTransactionAmount(transaction).direction === 'credit' ? '↑' : '↓'}
                            </div>
                            <div>
                              <div style={{fontWeight:'500', fontSize: '16px'}}>
                                {formatTransactionAmount(transaction).chargeType}
                              </div>
                              <div style={{fontSize: '14px', color: '#666', marginTop: '2px'}}>
                                {formatTransactionAmount(transaction).tradeNumber && `${formatTransactionAmount(transaction).tradeNumber} `}
                                {formatDateTimeET(transaction.created_at)}
                              </div>
                            </div>
                          </div>
                          <div style={{textAlign: 'right'}}>
                            <div style={{color: formatTransactionAmount(transaction).color, fontWeight:'bold', fontSize: '18px'}}>
                              {formatTransactionAmount(transaction).text}
                            </div>
                            <div style={{fontSize: '12px', color: '#666', textTransform: 'capitalize'}}>
                              {formatTransactionAmount(transaction).direction}
                            </div>
                          </div>
                        </div>
                      ))}
                      {hasMoreTransactions && (
                        <div style={{textAlign: 'center', marginTop: '16px'}}>
                          <button
                            onClick={loadMoreTransactions}
                            disabled={loadingMoreTransactions}
                            style={{
                              padding: '10px 20px',
                              backgroundColor: loadingMoreTransactions ? '#ccc' : '#2563eb',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: loadingMoreTransactions ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                          >
                            {loadingMoreTransactions ? 'Loading...' : 'Load More'}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p>No transactions found.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Profile & Settings</h3>
                <div style={{display:'flex', gap:'12px'}}>
                  <button className="btn" onClick={() => {
                    console.log('Change Password button clicked');
                    setShowPasswordModal(true);
                  }} style={{background:'#6c757d', color:'#fff'}}>
                    🔒 Change Password
                  </button>
                  <button className="btn" onClick={loadDashboardData} disabled={isLoading} style={{background:'#f8f9fa', color:'#333'}}>
                    Refresh
                  </button>
              </div>
              </div>

              {/* Personal Information Card */}
              <div style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:12, padding:20, marginBottom:20}}>
                <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:20}}>
                  <i className="fas fa-user" style={{color:'#6c757d'}}></i>
                  <h4 style={{margin:0}}>Personal Information</h4>
                </div>
                
                <div style={{display:'flex', alignItems:'center', gap:20, marginBottom:20}}>
                  <div style={{position:'relative'}}>
                    {profileImagePreview ? (
                      <img 
                        src={profileImagePreview} 
                        alt="Profile" 
                        style={{width:80, height:80, borderRadius:'50%', objectFit:'cover'}}
                      />
                    ) : (
                      <div style={{
                        width:80, 
                        height:80, 
                        borderRadius:'50%', 
                        background:'#6c757d', 
                        display:'flex', 
                        alignItems:'center', 
                        justifyContent:'center',
                        color:'#fff',
                        fontSize:32,
                        fontWeight:'bold'
                      }}>
                        {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setProfileImage(file);
                          const reader = new FileReader();
                          reader.onload = (e) => setProfileImagePreview(e.target.result);
                          reader.readAsDataURL(file);
                        }
                      }}
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%', opacity:0, cursor:'pointer'}}
                    />
                  </div>
                  
                  <div style={{flex:1}}>
                    <h3 style={{margin:'0 0 4px 0', fontSize:20}}>{profileFormData.name || currentUser?.name || 'Unknown'}</h3>
                    <p style={{margin:'0 0 4px 0', color:'#666'}}>@{profileFormData.closet_name || currentUser?.closet_name || 'unknown'}</p>
                    <p style={{margin:'0 0 4px 0', color:'#666'}}>{currentUser?.email || 'Unknown'}</p>
                    <div style={{display:'flex', gap:16, marginTop:8}}>
                      <span style={{fontSize:12, color:'#666'}}>
                        Member Since: {profileFormData.created_at ? new Date(profileFormData.created_at).toLocaleDateString() : (currentUser?.created_at ? new Date(currentUser.created_at).toLocaleDateString() : 'N/A')}
                      </span>
                      <span style={{
                        background:'#28a745', 
                        color:'#fff', 
                        padding:'2px 8px', 
                        borderRadius:12, 
                        fontSize:12,
                        fontWeight:'bold'
                      }}>
                        Active
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Edit Profile Information */}
              <div style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:12, padding:20, marginBottom:20}}>
                <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:20}}>
                  <i className="fas fa-edit" style={{color:'#6c757d'}}></i>
                  <h4 style={{margin:0}}>Edit Profile Information</h4>
                </div>

                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setIsLoading(true);
                  setError('');
                  setSuccess('');

                  try {
                    const updateData = { ...profileFormData };
                    
                    // Handle profile image upload
                    if (profileImage) {
                      const imageBase64 = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(profileImage);
                      });
                      updateData.profile_image = imageBase64;
                    }

                    // Validate required fields
                    if (!updateData.name || !updateData.closet_name || !updateData.phone || 
                        !updateData.street || !updateData.city || !updateData.state || !updateData.zip) {
                      throw new Error('Please fill in all required fields');
                    }

                    // Map frontend field names to backend field names
                    const backendData = {
                      name: updateData.name,
                      closet_name: updateData.closet_name,
                      phone: updateData.phone,
                      address: updateData.street,
                      apartment: updateData.apartment,
                      city: updateData.city,
                      state: updateData.state,
                      zip_code: updateData.zip,
                      profile_image: updateData.profile_image
                    };

                    // Update profile
                    const response = await profileAPI.update(backendData);

                    if (response) {
                      setSuccess('Profile updated successfully!');
                      setProfileSaveSuccess(true);
                      await loadDashboardData();
                      // Scroll to top to show success message
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      // Reset button success state after 3 seconds
                      setTimeout(() => {
                        setProfileSaveSuccess(false);
                      }, 3000);
                    } else {
                      throw new Error('Failed to update profile');
                    }
                  } catch (error) {
                    console.error('Profile update error:', error);
                    setError(error.message || 'Failed to update profile');
                  } finally {
                    setIsLoading(false);
                  }
                }}>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(250px, 1fr))', gap:16, marginBottom:16}}>
                    <div>
                      <label style={{display:'block', marginBottom:8, fontWeight:'bold'}}>Full Name *</label>
                      <input 
                        type="text" 
                        value={profileFormData.name}
                        onChange={(e) => setProfileFormData({...profileFormData, name: e.target.value})}
                        style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                        required
                      />
                    </div>
                    <div>
                      <label style={{display:'block', marginBottom:8, fontWeight:'bold'}}>Closet Name *</label>
                      <input 
                        type="text" 
                        value={profileFormData.closet_name}
                        onChange={(e) => setProfileFormData({...profileFormData, closet_name: e.target.value})}
                        style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                        required
                      />
                    </div>
                    <div>
                      <label style={{display:'block', marginBottom:8, fontWeight:'bold'}}>Email Address</label>
                      <input 
                        type="email" 
                        value={currentUser?.email || ''}
                        style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px', background:'#f8f9fa'}}
                        disabled
                        title="Email cannot be changed after signup"
                      />
                    </div>
                    <div>
                      <label style={{display:'block', marginBottom:8, fontWeight:'bold'}}>Phone Number *</label>
                      <input 
                        type="tel" 
                        value={profileFormData.phone}
                        onChange={(e) => setProfileFormData({...profileFormData, phone: e.target.value})}
                        style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                        required
                      />
                    </div>
                  </div>

                  <div style={{marginBottom:16}}>
                    <label style={{display:'block', marginBottom:8, fontWeight:'bold'}}>Address *</label>
                    <div style={{marginBottom:8}}>
                      <input 
                        type="text" 
                        placeholder="Street Address"
                        value={profileFormData.street}
                        onChange={(e) => setProfileFormData({...profileFormData, street: e.target.value})}
                        style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                        required
                      />
                    </div>
                    <div style={{marginBottom:8}}>
                      <input 
                        type="text" 
                        placeholder="Apartment, Unit, Suite (optional)"
                        value={profileFormData.apartment}
                        onChange={(e) => setProfileFormData({...profileFormData, apartment: e.target.value})}
                        style={{width:'100%', padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                      />
                    </div>
                    <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:12, marginBottom:8}}>
                      <input 
                        type="text" 
                        placeholder="City"
                        value={profileFormData.city}
                        onChange={(e) => setProfileFormData({...profileFormData, city: e.target.value})}
                        style={{padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                        required
                      />
                      <input 
                        type="text" 
                        placeholder="State"
                        value={profileFormData.state}
                        onChange={(e) => setProfileFormData({...profileFormData, state: e.target.value})}
                        style={{padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                        required
                      />
                      <input 
                        type="text" 
                        placeholder="ZIP Code"
                        value={profileFormData.zip}
                        onChange={(e) => setProfileFormData({...profileFormData, zip: e.target.value})}
                        style={{padding:'8px', border:'1px solid #ccc', borderRadius:'4px'}}
                        required
                      />
                    </div>
                  </div>


                  <div style={{display:'flex', gap:12, justifyContent:'flex-end'}}>
                    <button 
                      type="button"
                      onClick={() => {
                        setProfileFormData({
                          name: currentUser?.name || '',
                          closet_name: currentUser?.closet_name || '',
                          phone: currentUser?.phone || '',
                          street: currentUser?.address || '', // Backend uses 'address' field
                          apartment: currentUser?.apartment || '',
                          city: currentUser?.city || '',
                          state: currentUser?.state || '',
                          zip: currentUser?.zip_code || '', // Backend uses 'zip_code' field
                        });
                        setProfileImage(null);
                        setProfileImagePreview(null);
                      }}
                      className="btn"
                      style={{background:'#6c757d', color:'white', padding:'12px 24px'}}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn" 
                      style={{
                        background: profileSaveSuccess ? '#28a745' : '#007bff', 
                        color: 'white', 
                        padding: '12px 24px',
                        transition: 'background-color 0.3s ease'
                      }}
                      disabled={isLoading}
                    >
                      {profileSaveSuccess ? '✅ Saved!' : (isLoading ? 'Saving...' : '💾 Save Changes')}
                    </button>
                  </div>
                </form>
              </div>

              {/* Security Settings - Delete Account Only */}
              <div style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:12, padding:20}}>
                <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:20}}>
                  <i className="fas fa-shield-alt" style={{color:'#6c757d'}}></i>
                  <h4 style={{margin:0}}>Account Management</h4>
                </div>

                <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                  <button 
                    className="btn" 
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete your account? This action cannot be undone and will:\n\n• Cancel all active trades\n• Remove all your items\n• Forfeit any remaining balance\n• Delete your profile and trade history\n\nType "DELETE" to confirm:')) {
                        const confirmation = prompt('Type "DELETE" to confirm account deletion:');
                        if (confirmation === 'DELETE') {
                          setIsLoading(true);
                          setError('');
                          setSuccess('');

                          try {
                            // Call account deletion API
                            const response = await profileAPI.deleteAccount();

                            if (response && (response.success || response.message)) {
                              setSuccess('Account deleted successfully. You will be logged out.');
                              // Logout user after successful deletion
                              setTimeout(() => {
                                authAPI.logout();
                                window.location.reload();
                              }, 2000);
                            } else {
                              throw new Error('Failed to delete account');
                            }
                          } catch (error) {
                            console.error('Account deletion error:', error);
                            setError(error.message || 'Failed to delete account. Please contact support.');
                          } finally {
                            setIsLoading(false);
                          }
                        } else {
                          setError('Account deletion cancelled. You must type "DELETE" exactly to confirm.');
                        }
                      }
                    }}
                    style={{background:'#dc3545', color:'#fff', padding:'12px 20px'}}
                    disabled={isLoading}
                  >
                    🗑️ Delete Account
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* How To Tab */}
          {activeTab === 'how-to' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>How To Videos</h3>
              </div>

              <div style={{background:'#fff', border:'1px solid #dee2e6', borderRadius:12, padding:20, marginBottom:20}}>
                <div style={{marginBottom:20}}>
                  <h4 style={{margin:'0 0 16px 0'}}>How to upload an item</h4>
                  <div style={{position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:8, background:'#000'}}>
                    <iframe
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                      src="https://www.youtube.com/embed/0WqBErcUwsE"
                      title="How to upload an item"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div style={{marginBottom:20}}>
                  <h4 style={{margin:'0 0 16px 0'}}>How to deposit funds</h4>
                  <div style={{position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:8, background:'#000'}}>
                    <iframe
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                      src="https://www.youtube.com/embed/_7GgXYfetbg"
                      title="How to deposit funds"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div style={{marginBottom:20}}>
                  <h4 style={{margin:'0 0 16px 0'}}>How to withdraw funds</h4>
                  <div style={{position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:8, background:'#000'}}>
                    <iframe
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                      src="https://www.youtube.com/embed/lgjaYc5pZEw"
                      title="How to withdraw funds"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div style={{marginBottom:20}}>
                  <h4 style={{margin:'0 0 16px 0'}}>How to do a trade</h4>
                  <div style={{position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:8, background:'#000'}}>
                    <iframe
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                      src="https://www.youtube.com/embed/9BT4U9tNwLE"
                      title="How to do a trade"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div style={{marginBottom:20}}>
                  <h4 style={{margin:'0 0 16px 0'}}>How to view or cancel a trade</h4>
                  <div style={{position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:8, background:'#000'}}>
                    <iframe
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                      src="https://www.youtube.com/embed/XtGMZGuM8M8"
                      title="How to view or cancel a trade"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div style={{marginBottom:20}}>
                  <h4 style={{margin:'0 0 16px 0'}}>How to confirm items received</h4>
                  <div style={{position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:8, background:'#000'}}>
                    <iframe
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                      src="https://www.youtube.com/embed/Clp5o5cRJQU"
                      title="How to confirm items received"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div style={{marginBottom:20}}>
                  <h4 style={{margin:'0 0 16px 0'}}>How to find the postage label</h4>
                  <div style={{position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:8, background:'#000'}}>
                    <iframe
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                      src="https://www.youtube.com/embed/D3tU22orsAY"
                      title="How to find the postage label"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div style={{marginBottom:20}}>
                  <h4 style={{margin:'0 0 16px 0'}}>How to: Quick Overview</h4>
                  <div style={{position:'relative', paddingBottom:'56.25%', height:0, overflow:'hidden', borderRadius:8, background:'#000'}}>
                    <iframe
                      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                      src="https://www.youtube.com/embed/nZwSibtL1PQ"
                      title="How to: Quick Overview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rating Modal */}
      {showRatingModal && ratingTrade && (
        <div className="modal" style={{zIndex: 10002}}>
          <div className="modal-content" onClick={(e)=> e.stopPropagation()} style={{maxWidth: '500px'}}>
            <span className="close" onClick={()=> setShowRatingModal(false)}>&times;</span>
            <h3 style={{textAlign:'center', marginBottom:20}}>⭐ Rate Your Trading Experience</h3>
            
            <div style={{marginBottom:20}}>
              <label style={{display:'block', marginBottom:8, fontWeight:'bold'}}>Rating</label>
              <div style={{display:'flex', gap:8, justifyContent:'center', marginBottom:16}}>
                {[1,2,3,4,5].map(star => (
                  <button
                    key={star}
                    onClick={() => setRatingData(prev => ({...prev, rating: star}))}
                    style={{
                      background:'none',
                      border:'none',
                      fontSize:'32px',
                      color: star <= ratingData.rating ? '#ffc107' : '#ddd',
                      cursor:'pointer',
                      padding:'4px'
                    }}
                  >
                    ⭐
                  </button>
                ))}
              </div>
              <div style={{textAlign:'center', fontSize:14, color:'#666'}}>
                {ratingData.rating === 1 && 'Poor'}
                {ratingData.rating === 2 && 'Fair'}
                {ratingData.rating === 3 && 'Good'}
                {ratingData.rating === 4 && 'Very Good'}
                {ratingData.rating === 5 && 'Excellent'}
              </div>
            </div>

            <div style={{marginBottom:20}}>
              <label style={{display:'block', marginBottom:8, fontWeight:'bold'}}>Comment (Optional)</label>
              <textarea
                value={ratingData.comment}
                onChange={(e) => setRatingData(prev => ({...prev, comment: e.target.value}))}
                placeholder="Share your experience with this trade..."
                style={{
                  width:'100%',
                  height:'100px',
                  padding:'12px',
                  border:'1px solid #ddd',
                  borderRadius:'4px',
                  resize:'vertical',
                  fontSize:'14px'
                }}
              />
            </div>

            <div style={{display:'flex', gap:12, justifyContent:'center'}}>
              <button 
                className="btn" 
                onClick={()=> setShowRatingModal(false)}
                style={{background:'#6c757d', color:'#fff'}}
              >
                Cancel
              </button>
              <button 
                className="btn" 
                onClick={handleSubmitRating}
                disabled={isLoading}
                style={{background:'#28a745', color:'#fff'}}
              >
                {isLoading ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && confirmationDetails && (
        <div className="modal" style={{zIndex: 10001}}>
          <div className="modal-content" onClick={(e)=> e.stopPropagation()} style={{maxWidth: '400px'}}>
            <span className="close" onClick={()=> setShowConfirmationModal(false)}>&times;</span>
            <h3 style={{color:'#28a745', textAlign:'center'}}>✓ Confirmation Recorded</h3>
            <div style={{textAlign:'center', padding:'20px 0'}}>
              <div style={{fontSize:18, fontWeight:'bold', marginBottom:8}}>
                {confirmationDetails.itemName}
              </div>
              <div style={{fontSize:14, color:'#666', marginBottom:8}}>
                Trade Ref: {confirmationDetails.tradeRef}
              </div>
              <div style={{fontSize:14, color:'#666'}}>
                Confirmed at: {confirmationDetails.timestamp}
              </div>
            </div>
            <div style={{textAlign:'center', marginTop:20}}>
              <button className="btn" onClick={()=> setShowConfirmationModal(false)} style={{background:'#28a745', color:'#fff'}}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Trade Details Modal */}
      {tradeDetailsOpen && tradeDetails && (
        <div className="modal" style={{zIndex: 10000}}>
          <div className="modal-content" onClick={(e)=> e.stopPropagation()} style={{maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto'}}>
            <span className="close" onClick={()=> setTradeDetailsOpen(false)}>&times;</span>
            <h3>Trade Details</h3>
            <div style={{fontSize:12, color:'#666', marginTop:-6, marginBottom:16}}>Ref: {String(tradeDetails.trade?._id||'').slice(-6)}</div>
            
            {/* Admin Override Notice */}
            {tradeDetails.trade?.admin_override && (
              <div style={{background:'#fff3cd', border:'1px solid #ffeaa7', borderRadius:8, padding:12, marginBottom:16}}>
                <div style={{color:'#856404', fontWeight:'bold', marginBottom:4, fontSize:14}}>⚠️ Admin Override</div>
                <div style={{color:'#856404', fontSize:12}}>
                  An admin has overridden tracking confirmations for this trade. This action was taken to resolve a dispute or issue.
                </div>
              </div>
            )}
            
            {/* Comprehensive Trade Information */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:16, marginBottom:20}}>
              
              {/* Items Section */}
              <div style={{background:'#fff', border:'1px solid #eee', borderRadius:8, padding:16}}>
                <h4 style={{marginTop:0, marginBottom:16, color:'#333'}}>Trading Items</h4>
                
                {/* Offered Item */}
                <div style={{marginBottom:16, padding:12, background:'#f8f9fa', borderRadius:6}}>
                  <div style={{display:'flex', gap:12, alignItems:'center', marginBottom:8}}>
                    <div 
                      style={{width:80, height:80, background:'#f5f5f5', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#666', overflow:'hidden', cursor:'pointer', position:'relative'}}
                      onClick={() => {
                        const additionalImages = tradeDetails.trade?.offered_hat?.additional_images || tradeDetails.trade?.offered_hat?.images || [];
                        const mainImage = tradeDetails.trade?.offered_hat?.image_url;
                        const allImages = mainImage ? [mainImage, ...additionalImages.filter(img => img !== mainImage)] : additionalImages;
                        openPhotoGallery(allImages, 0);
                      }}
                    >
                      {tradeDetails.trade?.offered_hat?.image_url ? (
                        <img src={tradeDetails.trade.offered_hat.image_url} alt={tradeDetails.trade.offered_hat.name} style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:6}} />
                      ) : (
                        <span>No Image</span>
                      )}
                      {/* Photo count indicator */}
                      {(() => {
                        const additionalImages = tradeDetails.trade?.offered_hat?.additional_images || tradeDetails.trade?.offered_hat?.images || [];
                        const mainImage = tradeDetails.trade?.offered_hat?.image_url;
                        const totalImages = mainImage ? 1 + additionalImages.filter(img => img !== mainImage).length : additionalImages.length;
                        return totalImages > 1 ? (
                          <div style={{
                            position: 'absolute',
                            bottom: '4px',
                            right: '4px',
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}>
                            {totalImages}
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:'bold', fontSize:16, marginBottom:4}}>{tradeDetails.trade?.offered_hat?.name || 'Unknown Item'}</div>
                      <div style={{fontSize:14, color:'#666', marginBottom:4}}>Brand: {tradeDetails.trade?.offered_hat?.brand || 'N/A'}</div>
                      <div style={{fontSize:14, color:'#666', marginBottom:4}}>Size: {tradeDetails.trade?.offered_hat?.size || 'N/A'}</div>
                      <div style={{fontSize:14, color:'#666', marginBottom:4}}>Condition: {tradeDetails.trade?.offered_hat?.condition || 'N/A'}</div>
                      <div style={{fontSize:14, fontWeight:'bold', color:'#28a745'}}>Value: ${tradeDetails.trade?.offered_hat?.value?.toFixed(2) || '0.00'}</div>
                    </div>
                  </div>
                  <div style={{fontSize:12, color:'#666'}}>
                    Item of: <strong>{tradeDetails.trade?.from_user?.name || 'Unknown'}</strong>
                  </div>
                </div>

                {/* Requested Item */}
                <div style={{padding:12, background:'#f8f9fa', borderRadius:6}}>
                  <div style={{display:'flex', gap:12, alignItems:'center', marginBottom:8}}>
                    <div 
                      style={{width:80, height:80, background:'#f5f5f5', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#666', overflow:'hidden', cursor:'pointer', position:'relative'}}
                      onClick={() => {
                        const additionalImages = tradeDetails.trade?.requested_hat?.additional_images || tradeDetails.trade?.requested_hat?.images || [];
                        const mainImage = tradeDetails.trade?.requested_hat?.image_url;
                        const allImages = mainImage ? [mainImage, ...additionalImages.filter(img => img !== mainImage)] : additionalImages;
                        openPhotoGallery(allImages, 0);
                      }}
                    >
                      {tradeDetails.trade?.requested_hat?.image_url ? (
                        <img src={tradeDetails.trade.requested_hat.image_url} alt={tradeDetails.trade.requested_hat.name} style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:6}} />
                      ) : (
                        <span>No Image</span>
                      )}
                      {/* Photo count indicator */}
                      {(() => {
                        const additionalImages = tradeDetails.trade?.requested_hat?.additional_images || tradeDetails.trade?.requested_hat?.images || [];
                        const mainImage = tradeDetails.trade?.requested_hat?.image_url;
                        const totalImages = mainImage ? 1 + additionalImages.filter(img => img !== mainImage).length : additionalImages.length;
                        return totalImages > 1 ? (
                          <div style={{
                            position: 'absolute',
                            bottom: '4px',
                            right: '4px',
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}>
                            {totalImages}
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:'bold', fontSize:16, marginBottom:4}}>{tradeDetails.trade?.requested_hat?.name || 'Unknown Item'}</div>
                      <div style={{fontSize:14, color:'#666', marginBottom:4}}>Brand: {tradeDetails.trade?.requested_hat?.brand || 'N/A'}</div>
                      <div style={{fontSize:14, color:'#666', marginBottom:4}}>Size: {tradeDetails.trade?.requested_hat?.size || 'N/A'}</div>
                      <div style={{fontSize:14, color:'#666', marginBottom:4}}>Condition: {tradeDetails.trade?.requested_hat?.condition || 'N/A'}</div>
                      <div style={{fontSize:14, fontWeight:'bold', color:'#28a745'}}>Value: ${tradeDetails.trade?.requested_hat?.value?.toFixed(2) || '0.00'}</div>
                    </div>
                  </div>
                  <div style={{fontSize:12, color:'#666'}}>
                    Item of: <strong>{tradeDetails.trade?.to_user?.name || 'Unknown'}</strong>
                  </div>
                </div>
              </div>

              {/* Financial Breakdown */}
              <div style={{background:'#fff', border:'1px solid #eee', borderRadius:8, padding:16}}>
                <h4 style={{marginTop:0, marginBottom:16, color:'#333'}}>Financial Breakdown</h4>
                
                {tradeDetails.trade?.financial_breakdown ? (
                  <div>
                    <div style={{marginBottom:12, padding:8, background:'#e8f5e8', borderRadius:4}}>
                      <div style={{fontSize:14, fontWeight:'bold', marginBottom:4}}>Your Charges</div>
                      <div style={{fontSize:12, color:'#666', marginBottom:2}}>Site Fee: ${tradeDetails.trade.financial_breakdown.site_fee?.toFixed(2) || '0.00'}</div>
                      <div style={{fontSize:12, color:'#666', marginBottom:2}}>{tradeDetails.trade?.delivery_method === 'pickup' ? 'Pickup Fee' : 'Shipping Fee'}: ${tradeDetails.trade.financial_breakdown.delivery_fees?.toFixed(2) || '0.00'}</div>
                      <div style={{fontSize:12, color:'#666', marginBottom:4}}>Item Hold: ${tradeDetails.trade.financial_breakdown.other_item_value?.toFixed(2) || '0.00'}</div>
                      <div style={{fontSize:14, fontWeight:'bold', borderTop:'1px solid #ccc', paddingTop:4}}>Total: ${((tradeDetails.trade.financial_breakdown.site_fee || 0) + (tradeDetails.trade.financial_breakdown.delivery_fees || 0) + (tradeDetails.trade.financial_breakdown.other_item_value || 0)).toFixed(2)}</div>
                    </div>
                    
                    <div style={{marginBottom:12, padding:8, background:'#f0f8ff', borderRadius:4}}>
                      <div style={{fontSize:14, fontWeight:'bold', marginBottom:4}}>Balance Impact</div>
                      <div style={{fontSize:12, color:'#666', marginBottom:2}}>Before Trade: ${tradeDetails.trade.financial_breakdown.original_balance?.toFixed(2) || '0.00'}</div>
                      <div style={{fontSize:12, color:'#666', marginBottom:4}}>After Trade: ${((tradeDetails.trade.financial_breakdown.original_balance || 0) - (tradeDetails.trade.financial_breakdown.site_fee || 0) - (tradeDetails.trade.financial_breakdown.delivery_fees || 0) - (tradeDetails.trade.financial_breakdown.other_item_value || 0)).toFixed(2)}</div>
                    </div>

                    <div style={{marginBottom:12, padding:8, background:'#f8f9fa', borderRadius:4}}>
                      <div style={{fontSize:14, fontWeight:'bold', marginBottom:4}}>Trade Terms</div>
                      <div style={{fontSize:12, color:'#666', marginBottom:2}}>Start Date: {tradeDetails.trade?.start_date ? new Date(tradeDetails.trade.start_date).toLocaleDateString() : 'N/A'}</div>
                      <div style={{fontSize:12, color:'#666'}}>End Date: {tradeDetails.trade?.end_date ? new Date(tradeDetails.trade.end_date).toLocaleDateString() : 'N/A'}</div>
                    </div>
                  </div>
                ) : (
                  <div style={{fontSize:14, color:'#666'}}>Financial details not available</div>
                )}
              </div>

              {/* Status & Actions */}
              <div style={{background:'#fff', border:'1px solid #eee', borderRadius:8, padding:16}}>
                <h4 style={{marginTop:0, marginBottom:16, color:'#333'}}>Status & Actions</h4>
                
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:12, color:'#666', marginBottom:4}}>Current Status</div>
                  <div style={{
                    fontWeight:'bold', 
                    padding:'8px 12px',
                    borderRadius:4,
                    color:'#fff',
                    backgroundColor: tradeDetails.trade?.status === 'accepted' ? '#28a745' : 
                                   tradeDetails.trade?.status === 'rejected' ? '#dc3545' : 
                                   tradeDetails.trade?.status === 'completed' ? '#17a2b8' : '#ffc107',
                    textTransform:'uppercase',
                    fontSize:12
                  }}>
                    {tradeDetails.trade?.status || 'Unknown'}
                  </div>
                </div>

                {/* Action Buttons */}
                {tradeDetails.trade?.status === 'pending' && (
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:12, color:'#666', marginBottom:8}}>Available Actions</div>
                    <div style={{display:'flex', gap:8, flexDirection:'column'}}>
                      {(() => {
                        const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
                        const isInitiator = tradeDetails.trade.hold_user_id === currentUserId;
                        
                        if (isInitiator) {
                          return (
                            <>
                              <div style={{padding:'12px', background:'#e9ecef', borderRadius:4, fontSize:14, color:'#6c757d', textAlign:'center', display:'flex', alignItems:'center', justifyContent:'center', gap:8}}>
                                ⏳ Waiting for the other user to respond to your trade offer
                              </div>
                              <button className="btn" onClick={()=> handleTradeAction(tradeDetails.trade._id, 'cancel')} disabled={isLoading} style={{background:'#ffc107', color:'#000'}}>
                                ⚠ Cancel Trade
                              </button>
                            </>
                          );
                        } else {
                          return (
                            <>
                              <button className="btn" onClick={()=> handleTradeAction(tradeDetails.trade._id, 'accept')} disabled={isLoading} style={{background:'#28a745', color:'#fff'}}>
                                ✓ Accept Trade
                              </button>
                              <button className="btn" onClick={()=> handleTradeAction(tradeDetails.trade._id, 'reject')} disabled={isLoading} style={{background:'#dc3545', color:'#fff'}}>
                                ✗ Reject Trade
                              </button>
                              <button className="btn" onClick={()=> openModifyModal(tradeDetails.trade)} disabled={isLoading} style={{background:'#6f42c1', color:'#fff'}}>
                                ✏️ Modify
                              </button>
                            </>
                          );
                        }
                      })()}
                    </div>
                  </div>
                )}

                {tradeDetails.trade?.status === 'accepted' && (
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:12, color:'#666', marginBottom:8}}>Available Actions</div>
                    <div style={{display:'flex', gap:8, flexDirection:'column'}}>
                      {(() => {
                        const me = (currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id);
                        const isUser1 = tradeDetails.trade.user1_id === me;
                        const userReceived = isUser1 ? tradeDetails.trade.user1_received_at : tradeDetails.trade.user2_received_at;
                        const userReturned = isUser1 ? tradeDetails.trade.user1_returned_at : tradeDetails.trade.user2_returned_at;
                        const hasConfirmedReceived = !!userReceived;
                        const hasConfirmedReturned = !!userReturned;
                        const bothUsersReceived = tradeDetails.trade.user1_received_at && tradeDetails.trade.user2_received_at;
                        
                        return (
                          <>
                            <button 
                              className="btn" 
                              onClick={()=> handleAcknowledgeReceived(tradeDetails.trade._id)} 
                              disabled={isLoading || acknowledgingReceived || hasConfirmedReceived || myDisputes.find(d => String(d.trade_id) === String(tradeDetails.trade._id))} 
                              style={{
                                background: hasConfirmedReceived ? '#6c757d' : '#17a2b8', 
                                color:'#fff',
                                cursor: hasConfirmedReceived ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {hasConfirmedReceived ? '✅ Item Confirmed Received' : '📦 Confirm Item Received'}
                      </button>
                            <button 
                              className="btn" 
                              onClick={()=> handleAcknowledgeReturned(tradeDetails.trade._id)} 
                              disabled={isLoading || acknowledgingReturned || hasConfirmedReturned || !bothUsersReceived || myDisputes.find(d => String(d.trade_id) === String(tradeDetails.trade._id))} 
                              style={{
                                background: !bothUsersReceived ? '#6c757d' : hasConfirmedReturned ? '#6c757d' : '#6f42c1', 
                                color:'#fff',
                                cursor: (!bothUsersReceived || hasConfirmedReturned) ? 'not-allowed' : 'pointer'
                              }}
                              title={!bothUsersReceived ? 'Both users must confirm receipt before item can be returned' : hasConfirmedReturned ? 'Item already confirmed as returned' : 'Confirm item returned'}
                            >
                              {hasConfirmedReturned ? '✅ Item Confirmed Returned' : !bothUsersReceived ? '⏳ Waiting for Both Confirmations' : '🔄 Confirm Item Returned'}
                      </button>
                          </>
                        );
                      })()}
                      {(() => {
                        const existingDispute = myDisputes.find(d => String(d.trade_id) === String(tradeDetails.trade._id));
                        if (existingDispute) {
                          return (
                            <button className="btn" disabled style={{background:'#6c757d', color:'#fff'}}>
                              ⚠️ Dispute Already Created
                            </button>
                          );
                        }
                        return (
                          <div style={{position: 'relative'}}>
                            <button 
                              className="btn" 
                              onClick={()=> handleCreateDispute(tradeDetails.trade._id)} 
                              disabled={isLoading} 
                              style={{background:'#ffc107', color:'#000'}}
                              title="Use this if there's a problem with the item, delivery, or return that you can't resolve with the other user"
                            >
                              ⚠️ Create Dispute
                            </button>
                          </div>
                        );
                      })()}
                      
                      {/* Disable other action buttons if dispute exists */}
                      {(() => {
                        const existingDispute = myDisputes.find(d => String(d.trade_id) === String(tradeDetails.trade._id));
                        if (existingDispute) {
                          return (
                            <div style={{marginTop: '8px', padding: '8px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px'}}>
                              <div style={{fontSize: '12px', color: '#721c24'}}>
                                ⚠️ Trade actions are disabled while dispute is active
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                )}

                {/* Delivery Information */}
                <div style={{marginBottom:16, padding:8, background:'#f8f9fa', borderRadius:4}}>
                  <div style={{fontSize:12, color:'#666', marginBottom:4}}>Delivery Method</div>
                  <div style={{fontSize:14, fontWeight:'bold'}}>
                    {tradeDetails.trade?.delivery_method === 'pickup' ? '🚗 Pickup - View email for details' : '📦 Shipping - View email for details'}
                  </div>
                  {tradeDetails.trade?.delivery_method === 'pickup' && (
                    <div style={{fontSize:12, color:'#856404', marginTop: '4px', fontWeight: '500'}}>
                      🚗 Scheduled for closest Thursday or Sunday in term dates
                    </div>
                  )}
                  {tradeDetails.trade?.delivery_method === 'shipping' && (
                    <div style={{fontSize:12, color:'#666', marginTop:4}}>
                      <div>Shipping Cost: ${(tradeDetails.trade?.shipping_cost / 2)?.toFixed(2) || '0.00'} each way</div>
                      <div style={{marginTop: '4px', color: '#1976d2', fontWeight: '500'}}>
                        📦 Box size limit: 15" × 8" × 14"
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Detailed Timeline */}
            <div style={{background:'#fff', border:'1px solid #eee', borderRadius:8, padding:16, marginBottom:20}}>
              <h4 style={{marginTop:0, marginBottom:16, color:'#333'}}>Trade Timeline</h4>
              <div style={{display:'flex', flexDirection:'column', gap:8}}>
                {tradeDetails.trade?.created_at && (
                  <div style={{display:'flex', alignItems:'center', gap:12, padding:8, background:'#f8f9fa', borderRadius:4}}>
                    <div style={{width:8, height:8, background:'#28a745', borderRadius:'50%'}}></div>
                    <div style={{fontSize:14}}>Trade Created</div>
                    <div style={{fontSize:12, color:'#666', marginLeft:'auto'}}>
                      {formatDateTimeET(tradeDetails.trade.created_at)}
                    </div>
                  </div>
                )}
                
                {tradeDetails.trade?.accepted_at && (
                  <div style={{display:'flex', alignItems:'center', gap:12, padding:8, background:'#f8f9fa', borderRadius:4}}>
                    <div style={{width:8, height:8, background:'#17a2b8', borderRadius:'50%'}}></div>
                    <div style={{fontSize:14}}>Trade Accepted</div>
                    <div style={{fontSize:12, color:'#666', marginLeft:'auto'}}>
                      {formatDateTimeET(tradeDetails.trade.accepted_at)}
                    </div>
                  </div>
                )}

                {(() => {
                  const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
                  const isUser1 = tradeDetails.trade.user1_id === currentUserId;
                  const userReceived = isUser1 ? tradeDetails.trade.user1_received_at : tradeDetails.trade.user2_received_at;
                  const sisterReceived = isUser1 ? tradeDetails.trade.user2_received_at : tradeDetails.trade.user1_received_at;
                  
                  return (
                    <>
                      {userReceived && (
                        <div style={{display:'flex', alignItems:'center', gap:12, padding:8, background:'#f8f9fa', borderRadius:4}}>
                          <div style={{width:8, height:8, background:'#6f42c1', borderRadius:'50%'}}></div>
                          <div style={{fontSize:14}}>You Confirmed Receipt</div>
                          <div style={{fontSize:12, color:'#666', marginLeft:'auto'}}>
                            {formatDateTimeET(userReceived)}
                          </div>
                        </div>
                      )}
                      {sisterReceived && (
                        <div style={{display:'flex', alignItems:'center', gap:12, padding:8, background:'#f8f9fa', borderRadius:4}}>
                          <div style={{width:8, height:8, background:'#6f42c1', borderRadius:'50%'}}></div>
                          <div style={{fontSize:14}}>Sister Confirmed Receipt</div>
                          <div style={{fontSize:12, color:'#666', marginLeft:'auto'}}>
                            {formatDateTimeET(sisterReceived)}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {(() => {
                  const currentUserId = currentUser?.id || JSON.parse(localStorage.getItem('currentUser')||'{}')?.id;
                  const isUser1 = tradeDetails.trade.user1_id === currentUserId;
                  const userReturned = isUser1 ? tradeDetails.trade.user1_returned_at : tradeDetails.trade.user2_returned_at;
                  const sisterReturned = isUser1 ? tradeDetails.trade.user2_returned_at : tradeDetails.trade.user1_returned_at;
                  
                  return (
                    <>
                      {userReturned && (
                        <div style={{display:'flex', alignItems:'center', gap:12, padding:8, background:'#f8f9fa', borderRadius:4}}>
                          <div style={{width:8, height:8, background:'#fd7e14', borderRadius:'50%'}}></div>
                          <div style={{fontSize:14}}>You Confirmed Return</div>
                          <div style={{fontSize:12, color:'#666', marginLeft:'auto'}}>
                            {formatDateTimeET(userReturned)}
                          </div>
                        </div>
                      )}
                      {sisterReturned && (
                        <div style={{display:'flex', alignItems:'center', gap:12, padding:8, background:'#f8f9fa', borderRadius:4}}>
                          <div style={{width:8, height:8, background:'#fd7e14', borderRadius:'50%'}}></div>
                          <div style={{fontSize:14}}>Sister Confirmed Return</div>
                          <div style={{fontSize:12, color:'#666', marginLeft:'auto'}}>
                            {formatDateTimeET(sisterReturned)}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {tradeDetails.trade?.completed_at && (
                  <div style={{display:'flex', alignItems:'center', gap:12, padding:8, background:'#e8f5e8', borderRadius:4}}>
                    <div style={{width:8, height:8, background:'#28a745', borderRadius:'50%'}}></div>
                    <div style={{fontSize:14, fontWeight:'bold'}}>Trade Completed</div>
                    <div style={{fontSize:12, color:'#666', marginLeft:'auto'}}>
                      {formatDateTimeET(tradeDetails.trade.completed_at)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* User Profiles */}
            <div style={{background:'#fff', border:'1px solid #eee', borderRadius:8, padding:16}}>
              <h4 style={{marginTop:0, marginBottom:16, color:'#333'}}>Trading Sisters</h4>
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:16}}>
                {/* Initiator Profile */}
                <div style={{textAlign:'center', padding:12, background:'#f8f9fa', borderRadius:6}}>
                  {(tradeDetails.trade?.user1_id && (() => {
                    const ownerId = tradeDetails.trade.from_user?._id ?? tradeDetails.trade.user1_id;
                    const idStr = ownerId != null ? String(ownerId) : null;
                    return idStr ? (
                      <a href={`/closet/${idStr}`} target="_blank" rel="noopener noreferrer" style={{textDecoration:'none', color:'inherit', display:'inline-block'}} title="View profile">
                        <div style={{width:60, height:60, background:'#e9ecef', borderRadius:'50%', margin:'0 auto 8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#6c757d', cursor:'pointer'}}>
                          {tradeDetails.trade?.from_user?.profile_image_url || tradeDetails.trade?.from_user?.profile_image ? (
                            <img src={tradeDetails.trade.from_user.profile_image_url || tradeDetails.trade.from_user.profile_image} alt="Profile" style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%'}} />
                          ) : (
                            <i className="fas fa-user"></i>
                          )}
                        </div>
                        <div style={{fontWeight:'bold', fontSize:14}}>{tradeDetails.trade?.from_user?.name || 'Unknown'}</div>
                      </a>
                    ) : (
                      <>
                        <div style={{width:60, height:60, background:'#e9ecef', borderRadius:'50%', margin:'0 auto 8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#6c757d'}}>
                          {tradeDetails.trade?.from_user?.profile_image_url || tradeDetails.trade?.from_user?.profile_image ? (
                            <img src={tradeDetails.trade.from_user.profile_image_url || tradeDetails.trade.from_user.profile_image} alt="Profile" style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%'}} />
                          ) : (
                            <i className="fas fa-user"></i>
                          )}
                        </div>
                        <div style={{fontWeight:'bold', fontSize:14}}>{tradeDetails.trade?.from_user?.name || 'Unknown'}</div>
                      </>
                    );
                  })()) ?? (
                    <>
                      <div style={{width:60, height:60, background:'#e9ecef', borderRadius:'50%', margin:'0 auto 8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#6c757d'}}>
                        {tradeDetails.trade?.from_user?.profile_image_url || tradeDetails.trade?.from_user?.profile_image ? (
                          <img src={tradeDetails.trade.from_user.profile_image_url || tradeDetails.trade.from_user.profile_image} alt="Profile" style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%'}} />
                        ) : (
                          <i className="fas fa-user"></i>
                        )}
                      </div>
                      <div style={{fontWeight:'bold', fontSize:14}}>{tradeDetails.trade?.from_user?.name || 'Unknown'}</div>
                    </>
                  )}
                  <div style={{fontSize:12, color:'#666'}}>Trade Initiator</div>
                  {tradeDetails.trade?.from_user?.average_rating && (
                    <div style={{fontSize:12, color:'#ffc107', marginTop:4}}>
                      ⭐ {tradeDetails.trade.from_user.average_rating.toFixed(1)} ({tradeDetails.trade.from_user.rating_count || 0} reviews)
                    </div>
                  )}
                </div>

                {/* Receiver Profile */}
                <div style={{textAlign:'center', padding:12, background:'#f8f9fa', borderRadius:6}}>
                  {(tradeDetails.trade?.user2_id && (() => {
                    const ownerId = tradeDetails.trade.to_user?._id ?? tradeDetails.trade.user2_id;
                    const idStr = ownerId != null ? String(ownerId) : null;
                    return idStr ? (
                      <a href={`/closet/${idStr}`} target="_blank" rel="noopener noreferrer" style={{textDecoration:'none', color:'inherit', display:'inline-block'}} title="View profile">
                        <div style={{width:60, height:60, background:'#e9ecef', borderRadius:'50%', margin:'0 auto 8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#6c757d', cursor:'pointer'}}>
                          {tradeDetails.trade?.to_user?.profile_image_url || tradeDetails.trade?.to_user?.profile_image ? (
                            <img src={tradeDetails.trade.to_user.profile_image_url || tradeDetails.trade.to_user.profile_image} alt="Profile" style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%'}} />
                          ) : (
                            <i className="fas fa-user"></i>
                          )}
                        </div>
                        <div style={{fontWeight:'bold', fontSize:14}}>{tradeDetails.trade?.to_user?.name || 'Unknown'}</div>
                      </a>
                    ) : (
                      <>
                        <div style={{width:60, height:60, background:'#e9ecef', borderRadius:'50%', margin:'0 auto 8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#6c757d'}}>
                          {tradeDetails.trade?.to_user?.profile_image_url || tradeDetails.trade?.to_user?.profile_image ? (
                            <img src={tradeDetails.trade.to_user.profile_image_url || tradeDetails.trade.to_user.profile_image} alt="Profile" style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%'}} />
                          ) : (
                            <i className="fas fa-user"></i>
                          )}
                        </div>
                        <div style={{fontWeight:'bold', fontSize:14}}>{tradeDetails.trade?.to_user?.name || 'Unknown'}</div>
                      </>
                    );
                  })()) ?? (
                    <>
                      <div style={{width:60, height:60, background:'#e9ecef', borderRadius:'50%', margin:'0 auto 8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'#6c757d'}}>
                        {tradeDetails.trade?.to_user?.profile_image_url || tradeDetails.trade?.to_user?.profile_image ? (
                          <img src={tradeDetails.trade.to_user.profile_image_url || tradeDetails.trade.to_user.profile_image} alt="Profile" style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%'}} />
                        ) : (
                          <i className="fas fa-user"></i>
                        )}
                      </div>
                      <div style={{fontWeight:'bold', fontSize:14}}>{tradeDetails.trade?.to_user?.name || 'Unknown'}</div>
                    </>
                  )}
                  <div style={{fontSize:12, color:'#666'}}>Trade Receiver</div>
                  {tradeDetails.trade?.to_user?.average_rating && (
                    <div style={{fontSize:12, color:'#ffc107', marginTop:4}}>
                      ⭐ {tradeDetails.trade.to_user.average_rating.toFixed(1)} ({tradeDetails.trade.to_user.rating_count || 0} reviews)
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      <PasswordChangeModal 
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={() => {
          setShowPasswordModal(false);
          setSuccess('Password changed successfully');
        }}
      />
      
      <TwoFactorModal 
        isOpen={show2FAModal}
        onClose={() => setShow2FAModal(false)}
      />

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onTradeRequest={() => setDetailOpen(false)}
        onModify={() => {
          setDetailOpen(false);
          if (modifyTrade) {
            setModifyMode('item');
            setModifyOpen(true);
          }
        }}
      />

      {/* Edit Item Modal */}
      {editOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '20px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h3 style={{margin: 0}}>Edit Item</h3>
              <button 
                onClick={() => setEditOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setIsLoading(true);
              setError('');
              setSuccess('');

              try {
                const updateData = {
                  name: editFormData.name,
                  description: editFormData.description,
                  value: parseFloat(editFormData.value),
                  condition: editFormData.condition,
                  brand: editFormData.brand
                };

                // Validate required fields
                if (!updateData.name || !updateData.description || !updateData.value || !updateData.condition || !updateData.brand) {
                  throw new Error('Please fill in all required fields');
                }

                if (updateData.value <= 0) {
                  throw new Error('Value must be greater than 0');
                }


                // Update the hat
                console.log('Updating hat with data:', updateData);
                console.log('Hat ID:', editHat._id);
                const response = await hatsAPI.update(editHat._id, updateData);
                console.log('Update response:', response);

                if (response && response.message === 'Hat updated successfully') {
                  setSuccess('Item updated successfully!');
                  setEditOpen(false);
                  // Reload dashboard data to show the updated item
                  await loadDashboardData();
                } else {
                  throw new Error('Failed to update item');
                }
              } catch (error) {
                console.error('Update error:', error);
                setError(error.message || 'Failed to update item');
              } finally {
                setIsLoading(false);
              }
            }}>
              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>Item Name:</label>
                <input 
                  type="text" 
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px'}}
                  required
                />
              </div>

              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>Description:</label>
                <textarea 
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  rows="3"
                  style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px'}}
                  required
                />
              </div>

              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>Value ($):</label>
                <input 
                  type="number" 
                  value={editFormData.value}
                  step="0.01"
                  style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f5f5f5', color: '#666'}}
                  readOnly
                  disabled
                />
                <small style={{color: '#666', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                  Value cannot be changed after upload. Delete and re-upload item to change price.
                </small>
              </div>

              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>Condition:</label>
                <select 
                  value={editFormData.condition}
                  onChange={(e) => setEditFormData({...editFormData, condition: e.target.value})}
                  style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px'}}
                  required
                >
                  <option value="">Select condition</option>
                  <option value="New">New</option>
                  <option value="Like New">Like New</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                </select>
              </div>

              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>Brand:</label>
                <input 
                  type="text" 
                  value={editFormData.brand}
                  onChange={(e) => setEditFormData({...editFormData, brand: e.target.value})}
                  style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px'}}
                  required
                />
              </div>

              <div style={{marginBottom: '16px'}}>
                <button 
                  type="button" 
                  onClick={() => {
                    // Initialize photo management with current item's photos
                    const allPhotos = [];
                    if (editHat?.image_url) allPhotos.push(editHat.image_url);
                    if (editHat?.additional_images) allPhotos.push(...editHat.additional_images);
                    
                    setCurrentPhotos(allPhotos);
                    setMainImageIndex(0);
                    setNewPhotos([]);
                    setPhotoPreviews([]);
                    setPhotoModalOpen(true);
                  }}
                  className="btn" 
                  style={{background: '#17a2b8', color: 'white', padding: '12px 24px', width: '100%'}}
                >
                  📷 Manage Photos
                </button>
              </div>

              <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                <button 
                  type="button" 
                  onClick={() => setEditOpen(false)}
                  className="btn" 
                  style={{background: '#6c757d', color: 'white', padding: '12px 24px'}}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn" 
                  style={{background: '#28a745', color: 'white', padding: '12px 24px'}}
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Photo Management Modal */}
      {photoModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '20px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h3 style={{margin: 0}}>Manage Photos</h3>
              <button 
                onClick={() => setPhotoModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ✕
              </button>
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div style={{
                background: '#d4edda',
                color: '#155724',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '20px',
                border: '1px solid #c3e6cb'
              }}>
                {success}
              </div>
            )}
            {error && (
              <div style={{
                background: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '20px',
                border: '1px solid #f5c6cb'
              }}>
                {error}
              </div>
            )}

            {/* Upload Section */}
            <div style={{marginBottom: '20px', padding: '20px', border: '2px dashed #ccc', borderRadius: '8px', textAlign: 'center'}}>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  if (files.length > 0) {
                    setNewPhotos(files);
                    const previews = files.map(file => URL.createObjectURL(file));
                    setPhotoPreviews(previews);
                  }
                }}
                style={{display: 'none'}}
                id="photo-upload"
              />
              <label htmlFor="photo-upload" style={{cursor: 'pointer', display: 'block'}}>
                <div style={{fontSize: '24px', marginBottom: '10px'}}>📷</div>
                <div style={{fontSize: '16px', color: '#666'}}>
                  Click to upload photos or drag and drop
                </div>
                <div style={{fontSize: '12px', color: '#999', marginTop: '5px'}}>
                  You can select multiple images at once
                </div>
              </label>
            </div>

            {/* Current Photos Grid */}
            {(currentPhotos.length > 0 || photoPreviews.length > 0) && (
              <div style={{marginBottom: '20px'}}>
                <h4 style={{marginBottom: '15px'}}>Current Photos</h4>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '15px'}}>
                  {/* Existing Photos */}
                  {currentPhotos.map((photo, index) => (
                    <div key={`existing-${index}`} style={{position: 'relative', border: '2px solid #ddd', borderRadius: '8px', overflow: 'hidden'}}>
                      <img 
                        src={photo} 
                        alt={`Item ${index + 1}`}
                        style={{width: '100%', height: '120px', objectFit: 'cover'}}
                      />
                      <div style={{position: 'absolute', top: '5px', right: '5px'}}>
                        <button
                          onClick={() => {
                            const updatedPhotos = currentPhotos.filter((_, i) => i !== index);
                            setCurrentPhotos(updatedPhotos);
                            if (index === mainImageIndex) {
                              setMainImageIndex(0);
                            } else if (index < mainImageIndex) {
                              setMainImageIndex(mainImageIndex - 1);
                            }
                          }}
                          style={{
                            background: 'rgba(220, 53, 69, 0.8)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                      <div style={{position: 'absolute', top: '5px', left: '5px'}}>
                        <button
                          onClick={() => setMainImageIndex(index)}
                          style={{
                            background: index === mainImageIndex ? '#28a745' : 'rgba(255, 255, 255, 0.8)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            color: index === mainImageIndex ? 'white' : '#333',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          {index === mainImageIndex ? '⭐' : '☆'}
                        </button>
                      </div>
                      {index === mainImageIndex && (
                        <div style={{
                          position: 'absolute',
                          bottom: '5px',
                          left: '5px',
                          background: 'rgba(40, 167, 69, 0.9)',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          MAIN
                        </div>
                      )}
                    </div>
                  ))}

                  {/* New Photo Previews */}
                  {photoPreviews.map((preview, index) => {
                    const newPhotoIndex = currentPhotos.length + index;
                    const isMainImage = mainImageIndex === newPhotoIndex;
                    
                    return (
                      <div key={`new-${index}`} style={{position: 'relative', border: '2px solid #17a2b8', borderRadius: '8px', overflow: 'hidden'}}>
                        <img 
                          src={preview} 
                          alt={`New item ${index + 1}`}
                          style={{width: '100%', height: '120px', objectFit: 'cover'}}
                        />
                        <div style={{position: 'absolute', top: '5px', right: '5px'}}>
                          <button
                            onClick={() => {
                              const updatedPreviews = photoPreviews.filter((_, i) => i !== index);
                              const updatedFiles = newPhotos.filter((_, i) => i !== index);
                              setPhotoPreviews(updatedPreviews);
                              setNewPhotos(updatedFiles);
                              
                              // Adjust main image index if needed
                              if (isMainImage) {
                                setMainImageIndex(0);
                              } else if (newPhotoIndex < mainImageIndex) {
                                setMainImageIndex(mainImageIndex - 1);
                              }
                            }}
                            style={{
                              background: 'rgba(220, 53, 69, 0.8)',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                        <div style={{position: 'absolute', top: '5px', left: '5px'}}>
                          <button
                            onClick={() => setMainImageIndex(newPhotoIndex)}
                            style={{
                              background: isMainImage ? '#28a745' : 'rgba(255, 255, 255, 0.8)',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              color: isMainImage ? 'white' : '#333',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            {isMainImage ? '⭐' : '☆'}
                          </button>
                        </div>
                        {isMainImage && (
                          <div style={{
                            position: 'absolute',
                            bottom: '5px',
                            left: '5px',
                            background: 'rgba(40, 167, 69, 0.9)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}>
                            MAIN
                          </div>
                        )}
                        {!isMainImage && (
                          <div style={{
                            position: 'absolute',
                            bottom: '5px',
                            left: '5px',
                            background: 'rgba(23, 162, 184, 0.9)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}>
                            NEW
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button 
                onClick={() => setPhotoModalOpen(false)}
                className="btn" 
                style={{background: '#6c757d', color: 'white', padding: '12px 24px'}}
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  setIsLoading(true);
                  setError('');
                  setSuccess('');

                  try {
                    // Convert new photos to base64
                    const newPhotoData = [];
                    for (let i = 0; i < newPhotos.length; i++) {
                      const file = newPhotos[i];
                      const base64 = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.readAsDataURL(file);
                      });
                      newPhotoData.push(base64);
                    }

                    // Combine existing and new photos
                    const allPhotos = [...currentPhotos, ...newPhotoData];
                    
                    // Ensure main image index is valid
                    const validMainIndex = Math.min(mainImageIndex, allPhotos.length - 1);
                    
                    // Update the item with new photos
                    const updateData = {
                      image_url: allPhotos[validMainIndex] || '',
                      additional_images: allPhotos.filter((_, index) => index !== validMainIndex)
                    };


                    await hatsAPI.update(editHat._id, updateData);
                    setSuccess('✅ Photos saved successfully! Your item has been updated.');
                    // Keep modal open briefly to show success message
                    setTimeout(() => {
                      setPhotoModalOpen(false);
                    }, 2000);
                    await loadDashboardData();
                  } catch (error) {
                    console.error('Photo update error:', error);
                    setError('Failed to update photos. Please try again.');
                  } finally {
                    setIsLoading(false);
                  }
                }}
                className="btn" 
                style={{background: '#28a745', color: 'white', padding: '12px 24px'}}
                disabled={isLoading}
              >
{isLoading ? 'Saving...' : success ? '✅ Saved!' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modify Trade Modal */}
      {modifyOpen && modifyTrade && (
        <div className="modal-overlay">
          <div className="modal-content" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <h3>Modify Trade</h3>
              <button className="close-btn" onClick={() => setModifyOpen(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div style={{marginBottom: '20px'}}>
                <h4>Trade Details</h4>
                <p><strong>Offered:</strong> {modifyTrade.offered_hat?.name}</p>
                <p><strong>Requested:</strong> {modifyTrade.requested_hat?.name}</p>
                <p><strong>Sister:</strong> {modifyTrade.from_user?.name || modifyTrade.to_user?.name}</p>
              </div>

              {/* Modification Options */}
              <div style={{marginBottom: '20px'}}>
                <h4>What would you like to modify?</h4>
                <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
                  <button 
                    className={`btn ${modifyMode === 'date' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setModifyMode('date')}
                  >
                    📅 Dates
                  </button>
                  <button 
                    className={`btn ${modifyMode === 'item' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => {
                      setModifyMode('item');
                      loadOtherUserCloset(modifyTrade);
                    }}
                  >
                    👜 Items
                  </button>
                </div>
              </div>

              {/* Date Modification */}
              {modifyMode === 'date' && (
                <div style={{marginBottom: '20px'}}>
                  <h4>Modify Dates</h4>
                  <div style={{display: 'flex', gap: '10px', marginBottom: '10px'}}>
                    <div>
                      <label>Start Date:</label>
                      <input 
                        type="date" 
                        value={modifyDates.start_date}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        min={ymd(computeFirstAvailableET())}
                        style={{padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}}
                      />
                    </div>
                    <div>
                      <label>End Date:</label>
                      <input 
                        type="date" 
                        value={modifyDates.end_date}
                        onChange={(e) => handleEndDateChange(e.target.value)}
                        min={modifyDates.start_date ? ymd(new Date(modifyDates.start_date)) : ymd(computeFirstAvailableET())}
                        max={modifyDates.start_date ? ymd(computeMaxEndDate(modifyDates.start_date)) : ''}
                        style={{padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}}
                      />
                    </div>
                  </div>
                  {dateValidationError && (
                    <div style={{color: '#dc3545', fontSize: '14px', marginTop: '8px', padding: '8px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px'}}>
                      {dateValidationError}
                    </div>
                  )}
                </div>
              )}

              {/* Item Modification */}
              {modifyMode === 'item' && (
                <div style={{marginBottom: '20px'}}>
                  <h4>Choose Different Item</h4>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                    <p style={{fontSize: '14px', color: '#666', margin: 0}}>
                      Select an item from {modifyTrade.from_user?.name || modifyTrade.to_user?.name}'s closet:
                    </p>
                    <button 
                      className="btn btn-secondary" 
                      onClick={() => loadFullClosetModal(modifyTrade)}
                      style={{fontSize: '12px', padding: '4px 8px'}}
                    >
                      👁️ View Full Closet
                    </button>
                  </div>
                  
                  {closetLoading ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>Loading items...</div>
                  ) : (
                    <div style={{maxHeight: '300px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px'}}>
                      {closetItems.map((item) => (
                        <div 
                          key={item._id}
                          style={{
                            padding: '10px',
                            borderBottom: '1px solid #eee',
                            backgroundColor: selectedItemId === item._id ? '#e3f2fd' : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                          }}
                        >
                          <img 
                            src={item.image_url} 
                            alt={item.name}
                            style={{width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer'}}
                            onClick={() => showItemDetails(item)}
                          />
                          <div style={{flex: 1}}>
                            <div style={{fontWeight: 'bold'}}>{item.name}</div>
                            <div style={{fontSize: '12px', color: '#666'}}>
                              {item.brand} • {item.size} • {item.condition} • ${item.value}
                            </div>
                          </div>
                          <div style={{display: 'flex', gap: '4px'}}>
                            <button 
                              className="btn btn-sm" 
                              onClick={() => showItemDetails(item)}
                              style={{fontSize: '10px', padding: '2px 6px'}}
                            >
                              👁️ Details
                            </button>
                            <button 
                              className="btn btn-sm" 
                              onClick={() => setSelectedItemId(item._id)}
                              style={{
                                fontSize: '10px', 
                                padding: '2px 6px',
                                backgroundColor: selectedItemId === item._id ? '#007bff' : '#6c757d',
                                color: 'white'
                              }}
                            >
                              {selectedItemId === item._id ? '✓ Selected' : 'Select'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer" style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                className="btn btn-secondary" 
                onClick={() => setModifyOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={submitModify}
                disabled={isLoading}
              >
                {isLoading ? 'Modifying...' : 'Submit Modification'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Detail Popup for Modify Screen */}
      {showItemDetailPopup && selectedItemForDetail && (
        <div className="modal-overlay" style={{zIndex: 2000}}>
          <div className="modal-content" style={{maxWidth: '800px', maxHeight: '90vh', overflow: 'auto'}}>
            <div className="modal-header">
              <h3>Item Details</h3>
              <button className="close-btn" onClick={closeItemDetailPopup}>×</button>
            </div>
            
            <div className="modal-body" style={{display: 'flex', gap: '20px'}}>
              {/* Left side - Images */}
              <div style={{flex: '0 0 300px'}}>
                <div style={{marginBottom: '15px'}}>
                  <h4>Photos</h4>
                  {/* Main Image */}
                  <div style={{marginBottom: '10px'}}>
                    <img 
                      src={(() => {
                        const allImages = [];
                        if (selectedItemForDetail.image_url) {
                          allImages.push(selectedItemForDetail.image_url);
                        }
                        if (selectedItemForDetail.additional_images && selectedItemForDetail.additional_images.length > 0) {
                          allImages.push(...selectedItemForDetail.additional_images);
                        }
                        return allImages.length > 0 ? allImages[0] : '';
                      })()} 
                      alt={selectedItemForDetail.name}
                      style={{width: '100%', height: '200px', objectFit: 'contain', borderRadius: '8px', cursor: 'pointer'}}
                      onClick={() => openImageLightbox(selectedItemForDetail, 0)}
                    />
                  </div>
                  
                  {/* Thumbnails */}
                  {(() => {
                    const allImages = [];
                    if (selectedItemForDetail.image_url) {
                      allImages.push(selectedItemForDetail.image_url);
                    }
                    if (selectedItemForDetail.additional_images && selectedItemForDetail.additional_images.length > 0) {
                      allImages.push(...selectedItemForDetail.additional_images);
                    }
                    return allImages.length > 1 ? (
                      <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                        {allImages.map((image, index) => (
                          <img 
                            key={index}
                            src={image} 
                            alt={`${selectedItemForDetail.name} ${index + 1}`}
                            style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer'}}
                            onClick={() => openImageLightbox(selectedItemForDetail, index)}
                          />
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
              
              {/* Right side - Details */}
              <div style={{flex: 1}}>
                <h2 style={{marginTop: 0}}>{selectedItemForDetail.name}</h2>
                
                <div style={{marginBottom: '20px'}}>
                  <p style={{fontSize: '16px', color: '#666', lineHeight: '1.5'}}>
                    {selectedItemForDetail.description || 'No description available'}
                  </p>
                </div>
                
                <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px'}}>
                  <h4 style={{marginTop: 0, marginBottom: '15px'}}>Item Specifications</h4>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px'}}>
                    <div>
                      <strong>Brand:</strong><br/>
                      <span style={{color: '#666'}}>{selectedItemForDetail.brand || 'Unknown Brand'}</span>
                    </div>
                    <div>
                      <strong>Size:</strong><br/>
                      <span style={{color: '#666'}}>{selectedItemForDetail.size || 'N/A'}</span>
                    </div>
                    <div>
                      <strong>Condition:</strong><br/>
                      <span style={{color: '#666'}}>{selectedItemForDetail.condition || 'N/A'}</span>
                    </div>
                    <div>
                      <strong>Value:</strong><br/>
                      <span style={{color: '#666', fontWeight: 'bold'}}>${selectedItemForDetail.value || '0'}</span>
                    </div>
                    {selectedItemForDetail.material && (
                      <div>
                        <strong>Material:</strong><br/>
                        <span style={{color: '#666'}}>{selectedItemForDetail.material}</span>
                      </div>
                    )}
                    {selectedItemForDetail.color && (
                      <div>
                        <strong>Color:</strong><br/>
                        <span style={{color: '#666'}}>{selectedItemForDetail.color}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{background: '#e3f2fd', padding: '15px', borderRadius: '8px'}}>
                  <h4 style={{marginTop: 0, marginBottom: '10px'}}>Owner Information</h4>
                  <p style={{margin: 0, fontSize: '14px'}}>
                    This item belongs to <strong>{modifyTrade?.from_user?.name || modifyTrade?.to_user?.name || 'Unknown User'}</strong>
                  </p>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => loadFullClosetModal(modifyTrade)}
                    style={{marginTop: '10px', fontSize: '12px', padding: '6px 12px'}}
                  >
                    👁️ View Full Closet
                  </button>
                </div>
              </div>
            </div>
            
            <div className="modal-footer" style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                className="btn btn-secondary" 
                onClick={closeItemDetailPopup}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Gallery Modal */}
      {photoGalleryOpen && (
        <div className="modal" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 10000}}>
          <div className="modal-content" style={{maxWidth: '90vw', maxHeight: '90vh', margin: '5vh auto', background: '#fff', borderRadius: 8, position: 'relative'}}>
            <span className="close" onClick={closePhotoGallery} style={{position: 'absolute', top: 10, right: 15, fontSize: 28, fontWeight: 'bold', cursor: 'pointer', zIndex: 10001}}>&times;</span>
            
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', position: 'relative'}}>
              {/* Previous button */}
              {galleryImages.length > 1 && (
                <button 
                  onClick={prevImage}
                  style={{
                    position: 'absolute',
                    left: 20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: 50,
                    height: 50,
                    fontSize: 20,
                    cursor: 'pointer',
                    zIndex: 10001
                  }}
                >
                  ‹
                </button>
              )}
              
              {/* Main image */}
              <img 
                src={galleryImages[currentImageIndex]} 
                alt={`Gallery item ${currentImageIndex + 1}`}
                style={{
                  maxWidth: '80%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
              
              {/* Next button */}
              {galleryImages.length > 1 && (
                <button 
                  onClick={nextImage}
                  style={{
                    position: 'absolute',
                    right: 20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: 50,
                    height: 50,
                    fontSize: 20,
                    cursor: 'pointer',
                    zIndex: 10001
                  }}
                >
                  ›
                </button>
              )}
            </div>
            
            {/* Image counter and thumbnails */}
            <div style={{padding: '20px', textAlign: 'center', borderTop: '1px solid #eee'}}>
              <div style={{marginBottom: 15, fontSize: 16, color: '#666'}}>
                {currentImageIndex + 1} of {galleryImages.length}
              </div>
              
              {/* Thumbnail strip */}
              {galleryImages.length > 1 && (
                <div style={{display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap'}}>
                  {galleryImages.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      onClick={() => setCurrentImageIndex(index)}
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 4,
                        cursor: 'pointer',
                        border: index === currentImageIndex ? '3px solid #007bff' : '1px solid #ddd',
                        opacity: index === currentImageIndex ? 1 : 0.7
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Manual Deposit Modal */}
      <ManualDepositModal 
        isOpen={showManualDeposit} 
        onClose={() => {
          setShowManualDeposit(false);
          // Refresh dashboard data to show new pending deposits
          loadDashboardData();
        }} 
      />
      
      {/* Withdrawal Modal */}
      <WithdrawalModal 
        isOpen={showWithdrawal} 
        onClose={() => setShowWithdrawal(false)}
        onSuccess={handleWithdrawSuccess}
      />

      {/* Dispute Modal */}
      {showDisputeModal && (
        <div className="modal" style={{zIndex: 10004}}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowDisputeModal(false)}>&times;</span>
            <h3>{disputeSubmitted ? 'Dispute Submitted Successfully' : 'Create Dispute'}</h3>
            
            {disputeSubmitted && (
              <div style={{marginBottom: '16px', padding: '12px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px'}}>
                <div style={{fontSize: '14px', color: '#155724'}}>
                  <strong>✅ Dispute Created!</strong> Your dispute has been submitted and our team will review it within 24-48 hours. 
                  You can now use the communication features below to discuss the issue with the other party and admin.
                </div>
              </div>
            )}
            
            <div style={{marginBottom: '16px', padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px'}}>
              <div style={{fontSize: '14px', color: '#856404'}}>
                <strong>⚠️ After creating a dispute:</strong> Please try to resolve the issue directly with the other user. 
                Disputes are reviewed within 24-48 hours and may result in account restrictions if found to be frivolous.
              </div>
            </div>
            
            {selectedTradeForDispute && (
              <div style={{marginBottom: '16px', padding: '8px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px'}}>
                <div style={{fontSize: '12px', color: '#1976d2'}}>
                  <strong>Trade ID:</strong> {selectedTradeForDispute}
                </div>
              </div>
            )}
            
            {!disputeSubmitted && (
              <>
                <div className="form-group">
                  <label>Reason for Dispute *</label>
                  <select 
                    value={disputeForm.reason}
                    onChange={(e) => setDisputeForm(prev => ({...prev, reason: e.target.value}))}
                    style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                  >
                    <option value="">Select a reason</option>
                    <option value="item_not_as_described">Item not as described</option>
                    <option value="item_damaged">Item damaged during shipping</option>
                    <option value="item_not_received">Item not received</option>
                    <option value="return_issue">Return/refund issue</option>
                    <option value="communication_problem">Communication problem</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={disputeForm.description}
                    onChange={(e) => setDisputeForm(prev => ({...prev, description: e.target.value}))}
                    placeholder="Please provide a detailed description of the issue..."
                    rows="4"
                    style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                  />
                </div>

                <div className="form-group">
                  <label>Evidence (Optional)</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setDisputeForm(prev => ({...prev, evidence: [...prev.evidence, ...files]}));
                    }}
                    style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                  />
                  <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                    Upload photos, documents, or screenshots to support your claim
                  </div>
                </div>

                <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px'}}>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => setShowDisputeModal(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleSubmitDispute}
                    disabled={isLoading || !disputeForm.reason || !disputeForm.description}
                    style={{background: '#ffc107', color: '#000'}}
                  >
                    {isLoading ? 'Submitting...' : 'Submit Dispute'}
                  </button>
                </div>
              </>
            )}
            
            {disputeSubmitted && (
              <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px'}}>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowDisputeModal(false)}
                >
                  Close
                </button>
              </div>
            )}

            {/* Dispute Communication Section */}
            {selectedTradeForDispute && (
              <div style={{marginTop: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '6px'}}>
                <h4 style={{margin: '0 0 12px 0', fontSize: '16px'}}>💬 Dispute Communication</h4>
                <div style={{fontSize: '14px', color: '#666', marginBottom: '12px'}}>
                  {(() => {
                    // Check if dispute already exists for this trade
                    const existingDispute = myDisputes.find(d => String(d.trade_id) === String(selectedTradeForDispute));
                    if (existingDispute) {
                      return 'You can communicate directly with the other party and admin about this dispute.';
                    } else {
                      return 'Communication features will be available after you submit the dispute.';
                    }
                  })()}
                </div>
                <div style={{display: 'flex', gap: '8px'}}>
                  <button 
                    className="btn" 
                    disabled={(() => {
                      const existingDispute = myDisputes.find(d => String(d.trade_id) === String(selectedTradeForDispute));
                      return !existingDispute;
                    })()}
                    style={{
                      background: (() => {
                        const existingDispute = myDisputes.find(d => String(d.trade_id) === String(selectedTradeForDispute));
                        return existingDispute ? '#17a2b8' : '#6c757d';
                      })(), 
                      color: '#fff', 
                      fontSize: '12px', 
                      padding: '6px 12px',
                      opacity: (() => {
                        const existingDispute = myDisputes.find(d => String(d.trade_id) === String(selectedTradeForDispute));
                        return existingDispute ? '1' : '0.6';
                      })()
                    }}
                    onClick={async () => {
                      // Find the dispute for this trade
                      const dispute = myDisputes.find(d => String(d.trade_id) === String(selectedTradeForDispute));
                      if (dispute) {
                        try {
                          setSelectedDisputeForMessaging(dispute._id);
                          setShowDisputeMessagingModal(true);
                          await loadDisputeMessages(dispute._id);
                        } catch (error) {
                          console.error('Error opening messaging modal:', error);
                          setError('Failed to open messaging. Please try again.');
                        }
                      } else {
                        setError('Please submit the dispute first to enable messaging');
                      }
                    }}
                  >
                    💬 Send Message
                  </button>
                  <button 
                    className="btn" 
                    disabled={(() => {
                      const existingDispute = myDisputes.find(d => String(d.trade_id) === String(selectedTradeForDispute));
                      return !existingDispute;
                    })()}
                    style={{
                      background: (() => {
                        const existingDispute = myDisputes.find(d => String(d.trade_id) === String(selectedTradeForDispute));
                        return existingDispute ? '#6c757d' : '#6c757d';
                      })(), 
                      color: '#fff', 
                      fontSize: '12px', 
                      padding: '6px 12px',
                      opacity: (() => {
                        const existingDispute = myDisputes.find(d => String(d.trade_id) === String(selectedTradeForDispute));
                        return existingDispute ? '1' : '0.6';
                      })()
                    }}
                    onClick={async () => {
                      // Find the dispute for this trade
                      const dispute = myDisputes.find(d => String(d.trade_id) === String(selectedTradeForDispute));
                      if (dispute) {
                        try {
                          setSelectedDisputeForHistory(dispute._id);
                          setShowDisputeHistoryModal(true);
                          await loadDisputeMessages(dispute._id);
                        } catch (error) {
                          console.error('Error opening history modal:', error);
                          setError('Failed to open history. Please try again.');
                        }
                      } else {
                        setError('Please submit the dispute first to view history');
                      }
                    }}
                  >
                    📋 View History
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dispute Messaging Modal */}
      {showDisputeMessagingModal && (
        <div className="modal" style={{zIndex: 10005}}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowDisputeMessagingModal(false)}>&times;</span>
            <h3>Dispute Messaging</h3>
            
            {selectedDisputeForMessaging && (
              <div style={{marginBottom: '16px', padding: '8px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px'}}>
                <div style={{fontSize: '12px', color: '#1976d2'}}>
                  <strong>Dispute ID:</strong> {String(selectedDisputeForMessaging).slice(-8)}
                </div>
              </div>
            )}
            
            <div style={{marginBottom: '20px'}}>
              <div style={{height: '300px', border: '1px solid #ddd', padding: '10px', overflowY: 'auto', backgroundColor: '#f9f9f9', borderRadius: '6px'}}>
                {disputeMessages.map((msg, index) => (
                  <div key={index} style={{
                    marginBottom: '10px', 
                    padding: '8px', 
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{fontSize: '12px', color: '#666', marginBottom: '4px', display: 'flex', justifyContent: 'space-between'}}>
                      <strong>{msg.user_name}</strong>
                      <span>{new Date(msg.created_at).toLocaleString()}</span>
                    </div>
                    <div style={{fontSize: '14px', lineHeight: '1.4'}}>{msg.message}</div>
                  </div>
                ))}
                {disputeMessages.length === 0 && (
                  <div style={{textAlign: 'center', color: '#666', padding: '40px 20px'}}>
                    <div style={{fontSize: '18px', marginBottom: '8px'}}>💬</div>
                    <div>No messages yet. Start the conversation!</div>
                    <div style={{fontSize: '12px', marginTop: '4px', opacity: '0.7'}}>
                      Messages are visible to both parties and admin support
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message here..."
                style={{
                  flex: 1, 
                  padding: '12px', 
                  border: '2px solid #e0e0e0', 
                  borderRadius: '20px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onKeyPress={(e) => e.key === 'Enter' && sendDisputeMessage()}
                onFocus={(e) => e.target.style.borderColor = '#007bff'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
              <button 
                className="btn btn-primary" 
                onClick={sendDisputeMessage}
                disabled={!newMessage.trim() || isLoading}
                style={{
                  padding: '12px 20px',
                  borderRadius: '20px',
                  backgroundColor: '#007bff',
                  border: 'none',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: newMessage.trim() && !isLoading ? 'pointer' : 'not-allowed',
                  opacity: newMessage.trim() && !isLoading ? '1' : '0.6'
                }}
              >
                {isLoading ? 'Sending...' : 'Send'}
              </button>
            </div>
            
            <div style={{marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowDisputeMessagingModal(false);
                  setNewMessage('');
                  setDisputeMessages([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute History Modal */}
      {showDisputeHistoryModal && (
        <div className="modal" style={{zIndex: 10005}}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowDisputeHistoryModal(false)}>&times;</span>
            <h3>Dispute History</h3>
            
            <div style={{height: '400px', border: '1px solid #ddd', padding: '10px', overflowY: 'auto', backgroundColor: '#f9f9f9'}}>
              {disputeMessages.map((msg, index) => (
                <div key={index} style={{marginBottom: '15px', padding: '10px', backgroundColor: 'white', borderRadius: '4px', borderLeft: '4px solid #007bff'}}>
                  <div style={{fontSize: '12px', color: '#666', marginBottom: '4px'}}>
                    <strong>{msg.user_name}</strong> - {new Date(msg.created_at).toLocaleString()}
                  </div>
                  <div style={{fontSize: '14px'}}>{msg.message}</div>
                </div>
              ))}
              {disputeMessages.length === 0 && (
                <div style={{textAlign: 'center', color: '#666', padding: '40px'}}>
                  No communication history available.
                </div>
              )}
            </div>
            
            <div style={{marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowDisputeHistoryModal(false);
                  setDisputeMessages([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Enhanced Tracking Modal */}
      {trackingModalOpen && (
        <div className="modal" style={{zIndex: 10003}}>
          <div className="modal-content" onClick={(e)=> e.stopPropagation()} style={{maxWidth: '600px'}}>
            <span className="close" onClick={()=> setTrackingModalOpen(false)}>&times;</span>
            <h3 style={{textAlign:'center', marginBottom:20}}>📍 Package Tracking</h3>
            
            {loadingTracking ? (
              <div style={{textAlign:'center', padding:'40px'}}>
                <div style={{fontSize:'18px', color:'#666'}}>Loading tracking information...</div>
              </div>
            ) : trackingData ? (
              <div style={{display:'grid', gap:20}}>
                {/* Outgoing Packages */}
                <div style={{border:'1px solid #e0e0e0', borderRadius:8, padding:16}}>
                  <h4 style={{margin:'0 0 12px 0', color:'#007bff', fontSize:16}}>📤 Packages You're Sending</h4>
                  
                  {/* To Partner */}
                  {trackingData.outgoing?.to_partner?.tracking_number ? (
                    <div style={{marginBottom:12, padding:12, backgroundColor:'#f8f9fa', borderRadius:6}}>
                      <div style={{fontWeight:'bold', marginBottom:4}}>To Trading Partner</div>
                      <div style={{fontSize:14, marginBottom:4}}>
                        <strong>Tracking #:</strong> {trackingData.outgoing.to_partner.tracking_number}
                      </div>
                      {trackingData.outgoing.to_partner.carrier && (
                        <div style={{fontSize:14, marginBottom:4}}>
                          <strong>Carrier:</strong> {trackingData.outgoing.to_partner.carrier}
                        </div>
                      )}
                      {trackingData.outgoing.to_partner.tracking_url && (
                        <div style={{marginTop:8}}>
                          <a 
                            href={trackingData.outgoing.to_partner.tracking_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{color:'#007bff', textDecoration:'none', fontSize:14}}
                          >
                            🔗 Track Package
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{fontSize:14, color:'#666', fontStyle:'italic'}}>Outbound tracking not available yet</div>
                  )}
                  
                  {/* Return */}
                  {trackingData.outgoing?.return?.tracking_number ? (
                    <div style={{padding:12, backgroundColor:'#f8f9fa', borderRadius:6}}>
                      <div style={{fontWeight:'bold', marginBottom:4}}>Return Package</div>
                      <div style={{fontSize:14, marginBottom:4}}>
                        <strong>Tracking #:</strong> {trackingData.outgoing.return.tracking_number}
                      </div>
                      {trackingData.outgoing.return.carrier && (
                        <div style={{fontSize:14, marginBottom:4}}>
                          <strong>Carrier:</strong> {trackingData.outgoing.return.carrier}
                        </div>
                      )}
                      {trackingData.outgoing.return.tracking_url && (
                        <div style={{marginTop:8}}>
                          <a 
                            href={trackingData.outgoing.return.tracking_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{color:'#007bff', textDecoration:'none', fontSize:14}}
                          >
                            🔗 Track Package
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{fontSize:14, color:'#666', fontStyle:'italic'}}>Return tracking not available yet</div>
                  )}
                </div>
                
                {/* Incoming Packages */}
                <div style={{border:'1px solid #e0e0e0', borderRadius:8, padding:16}}>
                  <h4 style={{margin:'0 0 12px 0', color:'#28a745', fontSize:16}}>📥 Packages You're Receiving</h4>
                  
                  {/* From Partner */}
                  {trackingData.incoming?.from_partner?.tracking_number ? (
                    <div style={{marginBottom:12, padding:12, backgroundColor:'#f8f9fa', borderRadius:6}}>
                      <div style={{fontWeight:'bold', marginBottom:4}}>From Trading Partner</div>
                      <div style={{fontSize:14, marginBottom:4}}>
                        <strong>Tracking #:</strong> {trackingData.incoming.from_partner.tracking_number}
                      </div>
                      {trackingData.incoming.from_partner.carrier && (
                        <div style={{fontSize:14, marginBottom:4}}>
                          <strong>Carrier:</strong> {trackingData.incoming.from_partner.carrier}
                        </div>
                      )}
                      {trackingData.incoming.from_partner.tracking_url && (
                        <div style={{marginTop:8}}>
                          <a 
                            href={trackingData.incoming.from_partner.tracking_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{color:'#28a745', textDecoration:'none', fontSize:14}}
                          >
                            🔗 Track Package
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{fontSize:14, color:'#666', fontStyle:'italic'}}>Incoming tracking not available yet</div>
                  )}
                  
                  {/* Return */}
                  {trackingData.incoming?.return?.tracking_number ? (
                    <div style={{padding:12, backgroundColor:'#f8f9fa', borderRadius:6}}>
                      <div style={{fontWeight:'bold', marginBottom:4}}>Return Package</div>
                      <div style={{fontSize:14, marginBottom:4}}>
                        <strong>Tracking #:</strong> {trackingData.incoming.return.tracking_number}
                      </div>
                      {trackingData.incoming.return.carrier && (
                        <div style={{fontSize:14, marginBottom:4}}>
                          <strong>Carrier:</strong> {trackingData.incoming.return.carrier}
                        </div>
                      )}
                      {trackingData.incoming.return.tracking_url && (
                        <div style={{marginTop:8}}>
                          <a 
                            href={trackingData.incoming.return.tracking_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{color:'#28a745', textDecoration:'none', fontSize:14}}
                          >
                            🔗 Track Package
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{fontSize:14, color:'#666', fontStyle:'italic'}}>Return tracking not available yet</div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{textAlign:'center', padding:'40px'}}>
                <div style={{fontSize:'16px', color:'#666'}}>No tracking information available</div>
                <div style={{fontSize:'14px', color:'#999', marginTop:8}}>Tracking will be available once packages are shipped</div>
              </div>
            )}
            
            <div style={{textAlign:'center', marginTop:20}}>
              <button 
                className="btn" 
                onClick={()=> setTrackingModalOpen(false)}
                style={{backgroundColor:'#6c757d', color:'white', border:'none', padding:'8px 16px', borderRadius:'4px'}}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Full Closet Modal */}
      {showFullClosetModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h3>Full Closet</h3>
              <button 
                onClick={() => setShowFullClosetModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>
            
            {fullClosetLoading ? (
              <div style={{textAlign: 'center', padding: '40px'}}>Loading items...</div>
            ) : fullClosetItems.length === 0 ? (
              <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                No items found in this closet.
              </div>
            ) : (
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px'}}>
                {fullClosetItems.map((item) => (
                  <div key={item._id} style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    <img 
                      src={item.image_url || ''} 
                      alt={item.name}
                      style={{width: '100%', height: '200px', objectFit: 'cover', borderRadius: '4px', marginBottom: '10px'}}
                    />
                    <h4 style={{margin: '0 0 10px 0', fontSize: '16px'}}>{item.name}</h4>
                    <p style={{margin: '0 0 5px 0', fontSize: '14px', color: '#666'}}><strong>Brand:</strong> {item.brand}</p>
                    <p style={{margin: '0 0 5px 0', fontSize: '14px', color: '#666'}}><strong>Size:</strong> {item.size}</p>
                    <p style={{margin: '0 0 10px 0', fontSize: '14px', color: '#666'}}><strong>Condition:</strong> {item.condition}</p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => {
                        // Select this item for the trade
                        setModifyTrade(prev => ({
                          ...prev,
                          other_item_id: item._id,
                          other_item_name: item.name,
                          other_item_value: item.value || 0
                        }));
                        setShowFullClosetModal(false);
                      }}
                      style={{width: '100%', fontSize: '14px', padding: '8px'}}
                    >
                      Select This Item
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
};

export default DashboardModal;

// Mobile responsive styles for button overlap fix
const mobileStyles = `
  @media (max-width: 768px) {
    .dashboard-modal .close {
      top: 10px !important;
      right: 10px !important;
      font-size: 24px !important;
      z-index: 1001 !important;
    }
    
    .dashboard-header {
      padding-top: 50px !important;
    }
    
    .dashboard-header .user-info {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 10px !important;
    }
    
    .dashboard-header .user-info .btn {
      margin-left: 0 !important;
      margin-top: 5px !important;
    }
  }
  
  @media (max-width: 480px) {
    .dashboard-modal .close {
      top: 8px !important;
      right: 8px !important;
      font-size: 20px !important;
    }
    
    .dashboard-header {
      padding-top: 45px !important;
    }
    
    .dashboard-header h2 {
      font-size: 18px !important;
    }
    
    .dashboard-header .user-info span {
      font-size: 14px !important;
    }
    
    .dashboard-header .user-info .btn {
      padding: 8px 16px !important;
      font-size: 14px !important;
    }
  }
`;

// Inject mobile styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = mobileStyles;
  document.head.appendChild(styleSheet);
}